import type { Translations } from './en';

/**
 * Typed against the English resource, so an added English key that has no
 * Spanish counterpart fails `npm run typecheck` instead of silently falling
 * back at runtime the way the old flat JSON files did.
 */
export const es: Translations = {
  common: {
    appName: 'Grocefy Admin',
    email: 'Correo electrónico',
    password: 'Contraseña',
    signIn: 'Iniciar sesión',
    signOut: 'Cerrar sesión',
    cancel: 'Cancelar',
    save: 'Guardar',
    search: 'Buscar',
    noOptionsMatch: 'Ninguna opción coincide.',
    typeToFilter: 'Escribe para filtrar…',
    noStoreSelected: 'Elige una tienda en el selector para ver su configuración.',
    loading: 'Cargando…',
    back: 'Atrás',
    backToDashboard: 'Volver al tablero',
    copy: 'Copiar',
    retry: 'Reintentar',
    reload: 'Recargar',
    yes: 'Si',
    no: 'No',
    notAvailable: 'N/D',
    language: 'Idioma',
    english: 'English',
    spanish: 'Español',
  },

  auth: {
    login: {
      title: 'Inicia sesión en tu cuenta',
      subtitle: 'Introduce las credenciales de tu cuenta de administrador.',
      emailPlaceholder: 'tu@empresa.com',
      forgotPassword: '¿Olvidaste tu contraseña?',
      submit: 'Iniciar sesión',
      submitting: 'Iniciando sesión…',
      needAccount: '¿Necesitas una cuenta?',
      requestAccess: 'Solicitar acceso',
      genericError: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
      showPassword: 'Mostrar contraseña',
      hidePassword: 'Ocultar contraseña',
    },

    register: {
      title: 'Solicitar una cuenta',
      subtitle:
        'Crea tus credenciales. Un administrador asigna tu rol y tus tiendas antes de que puedas iniciar sesión.',
      firstName: 'Nombre',
      lastName: 'Apellido',
      phone: 'Número de teléfono',
      phonePlaceholder: '(787) 555-0123',
      gender: 'Género',
      male: 'Masculino',
      female: 'Femenino',
      preferredLanguage: 'Idioma preferido',
      confirmPassword: 'Confirmar contraseña',
      submit: 'Solicitar cuenta',
      submitting: 'Creando cuenta…',
      haveAccount: '¿Ya tienes una cuenta?',
      genericError: 'No se pudo crear la cuenta. Inténtalo de nuevo.',
      successTitle: 'Cuenta solicitada',
      successBody:
        'Tu solicitud fue enviada. Un administrador debe asignarle un rol a tu cuenta antes de que puedas iniciar sesión — te avisaremos cuando ocurra.',
      backToSignIn: 'Volver al inicio de sesión',
      passwordRules: {
        length: 'Al menos 8 caracteres',
        uppercase: 'Una letra mayúscula',
        lowercase: 'Una letra minúscula',
        number: 'Un número',
      },
    },

    validation: {
      firstNameRequired: 'Introduce tu nombre',
      lastNameRequired: 'Introduce tu apellido',
      emailRequired: 'Introduce tu correo electrónico',
      emailInvalid: 'Introduce un correo electrónico válido',
      phoneRequired: 'Introduce tu número de teléfono',
      phoneInvalid: 'Introduce un número de teléfono con al menos 10 dígitos',
      passwordRequired: 'Introduce tu contraseña',
      passwordCreate: 'Introduce una contraseña',
      passwordWeak: 'La contraseña no cumple los requisitos indicados abajo',
      confirmRequired: 'Confirma tu contraseña',
      passwordMismatch: 'Las contraseñas no coinciden',
      notAuthorized:
        'Esta cuenta no está autorizada para usar el portal de administración.',
    },

    brand: {
      eyebrow: 'Consola de administración',
      headline: 'Todo lo que hacen tus tiendas, en un solo lugar.',
      subhead:
        'Una consola para toda la operación — sin cambiar de herramienta para responder una sola pregunta.',
      support:
        '¿Problemas para iniciar sesión? Contacta al administrador de tu cuenta.',
      calloutWithBooking: 'Reservas + comercio',
      calloutWithoutBooking: 'Comercio',
      calloutBodyWithBooking:
        'Las reservas y las ventas se reportan lado a lado, así un martes flojo aparece una vez — no dos veces en dos sistemas distintos.',
      calloutBodyWithoutBooking:
        'Ventas, inventario y despacho se reportan sobre las mismas cifras, así un martes flojo aparece una vez — no dos veces en dos sistemas distintos.',
      orders: 'Órdenes',
      ordersBody:
        'Cada orden desde el pago hasta la entrega, con todo su historial.',
      bookings: 'Reservas',
      bookingsBody:
        'Eventos, anfitriones, horarios y membresías en todas tus localidades.',
      catalog: 'Catálogo',
      catalogBody:
        'Artículos, precios e inventario, sincronizados en cada tienda.',
      delivery: 'Entregas',
      deliveryBody: 'Despacho, proveedores y rutas en un solo tablero.',
      marketing: 'Mercadeo',
      marketingBody:
        'Campañas, cupones y lealtad, medidos contra ventas reales.',
      finance: 'Finanzas',
      financeBody: 'Pagos, facturas y reportes que puedes cuadrar.',
    },

    publicNotice: {
      forgotTitle: 'Restablecer tu contraseña',
      forgotBody:
        'La recuperación de contraseña aún no forma parte del rediseño.',
    },
  },

  shell: {
    openNavigation: 'Abrir navegación',
    navigation: 'Navegación',
    mainNavigation: 'Principal',
    collapse: 'Contraer',
    collapseSidebar: 'Contraer barra lateral',
    expandSidebar: 'Expandir barra lateral',
    searchPlaceholder: 'Buscar…',
    searchPages: 'Buscar páginas',
    searchPagesShortcut: 'Buscar páginas (⌘K)',
    commandPlaceholder: 'Buscar páginas…',
    commandEmpty: 'Ninguna página coincide con esa búsqueda.',
    commandIn: 'en {{parent}}',
    appearance: 'Apariencia',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    themeSystem: 'Sistema',
    notImplemented: '{{name}} aún no está disponible.',
    profile: 'Perfil',
    footerRights: '© {{year}} Gopanza LLC. Todos los derechos reservados.',
  },

  businessSwitcher: {
    switchContext: 'Cambiar contexto',
    global: 'Global',
    store: 'Tienda',
    viewing: 'Viendo',
    ariaLabel: 'Contexto actual: {{context}}. Cambiar de tienda.',
    loadFailed: 'No se pudieron cargar tus tiendas.',
    noneAssigned: 'No hay tiendas asignadas a esta cuenta.',
  },

  customers: {
    title: 'Usuarios',
    subtitle:
      'Busca un cliente por nombre, correo, teléfono o código de amigo.',
    searchPlaceholder: 'Buscar por nombre, correo o teléfono...',
    searchLabel: 'Buscar clientes',
    clearSearch: 'Limpiar búsqueda',

    startTitle: 'Busca un cliente',
    startBody:
      'Escribe un nombre, correo electrónico, teléfono o código de amigo para comenzar. No se muestra nada hasta que busques.',
    noResultsTitle: 'No se encontraron clientes',
    noResultsBody: 'Nada coincide con "{{term}}". Revisa la escritura, o intenta con un teléfono o correo.',
    errorTitle: 'La búsqueda fallo',
    errorBody: 'No se pudo completar la búsqueda de clientes. Inténtalo de nuevo.',
    resultCount_one: '{{count}} cliente',
    resultCount_other: '{{count}} clientes',
    ordersCount_one: '{{count}} orden',
    ordersCount_other: '{{count}} órdenes',

    exportMarketing: 'Exportar usuarios de mercadeo',
    exportShort: 'Exportar',
    exportFailed: 'No se pudo generar la exportación.',

    name: 'Nombre',
    email: 'Correo electrónico',
    phone: 'Teléfono',
    orders: 'Órdenes',
    orderCount: 'Cantidad de órdenes',
    status: 'Estado',
    active: 'Activo',
    inactive: 'Inactivo',
    deleted: 'Eliminado',
    verified: 'Verificada',
    unverified: 'Sin verificar',
    createdAt: 'Creado',

    details: 'Detalles',
    locations: 'Direcciónes',
    cards: 'Tarjetas',
    activity: 'Actividad',
    friendCode: 'Código de amigo',
    userSince: 'Usuario desde',
    accountStatus: 'Estado de la cuenta',
    communicationPreferences: 'Preferencias de comunicación',
    allowPush: 'Notificaciones push',
    allowEmails: 'Correos',
    allowSms: 'SMS',

    locationName: 'Nombre',
    address: 'Dirección',
    city: 'Ciudad',
    zipcode: 'Código postal',
    noLocations: 'Este cliente no tiene direcciones guardadas.',

    nickname: 'Apodo',
    reference: 'Referencia',
    type: 'Tipo',
    expires: 'Vence',
    noCards: 'Este cliente no tiene tarjetas guardadas.',
    overview: 'Resumen',
    registered: 'Registrados',
    registeredToday: 'Registrados hoy',
    atLeastOneOrder: 'Con al menos una orden',

    actions: 'Acciones',
    editUser: 'Editar usuario',
    updated: 'Cambios guardados.',
    updateFailed: 'No se pudo guardar el cambio.',
    actionFailed: 'No se pudo completar la acción.',
    copiedToClipboard: 'Copiado al portapapeles',

    tabProfile: 'Perfil',
    tabFlags: 'Ajustes',
    tabRoles: 'Roles',
    tabActions: 'Acciones',

    externalClientId: 'ID de cliente externo',
    flagsHint: 'Cada interruptor se guarda por sí solo, al momento de cambiarlo.',
    rolesHint: 'Los roles aplican a toda la plataforma y toman efecto de inmediato.',
    roleUpdated: 'Rol actualizado.',
    roleFailed: 'No se pudo actualizar el rol.',

    isBlocked: 'Bloqueado',
    isGuest: 'Cuenta de invitado',
    isHighRisk: 'Alto riesgo',
    canMakeOrders: 'Puede hacer órdenes',
    betaStores: 'Puede ver tiendas beta',
    alphaStores: 'Puede ver tiendas alfa',
    betaInfoCards: 'Puede ver tarjetas informativas beta',

    passwordSection: 'Contraseña',
    resetPassword: 'Restablecer contraseña',
    resetPasswordRandom: 'Restablecer a una contraseña aleatoria',
    resetPasswordConfirmTitle: '¿Restablecer esta contraseña?',
    resetPasswordConfirmBody:
      'Se genera una contraseña aleatoria y se copia a tu portapapeles. La contraseña actual deja de funcionar de inmediato.',
    newPassword: 'Nueva contraseña',

    codesSection: 'Códigos de acceso',
    sendLoginCode: 'Enviar código de acceso',
    resetLoginCode: 'Restablecer código de acceso',
    getManagerCode: 'Obtener código de gerente',
    generateManagerCode: 'Generar nuevo código de gerente',

    dangerSection: 'Zona de riesgo',
    deleteUser: 'Eliminar usuario',
    deleteConfirmTitle: '¿Eliminar esta cuenta?',
    deleteConfirmBody:
      'Esto elimina permanentemente a {{name}} y no se puede deshacer.',
    funding: {
      credit: 'Crédito',
      debit: 'Débito',
      prepaid: 'Prepagada',
      unknown: 'Desconocido',
    },
  },

  accountRequests: {
    title: 'Solicitudes de cuenta',
    subtitle:
      'Personas que solicitaron una cuenta. Al aprobar una se le envía un enlace de registro.',
    searchPlaceholder: 'Buscar por nombre, negocio, correo o teléfono...',
    showing: 'Mostrando',
    all: 'Todas',
    view: 'Ver',

    resultCount_one: '{{count}} solicitud',
    resultCount_other: '{{count}} solicitudes',
    noneInQueue: 'No hay nada por el momento.',
    noResults: 'Nada coincidió con "{{term}}".',
    loadFailed: 'No se pudieron cargar las solicitudes de cuenta.',

    applicant: 'Solicitante',
    email: 'Correo',
    phone: 'Teléfono',
    requestedOn: 'Solicitada',
    statusColumn: 'Estado',
    noName: 'Sin nombre',

    status: {
      pending: 'Pendiente',
      approved: 'Aprobada',
      rejected: 'Rechazada',
      registered: 'Registrada',
    },

    requestDetails: 'Detalles de la solicitud',
    linkExpiresOn: 'enlace válido hasta {{date}}',
    linkExpiredOn: 'enlace vencido el {{date}}',
    alreadyRegistered:
      'El solicitante ya creó su cuenta, así que la solicitud ya no se puede modificar.',

    approve: 'Aprobar',
    approveConfirm:
      'Se le envía a {{name}} un enlace de registro por correo, válido por tiempo limitado.',
    approvedToast: 'Solicitud aprobada.',

    reject: 'Rechazar',
    rejectConfirm: 'La solicitud de {{name}} queda rechazada y no se puede deshacer desde aquí.',
    rejectionMessage: 'Mensaje (opcional)',
    rejectionPlaceholder: 'Se le envía al solicitante junto con la decisión.',
    rejectedToast: 'Solicitud rechazada.',

    getApprovalLink: 'Copiar enlace de registro',
    linkCopied: 'Enlace de registro copiado al portapapeles.',

    saved: 'Cambios guardados.',
    actionFailed: 'No se pudo completar esa acción.',

    businessName: 'Nombre del negocio',
    firstName: 'Nombre',
    lastName: 'Apellido',
    gender: 'Género',
    male: 'Masculino',
    female: 'Femenino',
    notSaid: 'Prefiero no decir',
    addressLine1: 'Dirección línea 1',
    addressLine2: 'Dirección línea 2',
    city: 'Ciudad',
    state: 'Estado',
    country: 'País',
    externalClientId: 'ID de cliente externo',
    businessLicense: 'Licencia del negocio',
    referralCode: 'Código de referido',
  },

  pageHistory: {
    help: 'Páginas que abrió esta cuenta en todas sus apps, de la más reciente a la más antigua.',
    allApps: 'Todas las apps',
    apps: {
      'admin-portal': 'Portal de administración',
      'webfront': 'Webfront'
    },
    lastDays_one: 'Último día',
    lastDays_other: 'Últimos {{count}} días',
    retained: 'Todo lo guardado',
    today: 'Hoy',
    yesterday: 'Ayer',
    global: 'Global',
    none: 'No hay páginas registradas para esta cuenta en este periodo.',
    noneForApp: 'No hay páginas registradas en esta app para esta cuenta en este periodo.',
    failed: 'No se pudo cargar el historial de páginas.',
    loadMore: 'Cargar más',
  },

  /**
   * The app usage report. Reads the same visits as `pageHistory`, across
   * accounts instead of for one, so the app names are shared with it.
   */
  usage: {
    title: 'Uso de las aplicaciones',
    subtitle:
      'Qué páginas abre la gente y cuándo, para una persona o un grupo junto a otro.',
    retention: 'Las visitas se conservan por 180 días.',
    loadFailed: 'No se pudo cargar el informe.',
    rangeInvalid: 'La fecha de inicio es posterior a la fecha final.',
    noSubjects: 'Añade una persona o un grupo para ver cómo usan las aplicaciones.',
    noVisits: 'No hay visitas en este periodo.',
    noPages: 'No se abrió ninguna página en este periodo.',

    from: 'Desde',
    to: 'Hasta',
    app: 'Aplicación',

    subjects: 'Comparando',
    subjectsHelp:
      'Cada persona o grupo se muestra por separado. Se comparan, nunca se suman, así que pueden coincidir.',
    subjectsFull_one: 'Uno es el máximo que se puede comparar a la vez.',
    subjectsFull_other:
      '{{count}} es el máximo que se puede comparar a la vez. Quita uno para añadir otro.',
    addSubject: 'Añadir',
    addSubjectHelp: 'Una cuenta, o todos los que cumplan un conjunto de filtros.',
    removeSubject: 'Quitar {{name}}',
    person: 'Persona',
    group: 'Grupo',
    everyone: 'Todos',
    unnamed: 'Cuenta sin nombre',

    personPlaceholder: 'Buscar por correo, nombre o teléfono',
    personHint: 'Escribe al menos tres caracteres.',
    personNone: 'Ninguna cuenta coincide.',
    personFailed: 'No se pudo realizar la búsqueda.',
    alreadyAdded: 'Añadida',

    any: 'Cualquiera',
    gender: 'Género',
    male: 'Masculino',
    female: 'Femenino',
    genderNotSaid: 'Género no indicado',
    ageGroup: 'Grupo de edad',
    ageCaveat:
      'Las cuentas sin fecha de nacimiento quedan fuera de un grupo de edad.',
    accountType: 'Tipo de cuenta',
    registered: 'Registradas',
    guests: 'Invitados',
    addGroup: 'Añadir grupo',
    addEveryone: 'Añadir a todos',

    summaryTitle: 'Resumen',
    summaryHelp:
      'El cambio junto a las visitas es contra la misma cantidad de días justo antes de este periodo.',
    subject: 'Quién',
    members: 'Cuentas',
    activeUsers: 'Usuarios activos',
    visits: 'Visitas',
    sessions: 'Sesiones',
    visitsPerUser: 'Visitas por usuario activo',
    pagesPerSession: 'Páginas por sesión',
    lastSeen: 'Última visita',
    viewMembers: 'Ver cuentas',
    openCustomer: 'Abrir cliente',

    trendTitle: 'Tendencia',
    trendMetric: 'Qué graficar',
    trendHelp: {
      perUser:
        'Visitas de cada día divididas entre las cuentas activas ese día, para que una persona y un grupo grande compartan escala.',
      activeUsers: 'Cuentas con al menos una visita cada día.',
      visits: 'Páginas abiertas cada día.',
    },

    pagesTitle: 'Páginas principales',
    pagesHelp:
      'Cada cifra es la proporción de esa página en las visitas del propio sujeto. Las barras usan una sola escala en toda la tabla.',
    page: 'Página',
    pageCell: '{{visits}} visitas de {{users}} cuentas',
    showAllPages: 'Mostrar las {{count}} páginas',
    showFewer: 'Mostrar menos',

    heatTitle: 'Cuándo',
    heatHelp:
      'Visitas por día de la semana y hora, en tu zona horaria. Cada cuadrícula se sombrea contra su propia hora más activa.',
    heatPeak_one: 'Hora más activa: {{formatted}} visita',
    heatPeak_other: 'Hora más activa: {{formatted}} visitas',

    favoriteStoreIs: 'Favorita: {{store}}',
    joinedFrom: 'Registrada desde',
    joinedTo: 'Registrada hasta',
    joinedBetween: 'Registradas {{from}} – {{to}}',
    joinedSince: 'Registradas desde {{date}}',
    joinedBefore: 'Registradas hasta {{date}}',

    distinctTitle: 'Qué los distingue',
    distinctHelp:
      'Páginas que cada sujeto abre más, o menos, que todos. El múltiplo compara la proporción de la página en sus visitas con su proporción en las de todos, que se muestra al lado.',
    usesMore: 'Más que todos',
    usesLess: 'Menos que todos',
    never: 'Nunca',
    nothingStandsOut: 'Nada destaca.',
    tooFewVisits_one:
      'Menos de {{count}} visita en este periodo, muy pocas para comparar.',
    tooFewVisits_other:
      'Menos de {{count}} visitas en este periodo, muy pocas para comparar.',

    moversTitle: 'En alza y en baja',
    moversHelp:
      'Páginas cuya proporción en las visitas de cada sujeto cambió más contra la misma cantidad de días justo antes. Las cifras son puntos porcentuales.',
    rising: 'En alza',
    falling: 'En baja',
    nothingMoved: 'Nada cambió.',
    noEarlierPeriod:
      'El periodo anterior a este ya no se conserva, así que no hay con qué comparar.',
    tooFewVisitsBoth_one:
      'Menos de {{count}} visita en este periodo o en el anterior, muy pocas para comparar.',
    tooFewVisitsBoth_other:
      'Menos de {{count}} visitas en este periodo o en el anterior, muy pocas para comparar.',

    breakdownTitle: 'Desglose',
    breakdownHelp:
      'Las visitas de cada sujeto divididas de una manera. Las cifras son proporciones de las visitas del propio sujeto.',
    breakdownBy: 'Dividir por',
    dimensions: {
      age: 'Grupo de edad',
      gender: 'Género',
      language: 'Idioma',
      domain: 'Aplicación',
      business: 'Tienda',
    },
    ageUnknown: 'Sin fecha de nacimiento',
    ageUnder18: 'Menores de 18',
    otherLanguage: 'Otro',

    retentionTitle: 'Quién regresó',
    retentionHelp:
      'Por semana, de domingo a sábado. Nueva significa vista por primera vez en las visitas que aún se conservan, no una cuenta nueva, así que las primeras semanas registradas son casi todas nuevas.',
    returning: 'Recurrentes',
    newAccounts: 'Nuevas',
    tallestBar: 'Barra más alta: {{formatted}} cuentas',
    weekOf: 'Semana del {{date}}',
    cohortTitle:
      'De las cuentas vistas por primera vez cada semana, la proporción activa en las semanas siguientes',
    firstSeen: 'Primera visita, semana del',
    cohortCell: '{{users}} de {{size}} cuentas',
    lapsedCount_one:
      '{{formatted}} cuenta estuvo activa justo antes de este periodo y no durante él.',
    lapsedCount_other:
      '{{formatted}} cuentas estuvieron activas justo antes de este periodo y no durante él.',
    viewLapsed: 'Ver',
    lapsedHelp:
      'Cuentas de este grupo que visitaron en la misma cantidad de días justo antes de este periodo y no lo han hecho desde entonces. Las visitas y fechas son de ese periodo anterior.',
    noLapsed: 'Nadie dejó de visitar.',

    exportCsv: 'Exportar CSV',
    exportFailed: 'No se pudo generar la exportación.',
    exportCapped_one: 'La exportación se detiene en {{formatted}} cuenta. Hay más.',
    exportCapped_other: 'La exportación se detiene en {{formatted}} cuentas. Hay más.',

    previousVisits: 'Visitas en el periodo anterior',
    lastSeenUtc: 'Última visita (UTC)',
    pageKey: 'Clave de página',
    share: 'Proporción',

    flowsTitle: 'Recorridos',
    flowsHelp:
      'Cómo se mueven las sesiones por las aplicaciones. Una sesión es una pestaña del navegador, no un inicio de sesión, y solo se cuenta la parte que cae dentro de este periodo.',
    flowEntries: 'Empieza en',
    flowExits: 'Termina en',
    flowNext: 'Qué sigue',
    flowDepth: 'Duración de la sesión',
    flowDepthHeading: 'Páginas en la sesión',
    flowAfter: 'Después de abrir',
    flowCell: '{{part}} de {{total}} sesiones',
    nextCell: '{{part}} de {{total}} visitas a la página',
    sessionEnded: 'Nada: la sesión terminó',
    noSessions: 'No se registraron sesiones en este periodo.',
    noNext: 'Nadie abrió esta página en este periodo.',
    sessionCount_one: '{{formatted}} sesión',
    sessionCount_other: '{{formatted}} sesiones',
    fromVisitCount_one: '{{formatted}} visita a la página',
    fromVisitCount_other: '{{formatted}} visitas a la página',
    depth: {
      one: '1 página',
      range: '{{range}} páginas',
      many: '11 páginas o más',
    },

    unusedTitle: 'Páginas del portal poco abiertas',
    unusedHelp:
      'Páginas del portal administrativo que entre todos abrieron menos de cinco veces en este periodo, sin importar a quién se compare arriba. Léelo en un periodo largo: una página que se usa una vez por trimestre está quieta en cualquier mes.',
    neverOpened: 'Nunca abierta',
    noUnused_one: 'Todas las páginas se abrieron al menos {{count}} vez.',
    noUnused_other: 'Todas las páginas se abrieron al menos {{count}} veces.',

    savedViews: 'Vistas guardadas',
    savedViewsHelp:
      'Comparaciones que has nombrado, guardadas en este navegador. Una vista guardada sin elegir fechas se mantiene en los últimos 30 días.',
    saveCurrent: 'Guardar lo que está en pantalla como',
    viewNamePlaceholder: 'p. ej. Mujeres 25–34 vs todos',
    savedViewsFull: 'La lista está llena. Guardar otra elimina la más antigua.',
    noSavedViews: 'Aún no hay nada guardado.',
    deleteView: 'Eliminar {{name}}',

    membersHelp: 'Cuentas de este grupo que visitaron en el periodo, las más activas primero.',
    visitCount_one: '{{formatted}} visita',
    visitCount_other: '{{formatted}} visitas',
  },

  pagination: {
    showing: 'Mostrando {{first}}-{{last}} de {{total}}',
    pageOf: 'Página {{page}} de {{pages}}',
    pageSize: 'Filas por página',
    previous: 'Página anterior',
    next: 'Página siguiente',
  },

  orders: {
    title: 'Órdenes',
    subtitle: 'Todas las órdenes de todas las tiendas, filtradas por estado y horario.',
    searchPlaceholder: 'Buscar por código de confirmación, cliente o teléfono...',
    searchLabel: 'Buscar órdenes',

    order: 'Orden',
    typeColumn: 'Tipo',
    customer: 'Cliente',
    total: 'Total',
    payment: 'Pago',
    statusColumn: 'Estado',
    serviceWindow: 'Horario de servicio',
    documents: 'Documentos',
    signature: 'Firma',
    ebtReceipt: 'Recibo EBT',
    new: 'Nueva',

    itemCount_one: '{{count}} artículo',
    itemCount_other: '{{count}} artículos',
    resultCount_one: '{{count}} orden',
    resultCount_other: '{{count}} órdenes',
    totalRevenue: 'Ingresos',

    noResultsTitle: 'Ninguna orden coincide',
    noResultsBody: 'Prueba otro estado, un rango de tiempo más amplio, o limpia los filtros.',
    errorBody: 'No se pudieron cargar las órdenes. Inténtalo de nuevo.',

    items: 'Artículos',
    history: 'Historial',
    item: 'Artículo',
    upc: 'UPC',
    quantity: 'Cant.',
    price: 'Precio',
    noItems: 'Esta orden no tiene artículos.',
    noLogs: 'No hay historial para esta orden.',
    itemsFailed: 'No se pudieron cargar los artículos.',
    logsFailed: 'No se pudo cargar el historial.',

    sort: 'Orden',
    oldest: 'Más antiguas primero',
    newest: 'Más recientes primero',
    actions: 'Acciones',
    logs: 'Registros',
    voided: 'Anulado',

    general: 'General',
    detailsTab: 'Detalles',
    pickupName: 'Nombre de recogido',
    dates: 'Fechas',
    created: 'Creada',
    expected: 'Esperada',
    leadTime: 'Tiempo de anticipación',
    hours_one: '{{count}} hora',
    hours_other: '{{count}} horas',
    dispatched: 'Despachada',
    method: 'Método',
    giftCard: 'Tarjeta de regalo',
    card: 'Tarjeta',
    cardHolder: 'Titular',
    authorized: 'Autorizada',
    charged: 'Cobrada',
    ivuControl: 'Control IVU',
    totals: 'Totales',
    subtotal: 'Subtotal',
    municipalTax: 'Impuesto municipal',
    stateTax: 'Impuesto estatal',
    serviceFee: 'Cargo por servicio',
    deliveryFee: 'Entrega',
    discount: 'Descuento',
    return: 'Devolución',
    transfers: 'Transferencias',
    transferPending: 'Pendiente',
    transferPaidOut: 'Pagado',
    transferPaidAmount: 'Monto pagado',
    posLane: 'Caja POS',
    terminalId: 'ID de terminal',
    version: 'Versión',
    deviceName: 'Nombre del equipo',
    deviceModel: 'Modelo del equipo',
    deviceManufacturer: 'Fabricante',
    delivery: 'Entrega',
    zone: 'Zona',
    driver: 'Conductor',
    driverPhone: 'Teléfono del conductor',
    driverInstructions: 'Instrucciones al conductor',
    deliveryAddress: 'Dirección de entrega',
    noAddress: 'Esta orden no tiene dirección de entrega.',

    fulfilment: 'Despacho',
    dispatch: 'Despachar',
    dispatched2: 'Despachada',
    markDelivered: 'Marcar como entregada',
    changeToPickup: 'Cambiar a recogido',
    changeStatus: 'Cambiar estado',
    selectStatus: 'Selecciona un estado',
    changePaymentMethod: 'Cambiar método de pago',
    selectPayment: 'Selecciona un método',
    toggleServiceFee: 'Alternar cargo por servicio',
    forceCapture: 'Forzar cobro',
    cancelOrder: 'Cancelar orden',
    cancelConfirmTitle: '¿Cancelar esta orden?',
    cancelConfirmBody: 'La orden {{code}} será cancelada. Esto no se puede deshacer desde aquí.',
    actionFailed: 'No se pudo completar la acción.',
    instructionsSaved: 'Instrucciones guardadas.',
    changedToPickup: 'Orden cambiada a recogido.',
    statusChanged: 'Estado actualizado.',
    paymentChanged: 'Método de pago actualizado.',
    serviceFeeToggled: 'Cargo por servicio alternado.',
    captured: 'Pago cobrado.',
    cancelled: 'Orden cancelada.',
    markedDelivered: 'Orden marcada como entregada.',
    moreActionsNote:
      'Asignar conductor y cambiar tarjeta aún no están implementados.',

    receipt: 'Recibo',
    print: 'Imprimir',
    close: 'Cerrar',
    terminalData: 'Datos del terminal',
    service: 'Servicio',
    contact: 'Contacto',
    date: 'Fecha',
    store: 'Tienda',
    location: 'Ubicación',
    itemsIncluded: 'Artículos incluidos ({{count}})',
    itemsNotIncluded: 'Artículos no incluidos ({{count}})',
    paymentInformation: 'Información de pago',
    serviceType: 'Tipo de servicio',
    changeToDelivery: 'Cambiar a entrega',
    changedToDelivery: 'Orden cambiada a entrega.',
    chooseAddress: 'Elige la dirección de entrega para esta orden.',
    addressesFailed: 'No se pudieron cargar las direcciónes.',
    noDeliverableAddresses:
      'Este cliente no tiene una dirección a la que esta tienda pueda entregar.',

    paymentLogs: 'Registros de pago',
    noPaymentLogs: 'No hay registros de pago para esta orden.',
    createdOn: 'Creado el',
    operation: 'Operación',
    preview: 'Vista previa',
    serviceCost: 'Costo de servicio',
    giftCardBalance: 'Balance de la tarjeta de regalo: {{amount}}',
    giftCardFailed: 'No se pudo cargar el balance de la tarjeta de regalo.',

    stats: {
      pending: 'Colocadas',
      processing: 'En proceso',
      waiting: 'En espera',
      inTransit: 'En tránsito',
      completed: 'Completadas',
    },

    filters: {
      any: 'Cualquiera',
      status: 'Estado',
      time: 'Tiempo',
      type: 'Tipo',
      payment: 'Pago',
      store: 'Tienda',
      allStores: 'Todas las tiendas',
      reset: 'Limpiar filtros',
      active: 'Activas',
      processing: 'En proceso',
      picking: 'Recogiendo',
      packing: 'Empacando',
      waiting: 'En espera',
      inTransit: 'En tránsito',
      completed: 'Completadas',
      cancelled: 'Canceladas',
      paymentFailed: 'Pago fallido',
      pending: 'Pendientes',
      past: 'Pasadas',
      yesterday: 'Ayer',
      today: 'Hoy',
      tomorrow: 'Mañana',
      future: 'Futuras',
      creditCard: 'Tarjeta de crédito',
    },

    type: {
      pickup: 'Recogido',
      delivery: 'Entrega',
      inStore: 'En tienda',
      deliPickup: 'Recogido deli',
      deliDelivery: 'Entrega deli',
      unknown: 'Desconocido',
    },

    status: {
      placed: 'Colocada',
      deliveryAssigned: 'Conductor asignado',
      accepted: 'Aceptada',
      ready: 'Lista',
      deliveryStarted: 'En ruta',
      customerPickedUp: 'Recogida',
      delivered: 'Entregada',
      storeCancelled: 'Cancelada por la tienda',
      userCancelled: 'Cancelada por el cliente',
      declined: 'Rechazada',
      pickingStarted: 'Recogiendo',
      pickingFinished: 'Recogida',
      packingStarted: 'Empacando',
      readyForPickup: 'Lista para recoger',
      pendingDeliveryAssignment: 'Esperando conductor',
      pendingPaypalPayment: 'Esperando PayPal',
      pendingPayment: 'Esperando pago',
      underReview: 'En revisión',
      ivuControlPending: 'Control IVU pendiente',
      unknown: 'Desconocido',
    },
  },

  memberships: {
    title: 'Planes de membresía',
    subtitle: 'Los planes que los clientes pueden comprar, y los beneficios que otorga cada uno.',
    searchPlaceholder: 'Buscar por nombre o localidad...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    createNew: 'Crear nuevo',
    noPlans: 'Todavía no se han creado planes de membresía.',
    noMatches: 'Ningún plan coincide con estos filtros.',
    loadFailed: 'No se pudieron cargar los planes de membresía.',

    location: 'Localidad',
    name: 'Nombre',
    description: 'Descripción',
    internalName: 'Nombre interno',
    isActive: 'Activo',
    active: 'Activo',
    inactive: 'Inactivo',
    any: 'Cualquiera',
    recurrence: 'Recurrencia',
    price: 'Precio',
    isOnPromo: 'En promoción',
    actions: 'Acciones',
    edit: 'Editar',
    benefits: 'Beneficios',
    ordering: 'Orden',
    includedMembers: 'Miembros incluidos',
    image: 'Imagen',
    clickOrDrag: 'Selecciona una imagen',
    uploadFailed: 'No se pudo subir la imagen.',
    update: 'Actualizar',
    create: 'Crear',
    saveFailed: 'No se pudieron guardar los cambios.',
    deleteFailed: 'No se pudo eliminar.',

    editPlan: 'Editar plan de membresía',
    createPlan: 'Crear plan de membresía',
    planSaved: 'Plan de membresía guardado.',
    deletePlan: 'Eliminar plan',
    planDeleted: 'Plan de membresía eliminado.',
    deletePlanConfirmTitle: '¿Eliminar este plan?',
    deletePlanConfirmBody:
      'Se elimina el plan y sus beneficios. Esto no se puede deshacer.',

    allowRenewal: 'Permitir renovación',
    allowPaypalSubscription: 'Permitir suscripción PayPal',
    hasLimit: 'Tiene límite',
    limit: 'Límite',
    hasPromo: 'Tiene promoción',
    promoStart: 'La promoción comienza',
    promoEnd: 'La promoción termina',
    promoPrice: 'Precio promociónal',
    dateExtensionType: 'Tipo de extensión de fecha',
    dateExtension: 'Extensión de fecha',
    hasAutoRenewalDiscount: 'Tiene descuento por renovación automática',
    autoRenewalDiscountType: 'Tipo de descuento',
    autoRenewalDiscountValue: 'Valor del descuento',

    addBenefit: 'Añadir beneficio',
    editBenefit: 'Editar beneficio',
    benefitSaved: 'Beneficio guardado.',
    deleteBenefit: 'Eliminar beneficio',
    benefitDeleted: 'Beneficio eliminado.',
    deleteBenefitConfirmTitle: '¿Eliminar este beneficio?',
    deleteBenefitConfirmBody: 'Esto no se puede deshacer.',
    noBenefits: 'Este plan aún no tiene beneficios.',
    benefitsFailed: 'No se pudieron cargar los beneficios.',
    isPromoBenefit: 'Beneficio promociónal',
    applicableEventType: 'Tipo de evento aplicable',
    applicableServiceType: 'Tipo de servicio aplicable',
    type: 'Tipo',
    amount: 'Monto',

    recurrenceOptions: {
      none: 'Ninguna',
      daily: 'Diaria',
      weekly: 'Semanal',
      biweekly: 'Quincenal',
      monthly: 'Mensual',
      bimonthly: 'Bimensual',
      quarterly: 'Trimestral',
      semiyearly: 'Semestral',
      yearly: 'Anual',
    },

    discountType: {
      fixed: 'Fijo',
      percentage: 'Porcentaje',
    },

    extension: {
      none: 'Ninguna',
      days: 'Días',
      weeks: 'Semanas',
      months: 'Meses',
      years: 'Años',
    },

    eventType: {
      rental: 'Alquiler',
      class: 'Clase',
      other: 'Otro',
      yoga: 'Yoga',
      service: 'Servicio',
      none: 'Ninguno',
    },

    benefitType: {
      fixedDiscount: 'Descuento fijo',
      percentDiscount: 'Descuento porcentual',
      specialPrice: 'Precio especial',
      free: 'Gratis',
      earlyVisibility: 'Visibilidad anticipada',
      posFixedDiscount: 'Descuento fijo POS',
      posPercentDiscount: 'Descuento porcentual POS',
    },
  },

  events: {
    title: 'Eventos',
    subtitle: 'Los eventos en los que los clientes pueden reservar un horario.',
    searchPlaceholder: 'Buscar por nombre, localidad o anfitrión...',
    showingCount_one: 'Mostrando {{count}} evento {{state}}',
    showingCount_other: 'Mostrando {{count}} eventos {{state}}',
    createNew: 'Crear nuevo',
    noEvents: 'Todavía no se han creado eventos.',
    noMatches: 'Ningún evento coincide con estos filtros.',
    loadFailed: 'No se pudieron cargar los eventos.',

    all: 'Todos',
    any: 'Cualquiera',
    active: 'Activo',
    inactive: 'Inactivo',
    isActive: 'Activo',
    name: 'Nombre',
    internalName: 'Nombre interno',
    description: 'Descripción',
    informationalData: 'Datos informativos',
    additionalInformation: 'Información adicional',
    location: 'Localidad',
    host: 'Anfitrión',
    typeLabel: 'Tipo',
    serviceType: 'Tipo de servicio',
    subscriberVisibility: 'Visibilidad para suscriptores',
    price: 'Precio',
    image: 'Imagen',
    chooseImage: 'Selecciona una imagen',
    uploadFailed: 'No se pudo subir la imagen.',
    actions: 'Acciones',
    edit: 'Editar',
    create: 'Crear',
    update: 'Actualizar',
    submit: 'Enviar',
    none: 'Ninguno',
    selectHost: 'Selecciona un anfitrión',
    selectLocation: 'Selecciona una localidad',
    selectServiceType: 'Selecciona un tipo de servicio',
    timeSlots: 'Horarios',
    copyLink: 'Copiar enlace',
    linkCopied: 'Enlace copiado.',
    copyFailed: 'No se pudo copiar el enlace.',

    editEvent: 'Editar evento',
    createEvent: 'Crear evento',
    eventSaved: 'Evento guardado.',
    saveFailed: 'No se pudieron guardar los cambios.',
    deleteEvent: 'Eliminar evento',
    eventDeleted: 'Evento eliminado.',
    deleteFailed: 'No se pudo eliminar.',
    deleteConfirmTitle: '¿Eliminar este evento?',
    deleteConfirmBody: 'Se eliminan el evento y sus horarios. Esto no se puede deshacer.',

    timeSlotsFor: 'Horarios de',
    basic: 'Básico',
    advanced: 'Avanzado',
    startTime: 'Hora de inicio',
    endTime: 'Hora de fin',
    rangeStart: 'Desde',
    rangeEnd: 'Hasta',
    capacity: 'Capacidad',
    shouldBeActive: '¿El horario debe estar activo?',
    selectedHost: 'Anfitrión seleccionado',
    clearHost: 'Quitar anfitrión',
    searchCustomer: 'Buscar por nombre, correo o teléfono...',
    noCustomers: 'Ningún cliente coincide con esa búsqueda.',
    slotMinutes: 'Duración del horario (minutos)',
    slotMinutesHint: 'Cuánto dura cada horario generado.',
    bufferMinutes: 'Margen (minutos)',
    bufferMinutesHint: 'Espacio entre el fin de un horario y el inicio del siguiente.',
    preview: 'Vista previa',
    pickStartAndEnd: 'Selecciona una hora de inicio y de fin.',
    slotCreated: 'Horario creado.',
    slotsCreated: '{{count}} horarios creados.',
    slotUpdated: 'Horario actualizado.',
    slotRemoved: 'Horario eliminado.',
    slotActionFailed: 'No se pudo completar.',
    slotsFailed: 'No se pudieron cargar los horarios.',
    noSlots: 'No hay horarios en esta lista.',
    until: 'hasta',
    min: 'min',
    overBooked: 'Sobre reservado',
    booked: 'Lleno',
    available: 'Disponible',
    duplicate: 'Hora de inicio duplicada',
    cloneSlot: 'Clonar horario',
    cloneSlotBody: 'Se crea una copia con la misma hora y capacidad.',
    clone: 'Clonar',
    removeSlot: 'Eliminar horario',
    removeSlotBody: 'Esto no se puede deshacer.',
    remove: 'Eliminar',
    editTimeSlot: 'Editar horario',
    conflictingEvents: 'Horarios en conflicto',
    conflictsBody: 'Estos horarios se solapan con el que intentas crear.',
    createAnyway: 'Crear de todos modos',
    slotsToCreate: '{{count}} horarios por crear',
    slotsToCreateBody: 'Se crearán uno tras otro.',
    qrCode: 'Código QR de registro',
    resolution: 'Resolución',
    saveAsImage: 'Guardar como imagen',
    saveAsPdf: 'Guardar como PDF',

    bookings: 'Reservas',
    addBooking: 'Añadir reserva',
    customer: 'Cliente',
    shouldCreateCharge: '¿Crear un cargo para esta reserva?',
    bookingCreated: 'Reserva creada.',
    bookingsFailed: 'No se pudieron cargar las reservas.',
    noBookings: 'Nadie ha reservado este horario todavía.',
    bookingActionFailed: 'No se pudo completar.',
    exportList: 'Exportar lista',
    firstName: 'Nombre',
    lastName: 'Apellido',
    phone: 'Teléfono',
    email: 'Correo electrónico',
    status: 'Estado',
    createdOn: 'Creada el',
    comments: 'Comentarios',
    outstanding: 'Pendiente',
    payments: 'Pagos',
    paid: 'pagado',
    refunded: 'reembolsado',
    refund: 'Reembolsar',
    refundAmount: 'Monto a reembolsar',
    refundBody: 'Se puede reembolsar hasta {{max}}.',
    cancelPurchase: 'Cancelar solicitud',
    cancelPurchaseBody: 'Aplica un cargo por cancelación de {{fee}}.',
    purchaseCancelled: 'Solicitud de pago cancelada.',
    editBooking: 'Editar reserva',
    bookingUpdated: 'Reserva actualizada.',
    moveBooking: 'Mover reserva',
    moveBookingBody: 'Selecciona el horario al que debe moverse esta reserva.',
    move: 'Mover',
    bookingMoved: 'Reserva movida.',
    includePast: 'Incluir horarios pasados',
    noMoveTargets: 'No hay otros horarios disponibles.',
    increaseCapacity: 'Añadir un espacio si el horario está lleno',
    reason: 'Razón',
    willAddSpot: 'Se añadirá un espacio — la capacidad pasa a {{capacity}}.',
    full: 'lleno',
    recordPayment: 'Registrar pago',
    recordPaymentBody: 'Registra un pago recibido fuera de la plataforma.',
    applyPaymentTo: 'Aplicar el pago a',
    newPaymentOption: 'Una nueva solicitud de pago',
    amount: 'Monto',
    method: 'Método',
    reference: 'Referencia',
    adminNotes: 'Notas del administrador',
    paymentRecorded: 'Pago registrado.',
    createPaymentRequest: 'Crear solicitud de pago',
    createPurchaseBody: 'Se crea una solicitud de pago de {{amount}} para esta reserva.',
    purchaseCreated: 'Solicitud de pago creada.',
    cancelBooking: 'Cancelar reserva',
    cancelBookingBody: 'Aplica un cargo por cancelación de {{fee}}.',
    bookingCancelled: 'Reserva cancelada.',

    past: 'pasado',
    paymentMethod: {
      card: 'Tarjeta',
      paypal: 'PayPal',
      placetopay: 'PlaceToPay',
      athMovil: 'ATH Movil',
      cash: 'Efectivo',
      athMovilManual: 'ATH Movil (manual)',
      bankTransfer: 'Transferencia bancaria',
      check: 'Cheque',
      externalTerminal: 'Terminal externo',
      paypalInvoice: 'PayPal Invoice',
      otherOffline: 'Otro (fuera de línea)',
      unknown: 'Desconocido',
    },

    type: {
      rental: 'Alquiler',
      class: 'Clase',
      other: 'Entrada libre',
      yoga: 'Yoga',
      service: 'Servicio',
      none: 'Ninguno',
    },

    visibility: {
      free: 'Gratis',
      tier1: 'Nivel 1',
      tier2: 'Nivel 2',
      tier3: 'Nivel 3',
      tier4: 'Nivel 4',
      anySubscription: 'Cualquier suscripción',
    },

    bookingStatus: {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada',
    },

    slots: {
      activeUpcoming: 'Activos próximos',
      activePast: 'Activos pasados',
      inactiveUpcoming: 'Inactivos próximos',
      inactivePast: 'Inactivos pasados',
    },
    packages: 'Paquetes',
    addPackage: 'Añadir paquete',
    editPackage: 'Editar paquete',
    packageSaved: 'Paquete guardado.',
    packageSaveFailed: 'El paquete no se pudo guardar.',
    packagesFailed: 'Los paquetes no se pudieron cargar.',
    packageNamesRequired: 'El nombre en inglés y en español son obligatorios.',
    sortOrder: 'Orden',
    retired: 'Retirado',
    retire: 'Retirar',
    reactivate: 'Volver a poner a la venta',
    packageReactivated: 'Paquete de vuelta a la venta.',
    retirePackage: '¿Retirar este paquete?',
    retirePackageBody:
      'Sale de la venta y sigue visible, así que las reservas que lo eligieron lo siguen nombrando. Se puede volver a poner a la venta más adelante.',
    packageRetired: 'Paquete retirado.',
    noPackagesBody:
      'Este evento se vende a su propio precio. Al añadir un paquete, los clientes tendrán que elegir uno para reservar.',
    firstPackageBody:
      'Este es el primer paquete de este evento. Una vez que exista, toda reserva nueva tendrá que elegir un paquete.',
    packagePriceBody:
      'Cambiar el precio cambia lo que cuesta de ahora en adelante. Las reservas ya vendidas conservan el precio al que se vendieron.',
    packagesRequiredNote:
      'Los clientes deben elegir un paquete para reservar este evento.',

    changePackage: 'Cambiar paquete',
    changePackageBody: 'Mueve esta reserva a otro paquete del mismo evento.',
    noOtherPackages: 'Este evento no tiene otro paquete a la venta.',
    adjustPurchaseAmount: 'Ajustar el cargo',
    adjustPurchaseOn:
      'La reserva se vuelve a cotizar al nuevo paquete. Cualquier diferencia queda como balance por saldar.',
    adjustPurchaseOff: 'Solo cambia el paquete. El dinero no se mueve.',
    packageChanged: 'Paquete cambiado.',

    package: 'Paquete',

    stillNeeded: 'Todavía falta: {{fields}}.',

  },

  gifts: {
    title: 'Regalos de membresía',
    subtitle: 'Planes comprados para otra persona, en espera de asignarse a una cuenta.',
    searchPlaceholder: 'Buscar por comprador o destinatario...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar los regalos.',
    noGiftsTitle: 'No se encontraron regalos',
    noGiftsBody: 'Nada coincide con estos filtros. Prueba un rango de fechas más amplio.',

    any: 'Cualquiera',
    from: 'Desde',
    to: 'Hasta',
    location: 'Localidad',
    status: 'Estado',
    pending: 'Pendiente',
    assigned: 'Asignado',
    failed: 'Fallido',

    purchasedOn: 'Comprado el',
    buyer: 'Comprador',
    recipient: 'Destinatario',
    plan: 'Plan',
    actions: 'Acciones',

    assign: 'Asignar',
    assignMembership: 'Asignar membresía',
    assignBody: 'Elige la cuenta a la que se debe asignar este regalo para {{recipient}}.',
    user: 'Usuario',
    searchUser: 'Buscar por nombre, correo o teléfono...',
    noUsers: 'Ningún usuario coincide con esa búsqueda.',
    remove: 'Quitar',
    assignedToast: 'Membresía asignada.',
    assignFailed: 'No se pudo asignar la membresía.',
  },

  vouchers: {
    title: 'Certificados de regalo',
    subtitle: 'Los certificados que los clientes pueden comprar para regalar. Cada uno es un paquete de reservas gratis.',
    searchPlaceholder: 'Buscar por nombre o descripción...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar los certificados.',
    noProducts: 'Todavía no hay certificados. Crea el primero para empezar a vender certificados de regalo.',
    noMatches: 'Ningún certificado coincide con estos filtros.',

    any: 'Cualquiera',
    status: 'Estado',
    onSale: 'A la venta',
    offSale: 'Fuera de venta',
    onSaleHint: 'Fuera de venta lo oculta de la página de regalos. Los certificados ya vendidos siguen funcionando.',
    actions: 'Acciones',
    edit: 'Editar',
    issued: 'Vendidos',

    createProduct: 'Nuevo certificado',
    editProduct: 'Editar certificado',
    editNote: 'Los cambios aplican a los certificados que se vendan de ahora en adelante. Los ya vendidos mantienen las condiciones con las que se compraron.',
    name: 'Nombre',
    description: 'Descripción',
    price: 'Precio',
    priceHint: 'Cubre cualquier evento o paquete elegible, sin importar su precio.',
    uses: 'Reservas',
    usesHint: 'Cuántas reservas gratis da un certificado.',
    usesCount_one: '{{count}} reserva',
    usesCount_other: '{{count}} reservas',
    validity: 'Válido por',
    validityHint: 'Se cuenta desde el día de la compra.',
    validityDays_one: '{{count}} día',
    validityDays_other: '{{count}} días',
    validityMonths_one: '{{count}} mes',
    validityMonths_other: '{{count}} meses',
    days: 'Días',
    months: 'Meses',
    sortOrder: 'Orden de presentación',

    restrictions: 'Se puede usar en',
    restrictionsNone: 'No hay nada seleccionado, así que se puede usar en cualquier evento. Limítalo con cualquier combinación de abajo.',
    restrictionsHint: 'Un evento tiene que coincidir con cada lista que tenga una selección, y con cualquier opción dentro de cada una.',
    anyEventAnywhere: 'Cualquier evento',
    locations: 'Localidades',
    serviceTypes: 'Tipos de servicio',
    eventTypes: 'Tipos de evento',
    events: 'Eventos específicos',
    anyLocation: 'Cualquier localidad',
    anyServiceType: 'Cualquier tipo de servicio',
    anyEventType: 'Cualquier tipo de evento',
    anyEvent: 'Cualquier evento',
    selectedCount_one: '{{count}} seleccionado',
    selectedCount_other: '{{count}} seleccionados',

    create: 'Crear certificado',
    update: 'Guardar cambios',
    saved: 'Certificado guardado.',
    saveFailed: 'No se pudo guardar el certificado.',
    deleteProduct: 'Quitar del catálogo',
    deleteConfirmTitle: '¿Quitar este certificado?',
    deleteConfirmBody: 'Ya no se venderá. Los certificados ya vendidos no se afectan y se pueden seguir usando. Para pausar las ventas, desactiva A la venta.',
    deleted: 'Certificado quitado.',
    deleteFailed: 'No se pudo quitar el certificado.',

    errors: {
      nameRequired: 'Escribe un nombre en inglés y en español.',
      usesInvalid: 'Un certificado necesita al menos una reserva.',
      validityInvalid: 'La validez tiene que ser al menos 1.',
      priceInvalid: 'El precio no puede ser negativo.',
    },

    voucher: 'Certificado',
    view: 'Ver',
    code: 'Código',
    holder: 'Titular',
    buyer: 'Comprado por',
    from: 'De',
    sentTo: 'Enviado a',
    codeOnly: 'Código entregado en persona',
    inPerson: 'Vendido en persona',
    complimentary: 'Cortesía',
    custom: 'Personalizado',
    sold: 'Vendido',
    kind: 'Tipo',
    claimedFilter: 'Reclamado',
    statusFilter: {
      active: 'Activo',
    },
    paid: 'Pagado',
    expires: 'Expira',
    never: 'Nunca',
    message: 'Mensaje',
    created: 'Creado',
    adminNotes: 'Notas del personal',
    usesOf: '{{used}} de {{total}} usados · quedan {{remaining}}',
    usesOfShort: '{{used}}/{{total}} usados',
    claimedOn: 'Reclamado el {{date}}',
    emailIn: 'Correos en {{language}}',
    issuedByStaff: 'Emitido por {{name}}',
    refundedAmount: '{{amount}} reembolsado',
    summary: 'Resumen',
    bookings: 'Reservas',
    payments: 'Pagos',
    history: 'Historial',
    noBookings: 'Todavía no se han hecho reservas con este certificado.',
    noPayments: 'No hay pagos registrados.',
    complimentaryNoPayment: 'Cortesía — no se pagó nada por este certificado.',
    noHistory: 'Todavía no ha cambiado de manos.',
    bookedByStaff: 'Reservado por {{name}}',
    useSpent: 'Usado',
    useReturned: 'Uso devuelto',
    nobody: 'nadie',
    detailFailed: 'No se pudo cargar el certificado.',
    actionFailed: 'No se pudo completar.',
    searchUser: 'Buscar por nombre, correo o teléfono...',
    noUsers: 'Ningún usuario coincide con la búsqueda.',
    clearUser: 'Escoger a otra persona',
    recipientEmail: 'Correo del destinatario',
    recipientName: 'Nombre del destinatario',
    emailLanguage: 'Idioma del correo',
    newHolder: 'Nuevo titular',
    noExpiry: 'No expira',
    newExpiry: 'Nueva fecha de expiración',
    refundAmount: 'Monto a reembolsar',
    refundHint: 'Sugerido {{suggested}} por los usos restantes. Se puede reembolsar hasta {{refundable}}.',
    reason: 'Razón (opcional)',
    reasonRequired: 'Razón',

    state: {
      pendingPayment: 'Pendiente de pago',
      paymentFailed: 'Pago fallido',
      cancelled: 'Compra abandonada',
      voided: 'Anulado',
      refunded: 'Reembolsado',
      used: 'Agotado',
      expired: 'Expirado',
      claimed: 'Reclamado',
      unclaimed: 'Sin reclamar',
    },

    payment: {
      success: 'Recibido',
      pending: 'Pendiente',
      failed: 'Fallido',
      voided: 'Anulado',
      refunded: '{{amount}} reembolsado',
    },

    claimMethod: {
      self: 'lo compró para sí',
      autoByEmail: 'por su correo',
      code: 'entró el código',
      admin: 'asignado por el personal',
    },

    change: {
      claimed: 'Reclamado por {{name}} ({{how}})',
      reassigned: 'Pasado de {{from}} a {{to}}',
      emailChanged: 'Correo cambiado de {{from}} a {{to}}',
    },

    actionDialog: {
      resend: {
        button: 'Reenviar correo',
        title: '¿Reenviar el correo del certificado?',
        body: 'Vuelve a enviar el correo que espera el destinatario: la invitación con el código, o el aviso de que se añadió a su cuenta si ya lo reclamó.',
        confirm: 'Reenviar',
        done: 'Correo reenviado.',
      },
      email: {
        button: 'Cambiar correo',
        title: 'Cambiar a dónde se envió',
        body: 'Nadie ha reclamado este certificado. Se enviará a la nueva dirección, directo a la cuenta si alguna la usa.',
        confirm: 'Cambiar y enviar',
        done: 'Certificado enviado a la nueva dirección.',
      },
      reassign: {
        button: 'Reasignar',
        title: 'Pasar a otra cuenta',
        body: 'Los usos restantes pasan a la cuenta que escojas. Las reservas ya hechas se quedan con quien eran.',
        confirm: 'Pasar certificado',
        done: 'Certificado reasignado.',
      },
      extend: {
        button: 'Cambiar expiración',
        title: 'Cambiar la fecha de expiración',
        body: 'Funciona también con un certificado expirado. Los recordatorios empiezan de nuevo con la nueva fecha.',
        confirm: 'Guardar expiración',
        done: 'Expiración actualizada.',
      },
      void: {
        button: 'Anular',
        title: '¿Anular este certificado?',
        body: 'Ya no se podrá usar y no se devuelve dinero — para un error o mal uso. Las reservas ya hechas se mantienen. Para devolver dinero, reembólsalo.',
        confirm: 'Anular certificado',
        done: 'Certificado anulado.',
      },
      refund: {
        button: 'Reembolsar',
        title: 'Reembolsar este certificado',
        body: 'El dinero se devuelve por el mismo medio de pago; los pagos recibidos en persona solo se registran, así que devuélvelos a mano. El certificado deja de funcionar.',
        confirm: 'Reembolsar',
        done: 'Certificado reembolsado.',
      },
    },

    manage: {
      title: 'Certificados de regalo',
      subtitle: 'Todos los certificados vendidos o emitidos: quién lo tiene, qué le queda y las herramientas para corregirlo.',
      searchPlaceholder: 'Buscar por código, nombre o correo...',
      noResults: 'Ningún certificado coincide con estos filtros.',
    },

    issue: {
      title: 'Emitir certificado',
      what: 'Certificado',
      fromCatalogue: 'Del catálogo',
      custom: 'Personalizado',
      customHint: 'Un certificado único con sus propias condiciones. No se añade al catálogo.',
      pickProduct: 'Escoge un certificado',
      expiryPeriod: 'Un periodo desde hoy',
      expiryDate: 'En una fecha',
      who: 'Para',
      toAccount: 'Una cuenta',
      toEmail: 'Un correo',
      codeOnly: 'Solo código',
      emailHint: 'Si una cuenta usa esta dirección, se añade directo; si no, se envía una invitación con el código.',
      codeOnlyHint: 'No se envía nada. Dale al cliente el código que aparece en el certificado una vez creado.',
      messagePlaceholder: 'Opcional — aparece en el correo.',
      payment: 'Pago',
      complimentaryHint: 'Gratis — mercadeo, una disculpa, un regalo. Se reporta aparte de las ventas.',
      amount: 'Monto recibido',
      method: 'Pagado con',
      reference: 'Referencia',
      buyer: 'Cliente que pagó',
      buyerHint: 'Opcional. Recibe el recibo y lo ve en "Certificados que he enviado".',
      submit: 'Emitir certificado',
      done: 'Certificado emitido.',
      failed: 'No se pudo emitir el certificado.',
      errors: {
        product: 'Escoge un certificado del catálogo.',
        expiry: 'Escoge la fecha de expiración.',
        account: 'Escoge la cuenta a la que va.',
        email: 'Escribe el correo del destinatario.',
        amount: 'Escribe el monto recibido o márcalo como cortesía.',
      },
    },

    report: {
      title: 'Certificados de regalo',
      subtitle: 'Qué se vendió, qué pagaron esos certificados y qué se debe todavía en reservas futuras.',
      from: 'Desde',
      to: 'Hasta',
      basisNotice: 'Las ventas cuentan el día en que se pagaron y las reservas el día de la clase. Lo que se debe todavía es a la fecha de hoy, sin importar las fechas escogidas.',
      overviewGroup: 'Certificados de regalo',
      salesGroup: 'Ventas',
      usageGroup: 'Usados',
      liabilityGroup: 'Por cumplir',
      salesNet: 'Ventas de certificados',
      salesNetTooltip: 'Dinero recibido por certificados en el periodo, menos reembolsos. Ya está incluido en los ingresos netos.',
      soldSub_one: '{{count}} vendido · {{refunded}} reembolsado',
      soldSub_other: '{{count}} vendidos · {{refunded}} reembolsado',
      collected: 'Cobrado',
      vouchersSold_one: '{{count}} certificado',
      vouchersSold_other: '{{count}} certificados',
      refunded: 'Reembolsado',
      complimentary: 'Cortesías emitidas',
      complimentaryTooltip: 'Certificados regalados en el periodo. No son ingresos.',
      redeemedValue: 'Reservas pagadas con certificados',
      redeemedValueTooltip: 'El precio de las clases y alquileres del periodo cubiertos con un certificado. No son ingresos: el dinero entró cuando se vendió el certificado.',
      redemptionsSub_one: '{{count}} reserva',
      redemptionsSub_other: '{{count}} reservas',
      redemptions: 'Reservas',
      outstandingValue: 'Por cumplir en reservas',
      outstandingValueTooltip: 'Usos que faltan por reservar en certificados vigentes, valorados a lo que se pagó por uso. Las cortesías cuentan como cero.',
      outstandingSub_one: '{{count}} uso restante',
      outstandingSub_other: '{{count}} usos restantes',
      outstandingUses: 'Usos restantes',
      expiredUnused: 'Expirados sin usar',
      expiredUnusedTooltip: 'Usos que expiraron sin usarse en certificados que vencieron en el periodo, valorados a lo que se pagó por uso.',
    },
  },

  manageBookings: {
    title: 'Reservas',
    tabBookings: 'Reservas',
    tabPaymentReview: 'Revisión de pagos',
    subtitle: 'Todas las reservas de eventos, con lo pagado y lo que aún se debe.',
    searchPlaceholder: 'Buscar por nombre, correo o evento...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar las reservas.',
    detailFailed: 'No se pudo cargar esta reserva.',
    noResultsTitle: 'No se encontraron reservas',
    noResultsBody: 'Nada coincide con estos filtros. Prueba un rango de fechas más amplio.',
    export: 'Exportar',
    exportFailed: 'No se pudo generar la exportación.',
    nothingToExport: 'No hay nada que exportar con estos filtros.',
    actionFailed: 'No se pudo completar.',

    any: 'Cualquiera',
    from: 'Desde',
    to: 'Hasta',
    location: 'Localidad',
    eventType: 'Tipo de evento',
    serviceType: 'Tipo de servicio',
    host: 'Anfitrión',
    paidHow: 'Forma de pago',
    status: 'Estado',
    confirmed: 'Confirmada',
    pending: 'Pendiente',
    cancelled: 'Cancelada',
    checkedIn: 'Registrado',
    notCheckedIn: 'Sin registrar',

    user: 'Usuario',
    event: 'Evento',
    when: 'Cuándo',
    amount: 'Monto',
    view: 'Ver',
    booking: 'Reserva',
    firstTime: 'Primera vez en este evento',
    movedFrom: 'Movida desde',
    money: 'Dinero',
    eventPrice: 'Precio del evento',
    outstanding: 'Pendiente',
    refunded: 'Reembolsado',
    value: 'de valor',
    freeOfCharge: 'Sin costo',
    noCharge: 'Sin cargo',
    unaccountedFor: 'Sin justificar',
    membershipUsed: 'Membresía usada',
    benefitValue: 'Valor del beneficio',
    bookedAt: 'Reservada el',
    comments: 'Comentarios',
    additionalInformation: 'Información adicional',
    moveHistory: 'Historial de movimientos',
    movedBy: 'movida por',
    capacityIncreased: 'Capacidad aumentada',
    moveBooking: 'Mover reserva',

    payments: 'Pagos',
    noPayments: 'No hay pagos registrados.',
    recordPayment: 'Registrar pago',
    recordedBy: 'registrado por',
    voidPayment: 'Anular pago',
    voidPaymentBody: 'El pago se revierte y deja de contar para esta reserva.',
    paymentVoided: 'Pago anulado.',
    voided: 'Anulado',
    failed: 'Fallido',
    successful: 'Exitoso',

    category: {
      usedBenefit: 'Usó un beneficio',
      paidOnline: 'Pagado en línea',
      paidOffline: 'Pagado fuera de linea',
      freeEvent: 'Evento gratuito',
      comped: 'Cortesía',
      awaitingPayment: 'Esperando pago',
      refunded: 'Reembolsado',
      needsReview: 'Requiere revisión',
    },
    package: 'Paquete',
    soldAt: 'Vendido a',
    changePackage: 'Cambiar paquete',
    packageHistory: 'Historial de paquetes',
    noPackage: 'Sin paquete',
    changedBy: 'cambiado por',

    cancelBooking: 'Cancelar reserva',
    cancelBookingBody:
      '¿Cancelar la reserva de {{name}}? Se le notificará, el espacio quedará libre y los pagos en línea se reembolsarán.',
    cancelBookingFee: 'Se retiene un cargo por cancelación de {{fee}} del reembolso.',
    keepBooking: 'Mantener reserva',
    bookingCancelled: 'La reserva fue cancelada.',
  },

  manageSubscriptions: {
    title: 'Suscripciones',
    subtitle: 'Todas las personas inscritas en un plan, cómo llegaron y cuánto han pagado.',
    searchPlaceholder: 'Buscar por nombre, correo o teléfono...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar las suscripciones.',
    noResults: 'Ninguna suscripción coincide con estos filtros.',
    export: 'Exportar',
    exportFailed: 'No se pudo generar la exportación.',
    nothingToExport: 'No hay nada que exportar.',

    // Filtros
    location: 'Ubicación',
    plan: 'Plan',
    status: 'Estado',
    originLabel: 'Origen',
    paidHow: 'Pago',
    any: 'Cualquiera',
    all: 'Todas',
    active: 'Activas',
    expiringSoon: 'Por vencer',
    expired: 'Vencidas',
    cancelled: 'Cancelada',
    paidOnline: 'Pagado en línea',
    paidOffline: 'Pagado en persona',
    belowList: 'Bajo precio de lista',
    unbilled: 'Sin cobrar',
    sponsored: 'Patrocinada',

    // Columnas
    user: 'Miembro',
    period: 'Periodo',
    paid: 'Pagado',
    benefits: 'Beneficios',
    bookings: 'Reservas',
    actions: 'Acciones',

    // Celdas
    daysRemaining_one: 'Queda {{count}} día',
    daysRemaining_other: 'Quedan {{count}} días',
    nOfMUsed: '{{used}} de {{total}} usados',
    coveredByBuyer: 'Cubierta por el comprador',
    noCharge: 'Sin cargo',
    underListPrice: 'bajo el precio de lista',
    refunded: 'Reembolsado',
    boughtWithPromo: 'Comprada en promoción',
    origin: {
      selfPurchase: 'La compró',
      adminAssigned: 'Asignada',
      sponsored: 'Patrocinada',
      gift: 'Regalo',
      memberSeat: 'Cupo familiar',
    },

    // Acciones de fila
    edit: 'Editar',
    recordPayment: 'Registrar pago',
    cancelSubscription: 'Cancelar suscripción',
    cancelConfirmBody:
      'Esto termina la suscripción de {{name}} ahora. Sus beneficios restantes dejan de poder usarse y no se reembolsa nada automáticamente.',
    cancelledToast: 'La suscripción fue cancelada.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',

    // Diálogo de edición
    editSubscription: 'Editar suscripción',
    startDate: 'Inicia',
    endDate: 'Termina',
    autoRenew: 'Se renueva automáticamente',
    countForLimit: 'Cuenta para el límite del plan',
    comments: 'Notas',
    saved: 'La suscripción fue actualizada.',

    // Acciones de fila y encabezado
    view: 'Ver',
    newSubscription: 'Nueva suscripción',
    createSubscription: 'Crear suscripción',
    created: 'La suscripción fue creada.',
    planChanged: 'El plan fue cambiado.',

    // Orden
    sort: 'Ordenar por',
    sortCreatedDesc: 'Más recientes primero',
    sortCreatedAsc: 'Más antiguas primero',
    sortEndingSoonest: 'Vencen antes',
    sortEndingLatest: 'Vencen después',
    sortName: 'Nombre',
    sortPaidDesc: 'Mayor pago',

    // Panel de detalle
    subscription: 'Suscripción',
    detailFailed: 'No se pudo cargar esta suscripción.',
    summary: 'Resumen',
    payments: 'Pagos',
    family: 'Familia',
    dates: 'Fechas',
    money: 'Dinero',
    listPrice: 'Precio de lista',
    createdAt: 'Creada',
    coveredBy: 'Cubierta por',
    inactive: 'Inactiva',

    // Pestaña de pagos
    noPayments: 'No hay pagos registrados.',
    recordedBy: 'Registrado por',
    successful: 'Exitoso',
    failed: 'Fallido',
    voided: 'Anulado',
    renewal: 'Renovación',
    void: 'Anular',
    refund: 'Reembolsar',
    voidPaymentPrompt:
      'Esto elimina un pago que fue registrado a mano. La suscripción vuelve a deber ese monto.',
    refundPaymentPrompt:
      'Esto devuelve el dinero por el proveedor que lo cobró. No se puede deshacer desde aquí.',
    paymentVoided: 'El pago fue anulado.',
    paymentRefunded: 'El pago fue reembolsado.',

    // Pestaña de beneficios
    remainingBenefits: 'Beneficios restantes',
    benefitRedemptions: 'Beneficios usados',
    included: 'Incluidos',
    remaining: 'Restantes',
    unlimited: 'Ilimitado',
    noBenefits: 'No hay beneficios disponibles para usar ahora mismo.',
    noRedemptions: 'Todavía no se ha usado ningún beneficio.',

    // Pestaña de reservas
    noBookingsWithSubscription: 'No se ha reservado nada con esta suscripción.',
    checkedIn: 'Registrado',
    notCheckedIn: 'Sin registrar',
    benefitValue: 'Valor del beneficio',

    // Formulario
    who: 'Quién',
    whichPlan: 'Cuál plan',
    when: 'Cuándo',
    options: 'Opciones',
    howItWasPaid: 'Cómo se pagó',
    changePlan: 'Cambiar plan',
    selectPlan: 'Selecciona un plan',
    notActive: 'inactivo',
    noUsersFound: 'Nadie coincide con esa búsqueda.',
    remove: 'Quitar',
    overrideDates: 'Definir las fechas a mano',
    isActive: 'Activa',
    wasSponsored: 'Fue patrocinada',
    allowPromo: 'Aplicar la promoción vigente',
    paidModeReceived: 'Pago recibido',
    paidModeSponsored: 'Patrocinada — no hay nada que cobrar',
    paidModeNotYet: 'Aún sin pagar',
    amount: 'Monto',
    method: 'Método',
    reference: 'Referencia',
    adminNotes: 'Notas',
    days: 'días',
    months: 'meses',
    years: 'años',

    // El resumen en vivo, para que el formulario se lea como una sola frase.
    echoGets: 'recibe',
    echoFrom: 'desde',
    echoTo: 'hasta',
    echoRecordedAs: 'Registrado como',
    echoListPriceIs: 'El precio de lista es',
    echoLoggedAsDiscount: 'así que {{amount}} se registra como descuento.',
    echoSponsored: 'Patrocinada, así que no se cobra nada.',
    echoNotPaid: 'Todavía no se ha cobrado nada.',
    echoRecordPayment: 'Registrar {{amount}} {{method}} para {{name}}.',
  },

  bookingPaymentHistory: {
    title: 'Historial de pagos',
    subtitle: 'Todos los pagos cobrados por una reserva.',
    loadFailed: 'No se pudo cargar el historial de pagos.',
    noResults: 'Ningún pago coincide con estos filtros.',
    searchPlaceholder: 'Buscar por nombre, correo o evento...',
    showingOf: 'Mostrando {{shown}} de {{total}}',

    from: 'Desde',
    to: 'Hasta',
    eventType: 'Tipo de evento',
    serviceType: 'Tipo de servicio',
    any: 'Cualquiera',

    name: 'Nombre',
    event: 'Evento',
    timeSlot: 'Horario',
    paidOn: 'Pagado el',
    amount: 'Monto',
    cancelled: 'Cancelada',
  },

  itemMaster: {
    addRelation: 'Agregar un artículo',
    searchToBegin: 'Busca para encontrar un artículo.',
    productMetadata: 'Datos del producto',
    whatToCopy: 'Qué copiar',
    pickWhatToCopy: 'Elige al menos una cosa para copiar.',
    useThis: 'Usar este',
    metadataFailed: 'No se pudo conectar con el servicio de productos.',
    copyCatalog: 'Copiar catálogo',
    copyCatalogHint:
      'Copia todos los artículos de una tienda a otras tiendas. Los artículos que la tienda destino ya vende conservan su propia configuración.',
    sourceStore: 'Copiar de',
    destinationStores: 'Copiar a',
    copyCatalogNow: 'Copiar catálogo',
    copyCatalogPrompt_one:
      '¿Copiar todo el catálogo de {{store}} a 1 tienda?',
    copyCatalogPrompt_other:
      '¿Copiar todo el catálogo de {{store}} a {{count}} tiendas?',
    catalogCopied: 'El catálogo fue copiado.',
    title: 'Maestro de artículos',
    subtitle: 'Todos los productos del catálogo, por encima de cualquier tienda.',
    loadFailed: 'No se pudo buscar en el catálogo.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    created: 'El artículo fue creado.',
    create: 'Crear',
    createNew: 'Nuevo artículo',
    edit: 'Editar',
    add: 'Agregar',
    remove: 'Quitar',
    searchPlaceholder: 'Buscar por nombre, marca o código...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    noResults: 'Ningún artículo coincide con esta búsqueda.',
    clearFilters: 'Quitar filtros',
    tableView: 'Lista',
    gridView: 'Cuadrícula',
    tagged: 'Con términos',

    newItem: 'Nuevo artículo',
    editItem: 'Editar artículo',
    details: 'Detalles',
    archive: 'Archivar',
    archived: 'Archivado',
    archivePrompt:
      '¿Archivar este artículo? Sale del catálogo en todas partes, y las tiendas que lo venden dejan de mostrarlo.',
    restore: 'Restaurar',
    restored: 'El artículo fue restaurado.',
    primaryGtin: 'Código principal',
    createdOn: 'Creado',
    gtinHint: 'El código principal. Administra los demás en Códigos.',
    internalId: 'Id interno',
    brand: 'Marca',
    name: 'Nombre',
    description: 'Descripción',
    division: 'División',
    rank: 'Rango',
    rankHint: 'Un rango mayor aparece antes en la búsqueda.',
    searchTags: 'Términos de búsqueda',
    searchTagsHint: 'Palabras adicionales con las que encontrar este artículo.',
    packagingSensitive: 'Sensible al empaque',
    temperatureSensitive: 'Sensible a la temperatura',
    alcoholicBeverage: 'Alcohol',
    perishable: 'Perecedero',
    isEbt: 'EBT',
    idRequired: 'Requiere identificación',
    hasPluCode: 'Tiene código PLU',
    affectsInventory: 'Afecta el inventario',

    stores: 'Tiendas',
    noStores: 'Ninguna tienda vende este artículo todavía.',
    pickAStore: 'Elige una tienda',
    assignToStore: 'Asignar',
    assignToAll: 'Asignar a todas',
    assigned: 'El artículo fue asignado.',
    copyConfig: 'Copiar la configuración de un artículo a las demás tiendas',
    copyConfigHint:
      'Toma una tienda como modelo y deja las demás iguales: precio, código de barra, sección, pasillo, visibilidad, disponibilidad, impuestos, datos dietéticos y horarios de venta.',
    copyFrom: 'Tomar la configuración de',
    pickSourceStore: 'Elige la tienda a copiar...',
    copyTo: 'Aplicarla a',
    selectAll: 'Seleccionar todas',
    clearAll: 'Quitar',
    copyNow: 'Copiar configuración',
    copyConfigPrompt_one:
      '¿Dejar la otra tienda igual que {{store}}? Se sobrescribe su propia configuración para este artículo.',
    copyConfigPrompt_other:
      '¿Dejar {{count}} tiendas iguales que {{store}}? Se sobrescribe su propia configuración para este artículo.',
    configCopied: 'La configuración fue copiada.',
    forcePriceUpdate: 'Recargar precios desde la integración',
    pricesRefreshed: 'Los precios fueron recargados.',
    visible: 'Visible',
    hidden: 'Oculto',
    available: 'Disponible',
    unavailable: 'No disponible',
    aisle: 'Pasillo',
    onHand: 'en existencia',

    gtins: 'Códigos',
    newGtin: 'Agregar un código',
    noGtins: 'Todavía no hay códigos.',
    gtinAlreadyListed: 'Ese código ya está en la lista.',
    primary: 'Principal',
    makePrimary: 'Hacer principal',

    media: 'Imágenes',
    mediaFailed: 'No se pudieron cargar las imágenes.',
    pictures: 'Imágenes',
    videos: 'Videos',
    noPictures: 'Todavía no hay imágenes.',
    noVideos: 'Todavía no hay videos.',
    imageUrl: 'Pega una URL de imagen',
    videoUrl: 'Pega una URL de video',
    thumbnailUrl: 'Pega una URL de imagen de miniatura',
    addByUrl: 'Agregar por URL',
    uploadImage: 'Subir una imagen',
    uploadFailed: 'No se pudo subir la imagen.',
    mainImage: 'Se muestra primero',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    saveOrder: 'Guardar orden',
    orderSaved: 'El orden fue guardado.',

    filters: 'Filtros',
    manageFilters: 'Filtros',
    noFilters: 'No hay filtros configurados.',
    applied: 'Sí',
    notApplied: 'No',
    nItems_one: '{{count}} artículo',
    nItems_other: '{{count}} artículos',

    recommendations: 'Recomendado con',
    substitutes: 'Sustitutos',
    noRecommendations: 'Nada se recomienda con este artículo.',
    noSubstitutes: 'Nada sustituye a este artículo.',

    logs: 'Historial',
    logsFailed: 'No se pudo cargar el historial.',
    noLogs: 'Todavía no se ha cambiado nada.',
    logDetails: 'Detalles',
    logChanges: 'Qué cambió',
    logAllFields: 'Todos los campos',
    logRaw: 'Datos sin procesar',
    logNoChanges: 'Esta actualización no cambió ninguno de los valores registrados.',
    logNoPrevious:
      'No hay una entrada anterior cargada para comparar. Abre la página anterior para ver qué cambió.',
    logShowEmpty_one: 'Mostrar {{count}} campo vacío',
    logShowEmpty_other: 'Mostrar {{count}} campos vacíos',
    logHideEmpty: 'Ocultar los campos vacíos',
    logCopyData: 'Copiar los datos sin procesar',
  },

  businessItem: {
    general: 'General',
    pricing: 'Precios',
    hours: 'Horarios',
    flags: 'Opciones',

    primaryUpc: 'Código principal',
    upcs: 'Códigos',
    newUpc: 'Agregar un código',
    noUpcs: 'Esta tienda no tiene códigos para el artículo.',
    resolvePrimary: 'Determinar el principal',
    group: 'Grupo',
    groupHint: 'El departamento en que esta tienda ubica el artículo.',
    aisle: 'Pasillo',
    section: 'Sección',
    pickSection: 'Elige una sección',
    pickFromList: 'Elegir de la lista',
    typeNew: 'Escribir uno nuevo',
    minimum: 'Mínimo',
    maximum: 'Máximo',
    stepAmount: 'Incremento',
    pack: 'Paquete',
    randomWeight: 'Se vende por peso',
    weightType: 'Unidad de peso',
    unitPerPackage: 'Unidades por paquete',
    customSearchTags: 'Términos de la tienda',
    customSearchTagsHint:
      'Palabras adicionales con las que esta tienda encuentra el artículo.',
    itemType: 'Tipo de artículo',
    inStore: 'En tienda',
    externalSupplier: 'Suplidor externo',

    munTax: 'Impuesto municipal %',
    stateTax: 'Impuesto estatal %',
    basePrice: 'Precio base',
    promoPrice: 'Precio promocional',
    price: 'Precio',
    multiple: 'Múltiplo',
    cost: 'Costo',
    costOfBox: 'Costo de la caja',
    priceMethod: 'Método de precio',
    unit: 'Unidad',
    split: 'Fraccionado',
    taxRule: 'Regla de impuesto',
    taxNone: 'Ninguno',
    taxMunicipal: 'Municipal',
    taxState: 'Estatal',
    taxBoth: 'Ambos',
    startDate: 'Comienza',
    endDate: 'Termina',
    addPrice: 'Agregar un precio',
    removePrice: 'Quitar precio',
    priceNameTaken: 'Ya hay otro precio con ese nombre.',
    priceOrderTaken: 'Ya hay otro precio en ese orden.',
    editPrice: 'Editar precio',
    noPrices: 'Esta tienda no tiene precios para el artículo.',
    unnamedPrice: 'Precio sin nombre',
    inactive: 'Inactivo',
    active: 'Activo',
    ordering: 'Orden',

    limitDate: 'Disponible hasta',
    hourDelay: 'Horas de aviso',
    minuteDelay: 'Minutos de aviso',
    availableOnMondays: 'Lunes',
    availableOnTuesdays: 'Martes',
    availableOnWednesdays: 'Miércoles',
    availableOnThursdays: 'Jueves',
    availableOnFridays: 'Viernes',
    availableOnSaturdays: 'Sábados',
    availableOnSundays: 'Domingos',

    allowedInLayaway: 'Permitido en separado',
    hideInKiosk: 'Ocultar en el quiosco',
    hideInSelfCheckout: 'Ocultar en el autoservicio',
    promoted: 'Promocionado',
    madeLocally: 'Hecho localmente',
    customDepartment: 'Departamento personalizado',
    dietary: 'Dietético',
    natural: 'Todo natural',
    antioxidant: 'Antioxidante',
    glutenFree: 'Sin gluten',
    grassFed: 'De pastoreo',
    keto: 'Keto',
    lactoseFree: 'Sin lactosa',
    nonGmo: 'Sin transgénicos',
    nutFree: 'Sin nueces',
    organic: 'Orgánico',
    sugarFree: 'Sin azúcar',
    vegan: 'Vegano',
    vegetarian: 'Vegetariano',

    modifiers: 'Requisitos, acompañantes y complementos',
    modifiersGlobal:
      'Este cliente mantiene un solo conjunto para todas las tiendas, así que un cambio aquí llega a todas.',
    modifiersPerStore: 'Estos pertenecen solo a esta tienda.',
    requirements: 'Requisitos',
    sides: 'Acompañantes',
    addons: 'Complementos',
    noneAssigned: 'Todavía no hay nada asignado.',
    nothingToAssign: 'No queda nada por asignar.',
    searchModifiers: 'Buscar...',

    archivePrompt:
      '¿Que {{store}} deje de vender este artículo? Sale solo de esa tienda; las demás lo siguen vendiendo.',
    restorePrompt: '¿Que {{store}} vuelva a vender este artículo?',
    archived: 'El artículo salió de esa tienda.',
    applyToMultiple: 'Aplicar los mismos cambios a',
    link: 'Enlace',
    linkCopied: 'El enlace fue copiado.',
  },

  itemRegistration: {
    title: 'Registro de artículos',
    subtitle:
      'La biblioteca compartida desde la que se asigna el catálogo de una tienda. Describe un artículo una vez, véndelo en cualquier lugar.',
    loadFailed: 'No se pudo buscar en la biblioteca.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    createNew: 'Nuevo artículo',
    create: 'Crear',
    edit: 'Editar',
    add: 'Agregar',
    remove: 'Quitar',
    submit: 'Asignar',
    searchPlaceholder: 'Buscar por nombre, marca o código...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    itemNotFound: 'Nada en la biblioteca coincide con eso',
    itemNotFoundBody:
      'Prueba con otro código o nombre. Si el artículo realmente no está aquí, hay que registrarlo antes de que una tienda pueda venderlo.',

    assigned: 'En el catálogo',
    notAssigned: 'Sin asignar',
    assign: 'Asignar',
    assignedToast: 'El artículo fue asignado.',
    assignToCatalog: 'Asignar a un catálogo',
    cost: 'Costo',
    price: 'Precio',
    assignToMultipleStores: 'Asignar a varias tiendas',
    stores: 'Tiendas',
    selectAll: 'Seleccionar todas',
    clearAll: 'Quitar todas',

    newItem: 'Nuevo artículo',
    editItem: 'Editar artículo',
    archive: 'Archivar',
    archivePrompt:
      '¿Archivar este artículo? Sale de la biblioteca, pero los catálogos que ya lo tomaron conservan lo suyo.',
    archived: 'El artículo fue archivado.',
    primaryGtinHint: 'El código principal. Administra los demás en Códigos.',

    brand: 'Marca',
    name: 'Nombre',
    description: 'Descripción',
    searchTags: 'Términos de búsqueda',
    searchTagsHint: 'Palabras adicionales con las que encontrar este artículo.',
    division: 'División',
    group: 'Grupo',
    suggestedCategory: 'Categoría sugerida',
    pack: 'Paquete',
    taxRule: 'Regla de impuesto',
    stateTax: 'Impuesto estatal',
    municipalTax: 'Impuesto municipal',
    packagingSensitive: 'Sensible al empaque',
    temperatureSensitive: 'Sensible a la temperatura',
    alcoholicBeverage: 'Alcohol',
    perishable: 'Perecedero',
    isEbt: 'EBT',
    idRequired: 'Requiere identificación',
    hasPluCode: 'Tiene código PLU',
    hasRandomWeight: 'Se vende por peso',

    gtins: 'Códigos',
    newGtin: 'Agregar un código',
    noGtins: 'Todavía no hay códigos.',
    gtinAlreadyListed: 'Ese código ya está en la lista.',
    primary: 'Principal',
    makePrimary: 'Hacer principal',

    media: 'Media',
    mediaFailed: 'No se pudieron cargar las imágenes.',
    pictures: 'Imágenes',
    videos: 'Videos',
    noPictures: 'Todavía no hay imágenes.',
    noVideos: 'Todavía no hay videos.',
    addPicture: 'Agregar una imagen',
    addVideo: 'Agregar un video',
    addByUrl: 'Agregar por URL',
    imageUrl: 'Pega una URL de imagen',
    videoUrl: 'Pega una URL de video',
    thumbnailUrl: 'Pega una URL de imagen de miniatura',
    uploadImage: 'Subir una imagen',
    uploadFailed: 'No se pudo subir la imagen.',
    mainImage: 'Se muestra primero',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    saveOrder: 'Guardar orden',
    orderSaved: 'El orden fue guardado.',

    applyImages: 'Enviar imágenes',
    applyImagesBody:
      'Envía las imágenes de este artículo a cada catálogo que lo tomó. Agregar conserva lo que ya tiene cada tienda; reemplazar lo descarta primero.',
    addImages: 'Agregar a lo existente',
    replaceImages: 'Reemplazar',
    imagesApplied: 'Las imágenes fueron enviadas.',
  },

  itemPrices: {
    title: 'Precios de artículos',
    subtitle:
      'Lo que el POS dice que cuesta un artículo, y cómo llega al catálogo.',
    loadFailed: 'No se pudieron buscar los precios.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    reload: 'Recargar',
    close: 'Cerrar',
    createNew: 'Solicitar un artículo nuevo',
    create: 'Crear solicitud',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    noResults: 'Ningún precio coincide con esta búsqueda.',

    anyStore: 'Cualquier tienda',
    byGeneral: 'General',
    byItemNumber: 'Número de artículo',
    searchGeneral: 'Buscar por nombre, marca o descripción...',
    searchGtin: 'Buscar por GTIN...',
    searchItemNumber: 'Buscar por número de artículo...',
    pickStoreToConnect:
      'Elige una tienda para conectar precios con su catálogo.',

    updatePrice: 'Aplicar precio',
    priceApplied: 'El precio fue aplicado.',
    connect: 'Conectar',
    connected: 'Conectado',
    promo: 'Promo',

    details: 'Detalles',
    pricing: 'Precios',
    flags: 'Indicadores',
    itemNumber: 'Número de artículo',
    internalName: 'Nombre interno',
    department: 'Departamento',
    aisle: 'Pasillo',
    quantityOnHand: 'En existencia',
    taxRuleLabel: 'Regla de impuesto',
    basePrice: 'Precio base',
    baseMultiple: 'Múltiplo base',
    basePriceMethod: 'Método de precio',
    cost: 'Costo',
    pack: 'Paquete',
    stateTax: 'Impuesto estatal',
    municipalTax: 'Impuesto municipal',
    promoPrice: 'Precio promo',
    promoMultiple: 'Múltiplo promo',
    promoPriceMethod: 'Método de precio promo',
    promoStartDate: 'Promo inicia',
    promoEndDate: 'Promo termina',
    idRequired: 'Requiere identificación',
    alcoholicBeverage: 'Alcohol',
    isVisible: 'Visible',
    isAvailable: 'Disponible',
    isDeleted: 'Eliminado',
    isEbt: 'EBT',
    editedOn: 'Editado',
    createdOn: 'Creado',
    unknown: 'Desconocido',

    taxRule: {
      none: 'Sin impuesto',
      municipal: 'Solo municipal',
      state: 'Solo estatal',
      both: 'Municipal y estatal',
    },
    priceMethod: {
      unit: 'Precio por unidad',
      split: 'Paquete dividido',
    },
    weightType: {
      lb: 'LB',
      unit: 'Unidad',
    },

    foundItems: 'Posibles coincidencias',
    foundPotentialMatches:
      'Estos artículos del catálogo se parecen a este precio. Conéctalo a uno, o solicita un artículo nuevo.',
    noPotentialMatch:
      'Nada en el catálogo se parece a este precio. Solicita un artículo nuevo.',
    associate: 'Conectar a este',
    associateEcho: 'Conectando el precio de {{price}} con {{item}}.',
    connectedToast: 'El precio fue conectado.',
    newItemRequest: 'Solicitar un artículo nuevo',
    created: 'La solicitud fue creada.',

    theItem: 'El artículo',
    internalId: 'Id interno',
    brand: 'Marca',
    name: 'Nombre',
    description: 'Descripción',
    searchTags: 'Términos de búsqueda',
    searchTagsHint: 'Palabras adicionales con las que encontrar este artículo.',
    itemImage: 'Imagen',
    thumbnail: 'Miniatura',
    uploadImage: 'Subir una imagen',
    uploadFailed: 'No se pudo subir la imagen.',
    packagingSensitive: 'Sensible al empaque',
    temperatureSensitive: 'Sensible a la temperatura',
    perishable: 'Perecedero',

    whereItSits: 'Dónde va',
    inStoreId: 'Id en tienda',
    category: 'Categoría',
    selectCategory: 'Elige una categoría',
    howItIsSold: 'Cómo se vende',
    randomWeight: 'Se vende por peso',
    weightTypeLabel: 'Unidad de peso',
    minimum: 'Mínimo',
    maximum: 'Máximo',
    stepAmount: 'Incremento',
    unitPerPackage: 'Unidades por paquete',
    madeLocally: 'Hecho localmente',

    dietary: 'Declaraciones dietéticas',
    natural: 'Totalmente natural',
    lactoseFree: 'Sin lactosa',
    sugarFree: 'Sin azúcar',
    keto: 'Keto',
    organic: 'Orgánico',
    vegan: 'Vegano',
    vegetarian: 'Vegetariano',
    glutenFree: 'Sin gluten',
    grassFed: 'De pastoreo',
    antioxidant: 'Antioxidante',
    nutFree: 'Sin nueces',
    nonGmo: 'Sin transgénicos',
  },

  staff: {
    title: 'Personal',
    subtitle: 'Quién puede trabajar en esta tienda y qué puede hacer.',
    loadFailed: 'No se pudo cargar la lista de personal.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    refresh: 'Actualizar',
    noStaff: 'Todavía no hay nadie en esta tienda.',
    filterPlaceholder: 'Filtrar por nombre, correo o teléfono...',
    hideInactive: 'Ocultar inactivos',
    active: 'Activo',
    inactive: 'Inactivo',
    admin: 'Administrador',
    regular: 'Regular',
    since: 'Desde',
    yes: 'Sí',
    no: 'No',

    addStaff: 'Agregar a alguien',
    addStaffHint:
      'Busca una cuenta que ya exista en la plataforma y dale acceso a esta tienda.',
    searchPlaceholder: 'Buscar por nombre o correo...',
    searchToBegin: 'Busca para encontrar una cuenta.',
    noUsers: 'Ninguna cuenta coincide con esa búsqueda.',
    add: 'Agregar',
    userAdded: 'Ya tiene acceso a esta tienda.',

    provisionalCode: 'Código de gerente provisional',
    provisionalCodePrompt:
      '¿Crear un código de gerente de un solo uso para esta tienda? Se copia al portapapeles.',
    codeCopied: 'Código de gerente {{code}} — copiado.',
    managerCode: 'Código de gerente',
    barcodeUnavailable: 'Este código no se puede dibujar como código de barras.',
    generateNewCode: 'Generar un código nuevo',
    generateNewCodePrompt:
      '¿Generar un código de gerente nuevo para {{name}}? El actual deja de funcionar.',
    newCodeCreated: 'Se creó un código nuevo.',
    print: 'Imprimir',
    printBlocked:
      'Se bloqueó la ventana de impresión. Permite las ventanas emergentes e intenta de nuevo.',
    sendPin: 'Enviar PIN de acceso',
    pinSent: 'El PIN fue enviado.',
    pinCopied: 'PIN {{pin}} — copiado.',

    privileges: 'Permisos',
    notifications: 'Notificaciones',
    roles: 'Roles',
    noRoles: 'No tiene roles de plataforma.',
    rolesHint: 'Los roles vienen de la plataforma y no se asignan por tienda.',
    isActive: 'Activo',
    isActiveHint: 'Al apagarlo deja de poder entrar a esta tienda.',
    hiddenFromStaff: 'Oculto en la lista de personal',
    notEditableHere: 'Se muestran, pero se cambian en otro lugar',
    notEditableHint:
      'La actualización que usa esta página no tiene campo para estos, así que se muestran como están.',
    canEditOrders: 'Editar órdenes',
    canCancelOrders: 'Cancelar órdenes',
    canProvideCredit: 'Dar crédito',
    canCreateMenuItems: 'Crear productos',
    canEditMenuItems: 'Editar productos',
    canEditPrice: 'Editar precios',
    canEditStore: 'Editar la tienda',
    canEditLocation: 'Editar ubicaciones',
    canEditUsers: 'Editar personal',
    canEditPayments: 'Editar pagos',
    canViewAnalytics: 'Ver analíticas',
    canActivateUsers: 'Activar cuentas',
    forceSms: 'Forzar SMS',
    getNotifications: 'Todas las notificaciones',
    getOrderNotifications: 'Órdenes nuevas',
    getPickUpNotifications: 'Recogidos',
    getDeliveryNotifications: 'Entregas',
    getSensitiveNotifications: 'Sensibles',
    getSensitiveFinanceNotifications: 'Finanzas sensibles',
    getLaneNotifications: 'Cajas del POS',
    getLaneNotificationsHint:
      'La lista de personal no reporta este, así que empieza apagado y hay que fijarlo cada vez.',

    performance: 'Rendimiento',
    performanceFailed: 'No se pudieron cargar las cifras de rendimiento.',
    picking: 'Recogido',
    packing: 'Empaque',
    from: 'Desde',
    to: 'Hasta',
    orderCount: 'Órdenes',
    itemsPerOrder: 'Artículos por orden',
    itemsPerMinute: 'Artículos por minuto',
    allItems: 'Todos los artículos',
    averageTime: 'Tiempo promedio',
    minutes: 'min',
  },

  timesheetTypes: {
    title: 'Tipos de turno',
    subtitle: 'Las clases de turno que puede tener una entrada de horario.',
    loadFailed: 'No se pudieron cargar los tipos.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    refresh: 'Actualizar',
    noTypes: 'Todavía no hay tipos configurados.',
    newType: 'Nuevo tipo',
    editType: 'Editar tipo',
    create: 'Crear',
    nameEn: 'Nombre (inglés)',
    nameEs: 'Nombre (español)',
    default: 'Predeterminado',
    defaultHint: 'El tipo que toma un turno cuando nadie elige uno.',
    delete: 'Eliminar',
    deletePrompt:
      '¿Eliminar {{name}}? Los turnos ya registrados con ese tipo lo conservan.',
    deleted: 'El tipo fue eliminado.',
  },

  timesheets: {
    title: 'Horarios',
    subtitle: 'Quién trabajó y cuándo.',
    loadFailed: 'No se pudieron cargar los horarios.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    created: 'La entrada fue creada.',
    create: 'Crear',
    refresh: 'Actualizar',
    print: 'Imprimir',
    printBlocked:
      'Se bloqueó la ventana de impresión. Permite las ventanas emergentes e intenta de nuevo.',
    noEntries: 'Nadie trabajó en este rango.',

    from: 'Desde',
    to: 'Hasta',
    employee: 'Empleado',
    anyEmployee: 'Cualquiera',
    pickEmployee: 'Elige a alguien',
    type: 'Tipo',
    anyType: 'Cualquier tipo',
    pickType: 'Elige un tipo',
    inactive: 'inactivo',

    view_daily: 'Por día',
    view_person: 'Por persona',
    view_entry: 'Por turno',
    who: 'Quién',
    day: 'Día',
    startTime: 'Entrada',
    endTime: 'Salida',
    endTimeHint: 'Déjalo en blanco si el turno sigue abierto.',
    stillOpen: 'sigue abierto',
    hours: 'Horas',
    minutes: 'min',
    totalHours: 'Horas totales',
    totals_one: 'Totales — {{count}} fila',
    totals_other: 'Totales — {{count}} filas',
    comment: 'Comentario',
    toggleEntries: 'Ver los turnos',
    nShifts_one: '{{count}} turno',
    nShifts_other: '{{count}} turnos',

    layout_list: 'Lista',
    layout_calendar: 'Calendario',
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    thisMonth: 'Este mes',
    moreShifts: '+{{count}} más',
    pickDay: 'Elige un día para ver sus turnos.',
    noShiftsThatDay: 'Nadie trabajó este día.',

    forPayroll: 'Para nómina',
    hourlyRate: 'Tarifa por hora',
    deductionPercent: 'Deducción %',
    payrollHint:
      'El almuerzo sin paga se resta de las horas, y la deducción es un porcentaje de lo que queda. El almuerzo se escribe aquí y no se guarda.',
    lunch: 'Almuerzo (horas)',
    gross: 'Bruto',
    deduction: 'Deducción',
    net: 'Neto',

    newEntry: 'Nueva entrada',
    editEntry: 'Editar entrada',
    delete: 'Eliminar',
    deletePrompt: '¿Eliminar esta entrada? Las horas salen del total.',
    deleted: 'La entrada fue eliminada.',

    clockedIn: 'En turno',
    nobodyClockedIn: 'Nadie está en turno ahora mismo.',
    onTheClock: 'Trabajando',
    since: 'Desde',
  },

  posLanes: {
    title: 'Cajas del POS',
    subtitle: 'Las cajas registradoras de esta tienda.',
    loadFailed: 'No se pudieron cargar las cajas.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    created: 'La caja fue creada.',
    create: 'Crear',
    refresh: 'Actualizar',
    noLanes: 'Esta tienda todavía no tiene cajas.',
    noneMatch: 'Ninguna caja coincide con esta búsqueda.',
    searchPlaceholder: 'Buscar por etiqueta, equipo o terminal...',

    allLanes: 'Cajas',
    blocked: 'Bloqueada',
    blockedHint: 'Una caja bloqueada sigue configurada pero no puede vender.',
    outdated: 'Versión atrasada',
    latestVersion: 'La última es {{version}}',
    untagged: 'Caja sin etiqueta',
    neverReported: 'Nunca reportó',
    noDevice: 'Todavía no reporta equipo',
    lastTransaction: 'Última venta',
    version: 'Versión',
    device: 'Equipo',
    deviceName: 'Nombre del equipo',
    terminalId: 'Id de terminal',
    createdOn: 'Creada',

    sortTag: 'Ordenar por etiqueta',
    sortVersion: 'Ordenar por versión',
    sortLastTransaction: 'Ordenar por última venta',

    newLane: 'Nueva caja',
    editLane: 'Editar caja',
    tag: 'Etiqueta',
    tagPlaceholder: 'Caja 1',
    tagHint: 'Como el personal llama a esta caja.',
    remove: 'Quitar',
    removePrompt:
      '¿Quitar {{tag}}? Sus ventas anteriores quedan registradas, pero la caja deja de funcionar.',
    removed: 'La caja fue quitada.',
  },

  deliveries: {
    title: 'Entregas',
    searchTab: 'Buscar',
    sortingTab: 'Orden de ruta',

    from: 'Desde',
    to: 'Hasta',
    provider: 'Proveedor',
    anyProvider: 'Cualquier proveedor',
    zone: 'Zona',
    anyZone: 'Cualquier zona',
    statusFilter: 'Estado',
    status: {
      all: 'Todas',
      pending: 'Abiertas',
      assigned: 'Chofer asignado',
      inTransit: 'En tránsito',
      delivered: 'Entregadas',
      cancelled: 'Canceladas',
    },

    resultCount_one: '{{count}} entrega',
    resultCount_other: '{{count}} entregas',
    delivered: 'Entregada',
    unassigned: 'Sin chofer',
    bagCount_one: '{{count}} bolsa',
    bagCount_other: '{{count}} bolsas',

    export: 'Exportar',
    exporting: 'Exportando…',
    exportFailed: 'No se pudo crear la exportación.',
    loadFailed: 'No se pudieron cargar las entregas.',
    noResultsTitle: 'Ninguna entrega coincide',
    noResultsBody:
      'Prueba con otro estado, un rango de fechas más amplio u otra zona.',

    noZones: 'Esta tienda no tiene zonas de entrega activas.',
    noStops: 'No hay entregas abiertas en esta zona.',
    moveStop: 'Mover {{name}}',
    unsavedRoute: 'Cambios sin guardar',
    saveRoute: 'Guardar orden',
    routeSaved: 'Orden de ruta guardado.',
    routeFailed: 'No se pudo guardar el orden de ruta.',
  },

  cashDrawer: {
    title: 'Informe de caja',
    subtitle: 'Cuánto recibió cada caja y si cuadró.',
    loadFailed: 'No se pudo cargar el informe.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    refresh: 'Actualizar',
    noCheckIns: 'No se abrió ninguna caja en este rango.',

    from: 'Desde',
    to: 'Hasta',
    state: 'Estado',
    anyState: 'Cualquier estado',
    openOnly: 'Todavía abiertas',
    closedOnly: 'Cerradas',
    underOnly: 'Faltante',
    overOnly: 'Sobrante',
    lane: 'Caja',
    anyLane: 'Cualquier caja',
    employee: 'Empleado',
    anyEmployee: 'Cualquiera',

    open: 'Abierta',
    closed: 'Cerrada',
    balanced: 'Cuadra',
    over: 'Sobran',
    under: 'Faltan',
    unknownUser: 'Empleado desconocido',
    unknownLane: 'Caja desconocida',

    sales: 'Ventas',
    net: 'Neto',
    drops: 'Depósitos',
    balance: 'Balance',
    cashIn: 'Entrada',
    cashOut: 'Salida',
    openingAmount: 'Contado en la apertura',
    closingAmount: 'Contado al cierre',
    opening: 'Apertura',
    counted: 'Contado',
    expected: 'Debería tener',
    notCounted: 'Todavía sin contar',
    startTime: 'Apertura',
    endTime: 'Cierre',

    theCount: 'El conteo',
    movements: 'Movimientos',
    comments: 'Comentarios',
    noComments: 'Nadie ha dejado una nota en esta caja.',
    newComment: 'Deja una nota...',
    noMovements: 'No pasó efectivo por esta caja.',
    addMovement: 'Agregar un movimiento',
    editMovement: 'Editar movimiento',
    isDropIn: 'Depósito a la bóveda',
    isDropInHint: 'Un depósito saca efectivo de la caja sin una venta.',
    drop: 'Depósito',
    delete: 'Eliminar',
    deletePrompt:
      '¿Eliminar este movimiento? El balance se recalcula sin él.',
    deleted: 'El movimiento fue eliminado.',

    recalculate: 'Volver a calcular el cierre',
    recalculateHint:
      'Reconstruye lo que la caja debería tener a partir de sus movimientos. Primero previsualiza: informa qué cambiaría sin cambiarlo.',
    preview: 'Previsualizar',
    previewResult: '{{changed}} de {{evaluated}} cambiarían.',
    applyRecalculation: 'Aplicar',
    recalculated: 'Las cifras fueron recalculadas.',
  },

  businessInfo: {
    title: 'Información del negocio',
    subtitle: 'Los datos de la tienda.',
    loadFailed: 'No se pudieron cargar los datos de la tienda.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',

    general: 'General',
    pickup: 'Recogido',
    place: 'Ubicación',

    theBusiness: 'El negocio',
    theLocation: 'Dirección y contacto',
    theWindow: 'La ventana de recogido',
    theFee: 'El cargo por recogido',
    theMap: 'Dónde queda',

    businessName: 'Nombre del negocio',
    backgroundImage: 'Imagen de fondo',
    imageUrl: 'Pega una URL de imagen',
    upload: 'Subir',
    uploadFailed: 'No se pudo subir la imagen.',
    isAlpha: 'Tienda alfa',
    isBeta: 'Tienda beta',
    visibleInBeta: 'Visible para usuarios beta',

    locationName: 'Nombre de la ubicación',
    storePhone: 'Teléfono de la tienda',
    address1: 'Dirección',
    address2: 'Dirección línea 2',
    email: 'Correo de órdenes',
    smsAudienceId: 'Id de audiencia SMS',
    smsAudienceIdHint: 'La lista desde la que esta tienda envía textos.',
    allowDelivery: 'Entrega',
    allowPickup: 'Recogido',
    posIntegration: 'Integración con POS externo',

    pickupStartHour: 'Primera hora de recogido',
    pickupEndHour: 'Última hora de recogido',
    pickupBuffer: 'Margen',
    pickupBufferHint:
      'Minutos entre una orden y el primer turno que puede tomar.',
    slotRange: 'Duración del turno',
    slotRangeHint: 'Cuántos minutos cubre un turno de recogido.',
    pickupLimit: 'Órdenes por hora',
    pickupLimitHint: 'Cuántos recogidos acepta la tienda en una hora.',
    futureDays: 'Días de anticipación',
    futureDaysHint: 'Con cuánta anticipación se puede reservar un recogido.',
    preventSameDay: 'Sin turnos el mismo día',
    preventSameDayHint: 'Los clientes no pueden elegir un turno para hoy.',

    storeFee: 'Cargo por recogido de la tienda',
    storeFeeType: 'Tipo de cargo',
    feeNone: 'Ninguno',
    feeFixed: 'Fijo',
    feePercentage: 'Porcentaje',
    globalFee: 'Cargo de la plataforma',
    globalFeeHint: 'Se fija por encima de esta tienda y no se cambia aquí.',

    city: 'Ciudad',
    cityHint: 'La tienda lo reporta, pero la actualización no tiene ese campo.',
    directions: 'Indicaciones',
    lat: 'Latitud',
    lon: 'Longitud',
    noPin: 'Esta ubicación todavía no tiene coordenadas.',
    noMapKey: 'No hay una llave de Google Maps configurada, así que el mapa no está disponible.',
    mapFailed:
      'No se pudo cargar el mapa. Verifica que la llave permita la API de JavaScript de Maps y este sitio.',
    mapHint: 'Arrastra el marcador, o haz clic en el mapa, para moverlo.',
    mapNoPinHint: 'Haz clic en el mapa para ubicar esta tienda.',
    openInMaps: 'Abrir en Google Maps',
  },

  storeOptions: {
    title: 'Opciones de la tienda',
    subtitle: 'Cómo se comporta esta tienda.',
    loadFailed: 'No se pudieron cargar las opciones de la tienda.',
    noLocation: 'Esta tienda aún no tiene ubicación. Agrega su dirección en General primero.',
    actionFailed: 'Parte de eso no se pudo guardar. Revisa e intenta de nuevo.',
    saved: 'Guardado.',

    storefront: 'Tienda en línea',
    notifications: 'Notificaciones',
    orders: 'Órdenes',
    products: 'Productos',
    layaway: 'Separado',
    giftCards: 'Tarjetas de regalo',
    tax: 'Credenciales de IVU',

    departments: 'Departamentos que se muestran',
    sponsoredDepartment: 'Patrocinado',
    offersDepartment: 'Ofertas',
    recentlyAddedDepartment: 'Agregados recientemente',
    healthyDepartment: 'Saludable',
    madeLocallyDepartment: 'Hecho localmente',
    searchFilters: 'Filtros de búsqueda que se muestran',
    defaultFilters: 'Filtros predeterminados',
    healthyFilters: 'Filtros saludables',
    brandFilters: 'Filtros de marca',
    customFilters: 'Filtros personalizados',

    paypal: 'PayPal',
    paypalHint:
      'La aplicación de PayPal de esta tienda, que usa para sus pagos.',
    paypalClientId: 'Client id de PayPal',
    paypalClientSecret: 'Client secret de PayPal',
    reveal: 'Mostrar',
    hide: 'Ocultar',
    whoIsTold: 'De qué se le avisa a la tienda',
    notifyStore: 'Enviar correo a la tienda',
    notifyStoreHint:
      'Los correos de órdenes van a la dirección en Información del negocio.',
    delayInOrders: 'Órdenes atrasadas',
    pickingFinished: 'Recogido terminado',
    packingFinished: 'Empaque terminado',

    takingOrders: 'Recibir órdenes',
    codePrefix: 'Prefijo del código de confirmación',
    codePrefixHint: 'Va delante de cada número de orden de esta tienda.',
    minimumPickup: 'Orden mínima de recogido',
    minimumDelivery: 'Orden mínima de entrega',
    additionalItemLimit: 'Límite de artículos adicionales',
    additionalItemLimitHint:
      'Cuánto puede agregar un cliente después de ordenar.',
    allowAdditionalOrders: 'Permitir agregar artículos después de ordenar',
    allowOrderModification: 'Permitir cambiar las órdenes',
    authAtOrderEntry: 'Autorizar la tarjeta al colocar la orden',
    authAtOrderEntryHint:
      'De lo contrario se cobra cuando la orden se empaca.',
    managedBins: 'Casilleros administrados por la tienda principal',
    cancellation: 'Cancelación',
    cancellationFee: 'Cargo por cancelación',
    feeType: 'Tipo de cargo',
    feeNone: 'Ninguno',
    feeFixed: 'Fijo',
    feePercentage: 'Porcentaje',
    pickingAndPacking: 'Recogido y empaque',
    markPickedAsPacked: 'Marcar lo recogido como empacado',
    markNotPackedAsOut: 'Marcar lo no empacado como agotado',
    payouts: 'Pagos',
    stripeDelay: 'Retraso de transferencia Stripe',
    paypalDelay: 'Retraso de transferencia PayPal',
    transferDelayHint: 'Días que se retiene el dinero antes de transferirlo.',

    outOfStock: 'Agotados',
    oosTrigger: 'Agotar artículos automáticamente',
    oosTriggerHint: 'Cuando suficientes compradores reportan que falta uno.',
    oosLimit: 'Reportes necesarios',
    oosLimitHint: 'Cuántos reportes hacen falta para retirar un artículo.',
    whatShoppersSee: 'Lo que ven los compradores',
    subsByDefault: 'Sustituciones permitidas por defecto',
    subsByDefaultHint: 'El comprador aún puede apagarlas por artículo.',
    hideNoPictures: 'Ocultar productos sin imagen',

    hasLayaway: 'Separado disponible',
    hasLayawayHint: 'Los clientes pueden apartar artículos y pagar a plazos.',
    layawayExpirationDays: 'Días antes de vencer',
    layawayExpirationDaysHint: 'Cuánto puede durar un separado sin pagarse.',
    maxLayaways: 'Separados por cliente',
    maxLayawaysHint: 'Cuántos puede tener abiertos un cliente a la vez.',
    layawayAmounts: 'Lo que cuesta',
    layawayServiceFee: 'Cargo por servicio',
    layawayInitialPayment: 'Pago para abrirlo',
    layawayPaymentMinimum: 'Pago mínimo',
    layawayPenalty: 'Penalidad por cancelación',

    activationFee: 'Cargo por activación de tarjeta de regalo',
    activationFeeHint: 'Se cobra una vez al activar una tarjeta de regalo.',

    taxHint:
      'Quién emite los números de control de IVU de esta tienda y las credenciales con que firma.',
    taxProvider: 'Proveedor de IVU',
    taxProvider_0: 'Ninguno',
    taxProvider_1: 'Provisto por el POS',
    taxProvider_3: 'Softek',
    merchantId: 'Id de comerciante',
    terminalId: 'Id de terminal',
    terminalPassword: 'Contraseña del terminal',
  },

  paymentConfig: {
    title: 'Métodos de pago',
    subtitle: 'Las formas en que se le puede pagar a esta tienda.',
    loadFailed: 'No se pudieron cargar los métodos de pago.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    create: 'Crear',
    noOptions: 'Esta tienda todavía no puede recibir ningún pago.',
    unnamed: 'Método sin nombre',
    active: 'Activo',
    inactive: 'Inactivo',
    from: 'desde',
    nowhere: 'No se acepta en ningún lado',

    newOption: 'Nuevo método',
    createFrom: 'Crear a partir de un método existente',
    createFromPlaceholder: 'Partir de...',
    createFromHint:
      'Copia todo menos el nombre, que lleva un sufijo, y Activo, que empieza apagado para que la copia no entre en uso sin revisarse.',
    copyName: '{{name}} (copia)',
    editOption: 'Editar método',
    createDefaults: 'Crear predeterminados',
    createDefaultsHint:
      'Crea un conjunto inicial de métodos de pago. Agrega lo que marques y deja intacto lo ya configurado.',
    defaultsCreated: 'Los métodos de pago fueron creados.',
    delete: 'Eliminar',
    deletePrompt: '¿Eliminar {{name}}? Los clientes ya no podrán pagar así.',
    deleted: 'El método fue eliminado.',

    name: 'Nombre',
    image: 'Imagen',
    imageUrl: 'Pega una URL de imagen',
    uploadFailed: 'No se pudo subir la imagen.',
    paymentType: 'Tipo de pago',
    provider: 'Procesado por',
    selector: 'Tipo de tarjeta',
    chargeType: 'Se cobra',
    minCharge: 'Cargo mínimo',
    minChargeHint: 'Por debajo de esto, el método no se ofrece.',

    whereAccepted: 'Dónde se acepta',
    allowedInRegularService: 'Recogido',
    allowedInDeliveryService: 'Entrega',
    allowedInDeliService: 'Delicatessen',
    allowedInPosService: 'POS',
    allowedInSelfCheckoutService: 'Autoservicio',

    rules: 'Reglas',
    isActive: 'Activo',
    isActiveHint:
      'Un método inactivo nunca se ofrece, aunque esté permitido en algún lado.',
    offlineOnly: 'Solo fuera de línea',
    taxExcluded: 'Sin impuesto',
    allowNonFood: 'Permitir productos que no son alimentos',
    allowAlcohol: 'Permitir alcohol',
    allowTransfers: 'Permitir transferencias',
    allowServiceFee: 'Puede pagar el cargo por servicio',
    ignoreOrderCap: 'Ignorar el tope de la orden',
    freeDelivery: 'La entrega es gratis con este método',
    needsBillingAgreement: 'Requiere acuerdo de facturación',
    openCashDrawer: 'Abrir la gaveta de efectivo',
    openCashDrawerHint: 'En la caja, cuando se usa este método.',

    credentials: 'Credenciales',
    publicToken: 'Token público',
    privateToken: 'Token privado',
    transferToken: 'Token de transferencia',

    instructions: 'Instrucciones al cliente',
    instructionsEn: 'Inglés',
    instructionsEs: 'Español',

    group_webfront: 'Tienda en línea',
    group_pos: 'Terminal del POS',
    group_selfCheckout: 'Terminal de autoservicio',
    webfrontCash: 'Efectivo',
    webfrontPayPal: 'PayPal',
    webfrontPayPalBillingAgreement: 'Acuerdo de facturación PayPal',
    webfrontAth: 'ATH Móvil',
    webfrontEbt: 'EBT',
    webfrontTripleS: 'Triple-S',
    webfrontHumana: 'Humana',
    webfrontMCS: 'MCS',
    webfrontMMM: 'MMM',
    posTerminalCash: 'Efectivo',
    posTerminalCashOffline: 'Efectivo (fuera de línea)',
    posTerminalCreditDebit: 'Crédito y débito',
    posTerminalEbt: 'EBT',
    posTerminalEbtCash: 'EBT Efectivo',
    posTerminalFondo: 'Fondo',
    posTerminalUnica: 'Única',
    selfCheckoutTerminalCreditDebit: 'Crédito y débito',
    selfCheckoutTerminalEbt: 'EBT',

    pt_0: 'Stripe',
    pt_1: 'ATH Móvil',
    pt_2: 'Efectivo',
    pt_3: 'Otro',
    pt_4: 'EBT',
    pt_5: 'PayPal',
    pt_6: 'Cheque',
    pt_7: 'ATH',
    pt_8: 'Tarjeta Vanilla',
    pt_9: 'Tarjeta de regalo',
    pt_10: 'MCS',
    pt_11: 'MMM',
    pt_12: 'Accepta',
    pt_13: 'Net 30',
    pt_14: 'Net 60',
    pt_15: 'Sin seleccionar',
    pt_16: 'Tarjeta de crédito',
    pt_17: 'Tarjeta de débito',
    pt_18: 'SSS',
    pt_19: 'Humana',
    pt_20: 'BrainTree',
    pt_21: 'En tienda',
    pt_22: 'Prepagada',
    pt_23: 'EBT Efectivo',
    pt_24: 'Única',
    pt_25: 'Fondo',
    charge_0: 'Por adelantado',
    charge_1: 'Al despachar',
    charge_2: 'Orden de compra',
    charge_3: 'App a app',
    charge_4: 'Acuerdo de facturación',
    charge_5: 'Código QR',
    charge_6: 'Terminal',
  },

  searchOptimization: {
    title: 'Optimización de búsqueda',
    subtitle: 'Los términos con los que un cliente puede encontrar cada artículo.',
    loadFailed: 'No se pudo buscar en el catálogo.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    searchPlaceholder: 'Buscar por nombre, marca, código...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    noResults: 'Ningún artículo coincide con esta búsqueda.',
    clearFilters: 'Quitar filtros',
    tableView: 'Lista',
    gridView: 'Cuadrícula',
    searchTags: 'Términos de búsqueda',
    tagged: 'Con términos',
    edit: 'Editar',
    editItem: 'Editar artículo',
    customSearchTag: 'Términos de búsqueda personalizados',
    customSearchTagNote:
      'Palabras adicionales con las que se debe encontrar este artículo: errores de escritura de la marca, nombres locales, sinónimos. Sepáralas con espacios.',

    sortDefault: 'Default',
    sortAZ: 'A–Z',
    sortZA: 'Z–A',
    sortRankDesc: 'Rango, mayor',
    sortRankAsc: 'Rango, menor',
    sortEditedAt: 'Editados recientemente',
    sortTopSellingDesc: 'Más vendidos',
    sortTopSellingAsc: 'Menos vendidos',

    filter: {
      NoPictures: 'Sin foto',
      HasPicture: 'Con foto',
      HasVideo: 'Con video',
      PackagingSensitive: 'Sensible al empaque',
      TemperatureSensitive: 'Sensible a la temperatura',
      AlcoholicBeverage: 'Alcohol',
      IdRequired: 'Requiere identificación',
      Ebt: 'EBT',
      NotEbt: 'Sin EBT',
      Perishable: 'Perecedero',
    },
  },

  modifiers: {
    requirementsTitle: 'Requisitos',
    requirementsSubtitle:
      'Opciones con las que un artículo debe pedirse: cómo se cocina, qué tamaño.',
    sidesTitle: 'Acompañantes',
    sidesSubtitle: 'Artículos del catálogo que se pueden pedir junto a un plato.',
    addonsTitle: 'Extras',
    addonsSubtitle:
      'Adiciones que se pueden agregar a un artículo, cada una con su precio.',

    loadFailed: 'No se pudo cargar la lista.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    deleted: 'Eliminado.',
    reload: 'Recargar',
    createNew: 'Nuevo',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    add: 'Agregar',
    searchPlaceholder: 'Buscar por nombre...',
    noGroups: 'Todavía no hay nada aquí.',

    newGroup: 'Nuevo grupo',
    editGroup: 'Editar grupo',
    deleteGroupPrompt:
      '¿Eliminar este grupo y todas sus opciones? Los artículos que lo usan pierden la opción.',
    internalName: 'Nombre interno',
    internalNameHint: 'Por lo que busca el personal. Los clientes nunca lo ven.',
    name: 'Nombre',
    minimum: 'Mínimo',
    maximum: 'Máximo',
    pickRange: 'Elegir {{min}}–{{max}}',
    amountCountsForMinMax: 'Las cantidades cuentan para el mínimo y el máximo',
    amountCountsForMinMaxHint:
      'Con esto activo, una opción pedida tres veces cuenta como tres elecciones, no una.',

    options: 'Opciones',
    nOptions_one: '{{count}} opción',
    nOptions_other: '{{count}} opciones',
    noOptions: 'Todavía no hay opciones.',
    addOption: 'Agregar opción',
    newOption: 'Nueva opción',
    editOption: 'Editar opción',
    removeOption: 'Quitar opción',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    unavailable: 'No disponible',

    description: 'Descripción',
    price: 'Precio',
    brand: 'Marca',
    available: 'Disponible',
    availableHint: 'Apagado la oculta a los clientes sin eliminarla.',
    allowVariableAmount: 'Se puede pedir más de una vez',
    allowVariableAmountHint:
      'Permite que el cliente elija una cantidad de esta opción.',
    alcoholicBeverage: 'Bebida alcohólica',
    image: 'Imagen',
    uploadImage: 'Subir una imagen',
    clearImage: 'Quitar imagen',
    uploadFailed: 'No se pudo subir la imagen.',
    searchItem: 'Buscar el artículo',
    noItemsFound: 'Ningún artículo coincide con esa búsqueda.',
  },

  catalog: {
    loadFailed: 'No se pudo cargar la lista.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    deleted: 'Eliminado.',
    reload: 'Recargar',
    createNew: 'Nuevo',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    logs: 'Historial',
    logsFailed: 'No se pudo cargar el historial.',
    noLogs: 'Todavía no se ha cambiado nada.',
    showingOf: 'Mostrando {{shown}} de {{total}}',

    name: 'Nombre',
    ordering: 'Orden',
    orderingHint: 'Los números menores aparecen primero en el menú de la tienda.',
    color: 'Fondo',
    borderColor: 'Borde',
    image: 'Imagen',
    imageRequirements:
      'Un PNG cuadrado con fondo transparente funciona mejor.',
    uploadImage: 'Subir una imagen',
    imageUrl: 'O pega una URL de imagen',
    uploadFailed: 'No se pudo subir la imagen.',

    // Departamentos
    departmentsTitle: 'Departamentos',
    departmentsSubtitle: 'El primer nivel del menú de la tienda.',
    noDepartments: 'Todavía no hay departamentos.',
    newDepartment: 'Nuevo departamento',
    editDepartment: 'Editar departamento',
    deleteDepartmentPrompt:
      '¿Eliminar {{name}}? Las categorías que dependen de él se quedan sin departamento y dejan de aparecer en el menú hasta reasignarlas.',
    viewBreakdown: 'Menú completo',
    noDepartment: 'Sin departamento',

    // Categorías
    categoriesTitle: 'Categorías',
    categoriesSubtitle:
      'El segundo nivel del menú, cada una dentro de un departamento.',
    noCategories: 'Todavía no hay categorías.',
    newCategory: 'Nueva categoría',
    editCategory: 'Editar categoría',
    deleteCategoryPrompt:
      '¿Eliminar {{name}}? Los artículos siguen en el catálogo pero dejan de aparecer bajo esta categoría.',
    searchCategories: 'Buscar por nombre o pasillo...',
    department: 'Departamento',
    allDepartments: 'Todos los departamentos',
    selectDepartment: 'Elige un departamento',
    aisle: 'Pasillo',
    isAlcohol: 'Alcohol',
    isAlcoholHint: 'La verificación de edad y las reglas de entrega dependen de esto.',
    alcohol: 'Alcohol',
  },

  bookingSetup: {
    loadFailed: 'No se pudo cargar la lista.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    saved: 'Guardado.',
    deleted: 'Eliminado.',
    reload: 'Recargar',
    createNew: 'Nuevo',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    remove: 'Quitar',
    name: 'Nombre',
    description: 'Descripción',

    // Ubicaciones
    locationsTitle: 'Ubicaciones',
    locationsSubtitle:
      'Dónde ocurren las reservas, y el código QR con el que la gente se registra.',
    noLocations: 'Todavía no hay ubicaciones.',
    newLocation: 'Nueva ubicación',
    editLocation: 'Editar ubicación',
    deleteLocationPrompt:
      '¿Eliminar esta ubicación? Los eventos ya vinculados conservan su historial, pero ya no se podrá elegir.',
    internalName: 'Nombre interno',
    internalNameHint: 'Lo que escribe el personal. Los clientes nunca lo ven.',
    image: 'Imagen',
    uploadImage: 'Subir una imagen',
    imageUrl: 'O pega una URL de imagen',
    uploadFailed: 'No se pudo subir la imagen.',
    qrCode: 'Código QR',

    // Anfitriones
    hostsTitle: 'Anfitriones',
    hostsSubtitle: 'Las personas que dan los horarios.',
    noHosts: 'Todavía no hay anfitriones.',
    newHost: 'Nuevo anfitrión',
    editHost: 'Editar anfitrión',
    deleteHostPrompt:
      '¿Eliminar este anfitrión? Los horarios que ya dio conservan su registro, pero ya no se le podrá asignar.',
    user: 'Quién',
    userNotEditable:
      'La cuenta detrás de un anfitrión no se puede cambiar. Elimínalo y agrégalo de nuevo.',
    searchUserPlaceholder: 'Buscar por nombre o correo...',
    noUsersFound: 'Nadie coincide con esa búsqueda.',

    // Tipos de servicio
    serviceTypesTitle: 'Tipos de servicio',
    serviceTypesSubtitle: 'Qué clase de cosa es un evento.',
    noServiceTypes: 'Todavía no hay tipos de servicio.',
    newServiceType: 'Nuevo tipo de servicio',
    editServiceType: 'Editar tipo de servicio',
    deleteServiceTypePrompt:
      '¿Eliminar este tipo de servicio? Los eventos que ya lo usan lo conservan, pero ya no se podrá elegir.',
  },

  heatmaps: {
    salesTitle: 'Mapa de calor de ventas',
    salesSubtitle: 'Cuándo ocurren las ventas, por día y hora.',
    bookingsTitle: 'Mapa de calor de reservas',
    bookingsSubtitle: 'Cuándo ocurren las reservas, por día y hora.',
    loadFailed: 'No se pudo cargar el mapa de calor.',
    noData: 'No ocurrió nada en este rango.',
    reload: 'Recargar',

    from: 'Desde',
    to: 'Hasta',
    eventType: 'Tipo de evento',
    any: 'Cualquiera',

    selectACell: 'Elige una hora para ver qué había.',
    nothingInHour: 'No había nada programado en esta hora.',
  },

  timeslotHosts: {
    title: 'Anfitriones de horarios',
    subtitle: 'Quién dio qué, durante un periodo.',
    loadFailed: 'No se pudo cargar el reporte.',
    noResults: 'No se dio nada en este rango.',
    searchPlaceholder: 'Buscar por anfitrión o evento...',

    from: 'Desde',
    to: 'Hasta',
    groupBy: 'Agrupar por',
    byHost: 'Anfitrión',
    byEvent: 'Evento',

    nHosts_one: '{{count}} anfitrión',
    nHosts_other: '{{count}} anfitriones',
    nEvents_one: '{{count}} evento',
    nEvents_other: '{{count}} eventos',
    events: 'eventos',
    timeSlots: 'horarios',
    booked: 'reservados',
  },

  subscriptionPaymentHistory: {
    title: 'Historial de pagos',
    subtitle: 'Todos los pagos cobrados por una membresía.',
    loadFailed: 'No se pudo cargar el historial de pagos.',
    noResults: 'Ningún pago coincide con estos filtros.',
    searchPlaceholder: 'Buscar por nombre, correo o membresía...',
    showingOf: 'Mostrando {{shown}} de {{total}}',

    from: 'Desde',
    to: 'Hasta',
    location: 'Ubicación',
    status: 'Estado',
    any: 'Cualquiera',
    pending: 'Pendiente',
    successful: 'Exitoso',
    failed: 'Fallido',

    name: 'Nombre',
    membership: 'Membresía',
    period: 'Periodo pagado',
    paidOn: 'Pagado el',
    amount: 'Monto',
  },

  subscriptionSales: {
    title: 'Ventas',
    unredeemedGiftsTooltip:
      'Regalos que se pagaron y nunca se asignaron a nadie. Ingreso ya cobrado, contra una membresía que todavía nadie usa.',
    notRedeemed: 'Sin canjear',
    boughtBy: 'Comprado por',
    openInGifts: 'Abrir en Regalos de membresía',
    membership: 'Membresía',
    memberships: 'Membresías',
    benefits: 'Beneficios',
    bookings: 'Reservas',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    noRows: 'No hay nada detrás de esta cifra.',
    openInSubscriptions: 'Abrir en Suscripciones',
    subtitle:
      'Lo que hizo el negocio de membresías, y cuáles planes lo hicieron.',
    loadFailed: 'No se pudo cargar el reporte.',
    searchPlaceholder: 'Buscar por nombre, correo o teléfono...',
    thisMonthNotice:
      'Contado para el mes actual. Las cifras de activas y por vencer son al día de hoy.',

    // Filtros
    location: 'Ubicación',
    status: 'Estado',
    originLabel: 'Origen',
    paidHow: 'Pago',
    any: 'Cualquiera',
    all: 'Todas',
    active: 'Activas',
    expiringSoon: 'Por vencer',
    expired: 'Vencidas',
    cancelled: 'Cancelada',
    belowList: 'Bajo precio de lista',

    // Pestañas
    overview: 'Resumen',

    // Quién está suscrito
    whosSubscribed: 'Quién está suscrito',
    activeNow: 'Activas ahora',
    ofNEver: '{{count}} en total histórico',
    newMemberships: 'Nuevas',
    thisMonth: 'este mes',
    expiringIn30Days: 'Vencen en 30 días',
    notCountedAsSales: 'no cuentan como ventas',
    autoRenewOn: 'Con renovación automática',

    // Cómo la obtuvieron
    howTheyGotIt: 'Cómo la obtuvieron',
    scopeActive: 'suscripciones activas',
    paidOnline: 'Pagado en línea',
    paidOffline: 'Pagado en persona',
    sponsored: 'Patrocinada',

    // Dinero
    money: 'Dinero',
    collected: 'Cobrado',
    online: 'en línea',
    offline: 'en persona',
    refunded: 'Reembolsado',
    net: 'Neto',
    unbilled: 'Sin cobrar',
    unbilledTooltip:
      'Precio de lista de suscripciones sin pago registrado y sin patrocinio. No es ingreso: dinero que nunca se facturó.',
    nWithNoPayment_one: '{{count}} sin pago',
    nWithNoPayment_other: '{{count}} sin pago',
    discountsGiven: 'Descuentos dados',
    discountsGivenTooltip:
      'Cuánto menos del precio de lista se cobró, entre las suscripciones que sí se pagaron. Informativo: un descuento, no un monto adeudado.',
    unredeemedGifts: 'Regalos sin canjear',

    // Pestaña de planes
    noPlans: 'No se vendieron planes en este periodo.',
    total: 'Total',
    sold: 'Vendidas',
    activeColumn: 'Activas',
    unbilledValue: 'Valor sin cobrar',
    soldOfLimit: '{{sold}} de {{limit}} vendidas',
  },

  sales: {
    title: 'Ventas',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    noRows: 'No hay nada detrás de esta cifra.',
    openInBookings: 'Abrir en Reservas',
    subtitle:
      'Lo que hizo el negocio de reservas en un periodo, y en cuáles eventos lo hizo.',
    loadFailed: 'No se pudo cargar el reporte.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    export: 'Exportar',
    exportFailed: 'No se pudo generar la exportación.',
    nothingToExport: 'No hay nada que exportar.',
    searchPlaceholder: 'Buscar por nombre, correo o evento...',

    // Filtros
    from: 'Desde',
    to: 'Hasta',
    location: 'Ubicación',
    eventType: 'Tipo de evento',
    serviceType: 'Tipo de servicio',
    host: 'Anfitrión',
    any: 'Cualquiera',

    // Pestañas
    overview: 'Resumen',
    events: 'Eventos',

    // Asistencia
    attendanceGroup: 'Asistencia',
    bookings: 'Reservas',
    confirmed: 'Confirmadas',
    pending: 'Pendientes',
    cancelled: 'Canceladas',
    checkedIn: 'Registrados',
    attendance: 'de asistencia',
    noShow: 'No asistieron',
    noShowTooltip:
      'Se cuenta sobre reservas cuyo horario ya comenzó, así que registrados más ausentes es el pasado, no todo el rango.',
    fillRate: 'Ocupación',
    nOfMSlots: '{{bookings}} de {{slots}} espacios',

    // Cómo se pagó
    howItWasPaidGroup: 'Cómo se pagó',
    paidOnline: 'Pagado en línea',
    paidOffline: 'Pagado en persona',
    usedABenefit: 'Usaron un beneficio',
    membershipPerks: 'beneficios de membresía',
    freeEvent: 'Evento gratuito',
    comped: 'Cortesía',
    compedTooltip:
      'Regalado a propósito por alguien que lo firmó. No es lo mismo que una reserva cuyo dinero no se puede justificar.',
    awaitingPayment: 'Esperando pago',
    needsReview: 'Requiere revisión',
    needsReviewTooltip:
      'La única cifra aquí que significa que algo anda mal. Nadie decidió regalar estas: simplemente no se puede justificar el dinero. Abre Revisión de pagos.',

    // Dinero
    money: 'Dinero',
    collected: 'Cobrado',
    online: 'en línea',
    offline: 'en persona',
    refunded: 'Reembolsado',
    net: 'Neto',
    averageTicket: 'Ticket promedio',
    benefitValueGiven: 'Valor de beneficios dados',
    benefitValueGivenTooltip:
      'Precio de lista de cada reserva pagada con un beneficio de membresía: lo que las membresías regalaron. Nunca dinero cobrado.',
    nBookingsAtList_one: '{{count}} reserva a precio de lista',
    nBookingsAtList_other: '{{count}} reservas a precio de lista',
    outstanding: 'Pendiente de cobro',

    // Pestaña de eventos
    noEvents: 'No hay eventos en este rango.',
    total: 'Total',
    inBenefits: 'en beneficios',
    nTimeslots_one: '{{count}} horario',
    nTimeslots_other: '{{count}} horarios',
    noTimeslots: 'No hay horarios en este rango.',
    timeslotsFailed: 'No se pudieron cargar los horarios.',
    nSpotsLeft_one: 'Queda {{count}} espacio',
    nSpotsLeft_other: 'Quedan {{count}} espacios',
    full: 'Lleno',
    recordPayment: 'Registrar pago',
    view: 'Ver',

    // Agregar reserva
    addBooking: 'Agregar reserva',
    bookingAdded: 'La reserva fue agregada.',
    timeslotFullWarning:
      'Este horario está lleno. Agregar a alguien lo deja sobre su capacidad.',
    user: 'Quién',
    searchUserPlaceholder: 'Buscar por nombre, correo o teléfono...',
    noUsersFound: 'Nadie coincide con esa búsqueda.',
    remove: 'Quitar',
    howItIsPaid: 'Cómo se paga',
    modeCharge: 'Cobrar normalmente',
    modeChargeHelp: 'Lo pasa por el flujo de pago habitual.',
    modeNoCharge: 'Sin cargo',
    modeNoChargeHelp: 'Lo reserva sin cobrar nada. Aparece como cortesía.',
    modePaidOffline: 'Pagado en persona',
    modePaidOfflineHelp: 'Registra el dinero junto con la reserva.',
    amount: 'Monto',
    method: 'Método',
    reference: 'Referencia',
    adminNotes: 'Notas',
    comments: 'Comentarios',
    echoCharge: '{{name}} queda reservado y se cobra como siempre.',
    echoNoCharge: '{{name}} queda reservado sin cargo, registrado como cortesía.',
    echoPaidOffline: '{{name}} queda reservado y se registran {{amount}} {{method}}.',
    package: 'Paquete',

  },

  dashboard: {
    globalTitle: 'Tablero global',
    globalDescription: 'Ventas de todas las tiendas, con filtro por una o varias.',
    storeTitle: 'Tablero de tienda',
    storeDescription: 'Ventas de esta tienda.',
    noStoreSelectedTitle: 'Selecciona una tienda',
    noStoreSelectedBody:
      'El tablero de tienda filtra por una sola tienda — elige una en el cambiador para ver sus números.',
    refresh: 'Actualizar',
    noData: 'Sin datos para este rango.',

    // Filters
    from: 'Desde',
    to: 'Hasta',
    stores: 'Tiendas',
    filterByStore: 'Filtrar por tienda',
    allStores: 'Todas las tiendas',
    oneStore: '1 tienda',
    nStores: '{{count}} tiendas',
    active: 'Activo',
    noStores: 'No hay tiendas asignadas.',
    comparedTo: 'Comparado con',

    // Revenue stats
    revenue: 'Ingresos',
    orders: 'Órdenes',
    costOfGoods: 'Costo de producto',
    grossSales: 'Ventas Brutas',
    netSales: 'Ventas Netas',
    averageTicket: 'Ticket Promedio',
    amountCollected: 'Coleccionado',

    dailyRevenue: 'Ingreso diario',
    dailyRevenueHelp:
      'Ventas netas por día, contra el mismo día del período anterior.',
    dailyDetailsTitle: 'Ingresos por día',
    date: 'Fecha',
    vs: 'vs',
    thisPeriod: 'Este período',
    previousPeriod: 'Período anterior',

    // Quick view / detail sheet
    participation: 'Participación',
    shareOf: 'Porción de {{value}}',
    viewDetails: 'Ver detalles',
    searchProducts: 'Buscar por nombre o UPC…',

    // Payment participation
    paymentParticipation: 'Participación de pago',
    paymentParticipationHelp:
      'Los cinco mayores por monto cobrado, como porción de todo lo cobrado en el período.',
    tenderType: 'Tipo de pago',
    cardType: 'Tipo de tarjeta',
    refundTotal: 'Total reembolsado',
    salesTotal: 'Total de ventas',

    // Where it sold
    merchandise: 'Mercancía',
    merchandiseHelp:
      'Precio por cantidad de los productos del catálogo vendidos, antes de impuestos y descuentos. Incluye productos pagados con tarjeta de regalo: igual salieron de la tienda. No incluye compras de tarjetas de regalo, modificadores pagados ni líneas fuera del catálogo. Coincide exactamente con “Dónde se vendió” y con el reporte de Movimiento.',
    grossSalesHelp:
      'Lo vendido antes de descuentos y reembolsos: Coleccionado menos sus impuestos, sumando de vuelta los descuentos del POS y de cupones. Se calcula desde los totales de la orden, así que incluye compras de tarjetas de regalo y modificadores pagados, y deja fuera la parte de una venta pagada con tarjeta de regalo. Por eso nunca cuadra exactamente con Mercancía.',
    netSalesHelp:
      'Ventas Brutas menos descuentos y reembolsos, sin el impuesto de cada reembolso, para que siga siendo una cifra antes de impuestos. Incluye compras de tarjetas de regalo y modificadores pagados; deja fuera la parte de una venta pagada con tarjeta de regalo.',
    amountCollectedHelp:
      'El dinero realmente cobrado al pagar (efectivo, tarjeta y ATH), con impuestos y después de los descuentos del POS. Incluye compras de tarjetas de regalo. Deja fuera la parte de una venta pagada con tarjeta de regalo, porque no entró dinero (eso es Canjes de tarjetas de regalo), y se cuenta antes de cualquier reembolso posterior.',
    giftCardRedemptions: 'Canjes de tarjetas de regalo',
    giftCardRedemptionsHelp:
      'Lo pagado con tarjetas de regalo en las órdenes del período, con impuestos. No entró dinero por esta parte de la venta, así que no está en Coleccionado, Ventas Netas ni Ventas Brutas; los productos que pagó sí están en Mercancía. Las compras de tarjetas de regalo no están aquí: están en Coleccionado.',
    ordersHelp:
      'Órdenes completadas: recogidas o entregadas. Incluye compras de tarjetas de regalo y órdenes pagadas completamente con tarjeta de regalo. El reporte de Movimiento solo cuenta órdenes con algún producto del catálogo, así que puede ser un poco menor.',
    averageTicketHelp:
      'Coleccionado dividido entre Órdenes. Una orden pagada completamente con tarjeta de regalo cuenta como orden pero no suma a Coleccionado, así que baja el promedio.',
    costOfGoodsHelp:
      'Lo que le costaron a la tienda los productos vendidos: costo unitario de cada línea por cantidad, sobre las mismas líneas que Mercancía. Incluye productos pagados con tarjeta de regalo; deja fuera compras de tarjetas de regalo y modificadores. Mercancía menos esta cifra es la ganancia que muestra “Dónde se vendió”.',
    groupingTotal: 'Mercancía en todas las filas: {{value}}',
    whereItSold: 'Dónde se vendió',
    whereItSoldHelp:
      'Los cinco mayores por total de ventas, como porción de todas las ventas del período.',
    division: 'División',
    productGroup: 'Grupo de producto',
    department: 'Departamento',
    category: 'Categoría',
    sold: 'Vendido',
    profit: 'Ganancia',

    topProducts: 'Productos más vendidos',
    topProductsHelp:
      'Los cinco mayores por total de ventas, como porción de todas las ventas de productos del período.',
    product: 'Producto',
    upc: 'UPC',

    loss: 'Pérdida',
    lossHelp:
      'Unidades dadas de baja, por razón, como porción de todas las unidades dadas de baja en el período.',
    otherCount_one: 'Otro ({{count}})',
    otherCount_other: 'Otros ({{count}})',
    ofValue: 'de {{value}}',
    composition: 'Composición',
    searchingAll: 'Buscando entre todos los productos vendidos en el período, no solo los más vendidos.',
    sortBy: 'Ordenar por',
    sortAsc: 'Orden ascendente',
    sortDesc: 'Orden descendente',
    reason: 'Razón',
    units: 'Unidades',
    // InventoryMovementReason, que el endpoint de pérdida envía como entero.
    reasonNone: 'Sin especificar',
    reasonDamage: 'Daño',
    reasonLost: 'Perdido',
    reasonFound: 'Encontrado',
    reasonReplenishment: 'Reabastecimiento',
    reasonSampling: 'Degustación',
    reasonGift: 'Regalo',
    reasonSpoiled: 'Dañado',
    reasonTransferToKitchen: 'Transferencia a cocina',
    reasonTransferToOtherStore: 'Transferencia a otra tienda',
    reasonTransferToDeli: 'Transferencia a delicatessen',
    reasonUnknown: 'Otro',

    errorTitle: 'No se pudieron cargar estos números',
    errorBody:
      'Los reportes de ventas están limitados a cuentas de finanzas y administración. Si la suya es una de ellas, intente de nuevo — es posible que el rango haya sido demasiado amplio.',

    name: 'Nombre',
  },

  overview: {
    title: 'Resumen del negocio',
    subtitle: 'Membresías, reservas y regalos, en un solo lugar.',
    loadFailed: 'No se pudo cargar el resumen.',
    noData: 'No hay datos para este rango.',

    // Filtros
    from: 'Desde',
    to: 'Hasta',
    location: 'Ubicación',
    any: 'Cualquiera',
    noLocation: 'Sin ubicación',
    showing: 'Mostrando',
    summaryAt: 'en',
    summaryEveryLocation: 'en todas las ubicaciones',
    summaryNoLocation: 'para membresías sin ubicación asignada',

    // Pestañas
    money: 'Dinero',
    activity: 'Actividad',
    people: 'Personas',

    moneyBasisNotice:
      'Cada cifra aquí se cuenta el día que llegó el dinero, completa. Una membresía pagada el 20 de agosto por un mes que corre hasta el 20 de septiembre cuenta entera como agosto: no se reparte entre los dos meses. Lo mismo aplica a un espacio comprado en julio para una clase de agosto: ingreso de julio aquí, asistencia de agosto en Reservas, así que las dos páginas no coincidirán para el mismo mes.',
    activityBasisNotice:
      'Todo lo de abajo se cuenta el día de la clase, no el día que llegó el dinero: la asistencia y la popularidad son preguntas sobre cuándo ocurrió la clase.',
    excludedNoLocation:
      '{{amount}} no se muestra aquí: viene de planes de membresía que no están atados a ninguna ubicación, así que no aparece bajo ninguna. Quita el filtro de ubicación para incluirlo.',
    noLocationBookingsNotice:
      'Las reservas no se muestran aquí. Todo evento pertenece a una ubicación, así que "sin ubicación" solo puede significar membresías y regalos.',

    // Ingresos
    revenue: 'Ingresos',
    netRevenue: 'Ingreso neto',
    netRevenueTooltip:
      'Todo lo recibido en este periodo, menos reembolsos. Esto es lo que llegó al banco.',
    collected: 'Cobrado',
    paidOnline: 'Pagado en línea',
    paidOffline: 'Pagado en persona',
    owedToYou: 'Te deben',
    owedToYouTooltip:
      'Dinero esperado y no recibido, de dos lugares. La mitad de reservas son balances sin pagar de reservas vigentes al día de hoy: no se filtra por el rango de fechas, así que no cambia al mirar otro mes. La mitad de membresías es el precio de lista de membresías activas que nunca se facturaron. Ninguna es ingreso, y como se cuentan distinto el total no cuadrará con ningún otro reporte: persigue las dos mitades, no la suma.',
    averagePayment: 'Pago promedio',
    payments: 'Pagos',
    refunded: 'Reembolsado',
    noPaymentRecorded: 'Sin pago registrado',
    noPaymentRecordedTooltip:
      'Reservas que un empleado asignó sin cobrar. No tienen ningún pago asociado — ni pendiente ni fallido — así que no aparecen en ninguna otra cifra de esta página. "Cortesía" fue una decisión que alguien tomó y firmó; "requiere revisión" es un espacio pagado sin pago y sin responsable, lo cual vale la pena investigar.',
    comped: 'Cortesía',
    needsReview: 'Requiere revisión',
    value: 'Valor',

    // Por fuente
    revenueBySource: 'Ingresos por fuente',
    memberships: 'Membresías',
    bookings: 'Reservas',
    gifts: 'Regalos',
    vouchers: 'Certificados de regalo',
    other: 'Otro',
    nothingPreviousPeriod: 'nada en el periodo anterior',
    previousPeriod: 'Periodo anterior',
    whereItCameFrom: 'De dónde vino',
    whereItCameFromHelp: 'Cada fuente, con los eventos o planes detrás.',
    howItArrived: 'Cómo llegó',
    net: 'Neto',
    share: 'Participación',
    shareOfSource: 'Participación de esta fuente',
    nItems_one: '{{count}} elemento',
    nItems_other: '{{count}} elementos',
    offline: 'En persona',
    renewal: 'Renovación',

    // No es ingreso
    notIncome: 'No es ingreso',
    benefitValueGiven: 'Valor de beneficios dados',
    benefitValueGivenTooltip:
      'Precio de lista de cada reserva pagada con un beneficio de membresía: lo que las membresías regalaron. Nunca dinero cobrado.',
    voided: 'Anulado',
    voidedTooltip:
      'Pagos en persona ingresados por error y luego anulados. Excluidos de toda otra cifra de esta página.',

    // Recurrente
    recurringVsOneOff: 'Recurrente vs único',
    recurringRevenue: 'Ingreso recurrente',
    recurringRevenueTooltip:
      'Renovaciones de membresía. Vale separarlo porque el ingreso recurrente es un pronóstico, mientras que el único es un resultado.',
    ofCollected: 'de lo cobrado',
    oneOffRevenue: 'Ingreso único',
    autoRenewing: 'Con renovación automática',
    autoRenewingTooltip:
      'Membresías activas configuradas para renovarse. No es ingreso: es un conteo de lo que se espera volver a cobrar.',
    expectedToBillAgain: 'se espera volver a cobrar',
    dailyRevenue: 'Ingreso diario',

    // Actividad
    activeMemberships: 'Membresías activas',
    newInPeriod: 'nuevas en el periodo',
    expiringSoon: 'Por vencer',
    expiringSoonTooltip:
      'Membresías activas que terminan en 7 días. La lista de retención: todavía hay tiempo de hacer algo.',
    recentlyExpired: 'Vencidas recientemente',
    recentlyExpiredSub: 'últimos 14 días, sin renovar',
    recentlyExpiredTooltip:
      'Membresías que terminaron en los últimos 14 días y no se renovaron, de personas sin otra membresía activa. La lista para recuperar clientes. Es la situación de hoy: las fechas de arriba no la cambian.',
    events: 'Eventos',
    checkInRate: 'Tasa de asistencia',
    checkInRateTooltip:
      'Registros sobre reservas de clases que ya ocurrieron. Las reservas futuras se excluyen, o el mes actual siempre parecería ausentismo masivo.',
    checkedIn: 'Registrados',
    usedABenefit: 'Usaron un beneficio',
    uniqueCustomers: 'Clientes únicos',
    plansSelling: 'Planes que se venden',
    activeNow: 'Activas ahora',
    purchases: 'Compras',
    new: 'Nuevas',
    renewals: 'Renovaciones',
    topEvents: 'Eventos destacados',
    topEventsHelp:
      'Ordenados por cuánta gente reservó. Las reservas por beneficio se muestran aparte: una clase llena de miembros es popular y no genera ingreso directo.',
    fillRate: 'Ocupación',

    // Personas
    topBenefitUsers: 'Mayor uso de beneficios',
    topBenefitUsersHelp:
      'Tus miembros más contentos: aquellos para quienes la membresía claramente vale la pena.',
    unusedBenefits: 'Beneficios sin usar',
    unusedBenefitsHelp:
      'Membresías activas sin beneficios usados en este periodo, las que vencen antes primero. No son malos clientes: son clientes a punto de cancelar.',
    mostConsistent: 'Más constantes',
    mostConsistentHelp:
      'Mayor tasa de asistencia, entre quienes tienen al menos algunas reservas para que una asistencia con suerte no encabece la lista.',
    paidButNoShow: 'Pagaron y no asistieron',
    paidButNoShowHelp:
      'Gastaron dinero en reservas y no asistieron a ninguna. Una solicitud de reembolso a punto de ocurrir.',
    dropInsToConvert: 'Visitas por convertir',
    dropInsToConvertHelp:
      'Entrenan y pagan por clase sin ninguna membresía. Ordenados por cuánto ya gastan frente al plan más barato que les podrías vender: cualquiera por encima está pagando más de lo que costaría una membresía.',
    convertedCustomers: 'Clientes convertidos',
    convertedCustomersHelp:
      'Miembros que antes pagaban por clase, compraron una membresía y siguieron volviendo con beneficios. El rango de fechas acota su actividad, no quién está en la lista: una conversión ocurrió una vez, cuando haya ocurrido.',
    exportList: 'Exportar lista',
    benefitsUsed: 'Beneficios usados',
    expires: 'Vence',
    spent: 'Gastado',
    spentVsPlan: 'Gastado vs plan',
    spentBeforeJoining: 'Gastado antes de unirse',
    bookingsBeforeJoining: 'Reservas antes de unirse',
    planPriceCompare: '{{plan}} cuesta {{price}}',
    convertedOn: 'Convertido {{date}}',
    converted: 'Convertido',
    benefitsSinceJoining_one: '{{count}} beneficio usado desde entonces',
    benefitsSinceJoining_other: '{{count}} beneficios usados desde entonces',
    minBookingsNote:
      'Solo se clasifican las personas con al menos {{count}} reservas en clases que ya ocurrieron, para que una sola asistencia con suerte no encabece la lista.',
    lapsed: 'Vencida',
    neverAMember: 'Nunca fue miembro',
    plan: 'Plan',
    name: 'Nombre',
    email: 'Correo',
    phone: 'Teléfono',

    // Desglose
    total: 'Total',
    nRows_one: '{{count}} fila',
    nRows_other: '{{count}} filas',
  },

  paymentReview: {
    title: 'Revisión de pagos',
    subtitle:
      'Reservaciones que debieron pagarse y no tienen ningún pago registrado. Cada una termina en una decisión.',
    loadFailed: 'No se pudo cargar la lista de revisión.',
    actionFailed: 'No se pudo completar. Intenta de nuevo.',
    refresh: 'Refrescar',

    // Encabezado
    lastSearchedAt: 'PayPal se buscó por última vez {{when}}.',
    neverSearched: 'Aún no se ha buscado en PayPal.',
    searchPaypalNow: 'Buscar en PayPal ahora',
    findOldPayments: 'Buscar pagos antiguos',
    sweepFinished:
      'Se revisaron {{searched}} reservación(es) y se encontraron {{found}} pago(s) posible(s).',

    // Resumen
    openReviews: 'Esperando decisión',
    amountAtStake: 'Monto en juego',
    allClear: 'Todas las reservaciones tienen su pago justificado.',

    // Las dos mitades de la lista
    found: 'Pagos posibles encontrados',
    foundHelp:
      'Estas reservaciones no tienen pago registrado, pero un pago de PayPal parece coincidir. Verifica el pagador y el monto, luego adjunta el correcto.',
    noMatch: 'Sin pago encontrado',
    noMatchHelp:
      'Nada en PayPal coincide con estas reservaciones. Confirma que cada una fue cortesía a propósito, o cancélala.',

    // Datos de la reservación
    bookedAt: 'Reservado',
    confirmed: 'Confirmada',
    pending: 'Pendiente',
    cancelled: 'Cancelada',

    // Candidatos
    needsAChoice: 'Requiere elección',
    readyToAttach: 'Listo para adjuntar',
    ruledOutCount_one: '{{count}} pago ya descartado',
    ruledOutCount_other: '{{count}} pagos ya descartados',
    alsoFitsOtherBookings: 'También coincide con otra',
    alsoFitsOtherBookingsHelp:
      'Este mismo pago también coincide con otra reservación. Adjuntarlo aquí significa que ya no estará disponible para esa.',
    attachPayment: 'Adjuntar',
    attachPaymentPrompt:
      '¿Registrar este pago de PayPal de {{amount}} en la reservación de {{name}}?',
    paymentAttached: 'El pago fue adjuntado.',
    notThisOne: 'No es este',
    rejectPaymentPrompt:
      '¿Descartar este pago para esta reservación? No se volverá a sugerir.',
    paymentRuledOut: 'Ese pago no se volverá a sugerir.',

    // Decisiones sin coincidencia
    awaitingPayment: 'Esperando pago',
    needsReview: 'Requiere revisión',
    searchedTimes_one: 'Buscado {{count}} vez',
    searchedTimes_other: 'Buscado {{count}} veces',
    confirmComped: 'Confirmar como cortesía',
    confirmCompedHelp:
      'Esto registra que la reservación de {{name}} para {{event}} fue cortesía a propósito y que no falta ningún pago. Agrega una razón para que la próxima persona entienda por qué.',
    compedReasonPlaceholder: '¿Por qué se dio de cortesía?',
    comped: 'Registrada como cortesía.',
    cancelBooking: 'Cancelar reservación',
    cancelBookingPrompt:
      '¿Cancelar la reservación de {{name}}? Se le notificará y el espacio quedará libre.',
    bookingCancelled: 'La reservación fue cancelada.',

    // Buscar pagos antiguos
    close: 'Cerrar',
    preview: 'Revisar',
    applyChanges: 'Aplicar',
    findOldPaymentsHelp:
      'Busca los pagos de las reservaciones de este año que no tienen ninguno registrado. Vincula las membresías cuyo beneficio nunca se adjuntó y luego aplica cualquier pago de PayPal que pueda identificar por la referencia del pago o por correo del pagador, monto y fecha. Previsualiza primero; no se escribe nada hasta que apliques.',
    reconcileApplyPrompt:
      '¿Aplicar estos cambios? Los pagos se registrarán en las reservaciones mostradas.',
    previewOnly: 'Esto fue una vista previa. Todavía no se ha escrito nada.',
    bookingsExamined: 'revisadas',
    paypalPaymentsScanned: 'pagos de PayPal revisados',
    alreadyRecordedElsewhere:
      'de esos ya están registrados en otra reservación o membresía, y no se ofrecieron',
    benefitsRelinked: 'eran beneficios de membresía',
    matchedByReference: 'identificados por referencia del pago',
    matchedByEvidence: 'identificados por correo, monto y fecha',

    // Por qué no hubo coincidencia
    whyNoMatch_one: 'Por qué {{count}} no encontró nada',
    whyNoMatch_other: 'Por qué {{count}} no encontraron nada',
    missNoPaymentFromPayer:
      'no tienen ningún pago de PayPal desde ese correo: pagaron de otra forma, o su cuenta de PayPal usa otro correo',
    missWrongAmount:
      'sí pagaron por PayPal, pero nunca el precio de esta reservación: normalmente un precio que cambió después',
    missWrongPayer:
      'tienen un pago por el monto correcto y a la hora correcta, pero desde otro correo',
    missOutsideWindow:
      'tienen el pagador y el monto correctos, pero más separados que la ventana de {{hours}} horas',
    missClosestGap: '(el más cercano fue de {{hours}} horas)',

    // Resultados
    outcomeBenefit: 'Beneficio de membresía',
    outcomeCustomId: 'Identificado por referencia',
    outcomeEvidence: 'Identificado por evidencia',
    outcomeNone: 'Nada encontrado',

    // Cerrar el rezago antiguo
    olderBookings: 'Reservaciones antiguas',
    olderBookingsHelp:
      'Las reservaciones hechas antes del {{date}} son demasiado antiguas para buscarlas en PayPal. Cerrarlas las saca de esta lista y del conteo de Requiere revisión. Quedan registradas como cerradas sin revisar, no como cortesías, porque nadie las verificó.',
    closeOutPreview_one: 'Se cerraría {{count}} reservación por {{amount}}.',
    closeOutPreview_other: 'Se cerrarían {{count}} reservaciones por {{amount}}.',
    closeOutApplyPrompt_one:
      '¿Cerrar {{count}} reservación sin buscar su pago?',
    closeOutApplyPrompt_other:
      '¿Cerrar {{count}} reservaciones sin buscar sus pagos?',
    closeThemOut: 'Cerrarlas',
    archivedBookings_one: '{{count}} reservación antigua cerrada.',
    archivedBookings_other: '{{count}} reservaciones antiguas cerradas.',
  },

  giftCards: {
    title: 'Tarjetas de regalo',
    subtitle:
      'Balances de las tarjetas, las transacciones detrás de ellos y lo que aún se debe en tarjetas sin gastar.',

    accountPayable: 'Balance pendiente',
    accountPayableHint: 'Valor sin gastar en tarjetas activadas: lo que el negocio aún debe.',
    activeCards: 'Tarjetas activas',
    activeCardsHint: 'Tarjetas activadas que aún tienen balance.',
    payableFailed: 'No se pudo cargar el balance pendiente.',

    tabCards: 'Tarjetas',
    tabTransactions: 'Transacciones',
    tabPurchases: 'Compras',

    from: 'Desde',
    to: 'Hasta',
    allStores: 'Todas las tiendas',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    periodTotals:
      'Se cargaron {{purchased}} y se redimieron {{redeemed}} en todo el periodo, no solo en las filas de esta página.',
    loadFailed: 'No se pudo cargar esa lista.',

    searchPlaceholder: 'Buscar por código o apodo...',
    cardState: 'Estado de la tarjeta',
    stateAll: 'Todas',
    stateActivated: 'Activadas',
    stateWithBalance: 'Activadas, con balance',
    stateNoBalance: 'Activadas, sin balance',
    stateNotActivated: 'Sin activar',

    noCards: 'Ninguna tarjeta de regalo coincide con esta búsqueda.',
    noTransactions: 'No hay transacciones en este periodo.',
    noPurchases: 'No hay compras en este periodo.',

    code: 'Código',
    owner: 'Dueño',
    created: 'Creada',
    activated: 'Activada',
    activatedBadge: 'Activada',
    activatedOn: 'Activada el {{date}}',
    notActivated: 'Sin activar',
    opening: 'Inicial',
    balance: 'Balance',
    balanceAfter: 'Balance después',
    unassigned: 'Nadie',
    view: 'Ver',

    when: 'Cuándo',
    order: 'Orden',
    store: 'Tienda',
    type: 'Tipo',
    amount: 'Monto',
    load: 'Carga',
    redemption: 'Redención',

    revert: 'Revertir',
    revertConfirm:
      'Esto deshace {{amount}} en la tarjeta {{code}} y devuelve el balance. No se puede rehacer desde aquí.',
    reverted: 'Transacción revertida.',
    actionFailed: 'No se pudo completar esa acción.',

    cardLoadFailed: 'No se pudo cargar esta tarjeta.',
    nickname: 'Apodo',
    nicknamePlaceholder: 'Un nombre para reconocer esta tarjeta',
    nicknameSaved: 'Apodo guardado.',

    assignOwner: 'Asignar dueño',
    removeOwner: 'Quitar',
    ownerAssigned: 'Dueño asignado.',
    ownerRemoved: 'Dueño removido.',
    searchUser: 'Buscar por nombre, correo o teléfono...',
    noUsersFound: 'No se encontraron usuarios.',

    history: 'Historial',
    recalculate: 'Recalcular',
    balanceRecalculated: 'Balance recalculado a partir de las transacciones.',
    noCardHistory: 'Todavía no se ha movido nada en esta tarjeta.',

    createCards: 'Crear tarjetas',
    create: 'Crear',
    createBody:
      'Se generan códigos en blanco y se descargan como archivo de texto. Ese archivo es la única copia: los códigos no se pueden volver a listar como lote.',
    howMany: 'Cuántas',
    batchLimit: 'Entre 1 y {{max}} a la vez.',
    created_one: '{{count}} código creado y descargado.',
    created_other: '{{count}} códigos creados y descargados.',
  },

  imageUpload: {
    prompt: 'Haz clic o suelta una imagen aquí',
    urlPlaceholder: 'https://... o sube una abajo',
    replace: 'Reemplazar',
    clear: 'Quitar',
    wrongType: 'Ese archivo no es JPEG, PNG, WebP ni GIF.',
    tooLarge: 'Esa imagen pesa más de 5 MB. Compármela e inténtalo de nuevo.',
    failed: 'No se pudo subir la imagen.',
  },

  infoCards: {
    title: 'Tarjetas informativas',
    subtitle:
      'Los paneles promocionales que muestran la app y la web, y cuándo corre cada uno.',
    searchPlaceholder: 'Buscar por nombre o título...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar las tarjetas informativas.',
    none: 'Todavía no hay tarjetas informativas.',
    noneMatch: 'Ninguna tarjeta coincide con estos filtros.',

    status: 'Estado',
    statusAny: 'Cualquier estado',
    statusLive: 'Activa',
    statusPlanned: 'Programada',
    statusCompleted: 'Terminada',
    statusInactive: 'Inactiva',
    daysLeft_one: 'Queda {{count}} día',
    daysLeft_other: 'Quedan {{count}} días',

    name: 'Nombre',
    nameHint: 'Solo interno: así reconocerás esta tarjeta en la lista.',
    store: 'Tienda',
    storeHint: 'Déjala en todas las tiendas a menos que sea para una sola.',
    allStores: 'Todas las tiendas',
    oneStore: 'Una tienda',
    window: 'Periodo',
    shownIn: 'Se muestra en',
    app: 'App',
    web: 'Web',
    nowhere: 'En ningún lado',
    edit: 'Editar',

    newCard: 'Nueva tarjeta',
    editCard: 'Editar tarjeta informativa',
    create: 'Crear',
    created: 'Tarjeta informativa creada.',
    updated: 'Tarjeta informativa guardada.',
    saveFailed: 'No se pudo guardar la tarjeta informativa.',
    archive: 'Archivar',
    archiveConfirm:
      '"{{name}}" se quita de la lista y deja de mostrarse en todos lados. Esto no se puede deshacer desde aquí.',
    archived: 'Tarjeta informativa archivada.',

    content: 'Contenido',
    cardTitle: 'Título',
    body: 'Cuerpo',
    htmlBody: 'Cuerpo HTML',
    htmlBodyHint: 'Opcional. Lo usan los clientes que muestran texto enriquecido en vez del cuerpo simple.',
    actionText: 'Texto del botón',
    actionUrl: 'Enlace del botón',
    actionUrlHint: 'A dónde lleva la tarjeta al tocarla. Es el mismo para ambos idiomas.',
    image: 'Imagen',
    imageHint: 'Se muestra a quienes leen en este idioma.',

    schedule: 'Programación',
    starts: 'Comienza',
    ends: 'Termina',
    endBeforeStart: 'El final tiene que ser después del comienzo.',

    placement: 'Ubicación',
    isActive: 'Activa',
    isActiveHint: 'Apagada, la tarjeta no se muestra en ningún lado sin importar sus fechas.',
    showInApp: 'Mostrar en la app',
    showInWeb: 'Mostrar en la web',
    ordering: 'Orden',
    orderingHint: 'Los números más bajos van primero.',
    defaultHeight: 'Altura por defecto',
    height: 'Altura (px)',

    targeting: 'Segmentación',
    gender: 'Género',
    genderAny: 'Cualquiera',
    genderMale: 'Masculino',
    genderFemale: 'Femenino',
    ageRange: 'Grupo de edad',
    ageAll: 'Todas las edades',
    ageMinor: 'Menores',
    ageTeenager: 'Adolescentes',
    ageMiddleAged: 'Adultos',
    ageSenior: 'Adultos mayores',
  },

  bannerAds: {
    title: 'Anuncios de banner',
    subtitle:
      'Las franjas promocionales que llevan la app y la web, y si cada una está aprobada para correr.',
    searchPlaceholder: 'Buscar por nombre o tienda...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar los anuncios de banner.',
    none: 'Todavía no hay anuncios de banner.',
    noneMatch: 'Ningún anuncio coincide con estos filtros.',

    status: 'Estado',
    statusAny: 'Cualquier estado',
    statusLive: 'Activo',
    statusScheduled: 'Programado',
    statusEnded: 'Terminado',
    statusUnapproved: 'Sin aprobar',

    name: 'Nombre',
    nameHint: 'Solo interno: así reconocerás este banner en la lista.',
    position: 'Posición',
    anyPosition: 'Cualquier posición',
    placementTop: 'Arriba',
    placementBottom: 'Abajo',
    placementLeft: 'Izquierda',
    placementRight: 'Derecha',
    placementPopup: 'Emergente',
    window: 'Periodo',
    shownIn: 'Se muestra en',
    app: 'App',
    web: 'Web',
    nowhere: 'En ningún lado',
    store: 'Tienda',
    storeHint: 'Déjalo en todas las tiendas a menos que sea para una sola.',
    allStores: 'Todas las tiendas',
    edit: 'Editar',

    newBanner: 'Nuevo banner',
    editBanner: 'Editar banner',
    create: 'Crear',
    created: 'Banner creado.',
    updated: 'Banner guardado.',
    saveFailed: 'No se pudo guardar el banner.',
    archive: 'Archivar',
    archiveConfirm:
      '"{{name}}" deja de correr y sale de esta lista. El registro se conserva, pero el portal no puede recuperarlo.',
    archived: 'Banner archivado.',

    media: 'Medios',
    imageEnglish: 'Imagen en inglés',
    imageSpanish: 'Imagen en español',
    imageSpanishHint: 'Opcional. Si está vacía se usa la de inglés para quienes leen en español.',
    backgroundColor: 'Color de fondo',
    backgroundColorHint: 'Se ve detrás de la imagen donde esta no llena el espacio.',

    schedule: 'Programación',
    starts: 'Comienza',
    ends: 'Termina',
    datesHint: 'Deja una vacía para correr sin límite en esa dirección.',
    endBeforeStart: 'El final tiene que ser después del comienzo.',

    placement: 'Ubicación',
    approved: 'Aprobado',
    approvedHint: 'Nada corre hasta que esto esté encendido, sin importar las fechas.',
    showInApp: 'Mostrar en la app',
    showInWeb: 'Mostrar en la web',
    ordering: 'Orden',
    orderingHint: 'Los números más bajos van primero entre banners de la misma posición.',

    action: 'Acción',
    actionType: 'Al tocar',
    actionNone: 'No hacer nada',
    actionLink: 'Abrir un enlace',
    actionSearch: 'Hacer una búsqueda',
    linkUrl: 'Enlace',
    linkUrlHint: 'A dónde lleva el banner al tocarlo.',
    searchTerm: 'Término de búsqueda',
    searchTermHint: 'La búsqueda en el catálogo que hace este banner al tocarlo.',
  },

  promoCodes: {
    title: 'Códigos promocionales',
    subtitle:
      'Códigos de descuento que cualquiera puede escribir al pagar, y códigos emitidos a un cliente.',

    tabStore: 'Códigos de tienda',
    tabCustomer: 'Códigos de cliente',

    searchStore: 'Buscar por código o tienda...',
    searchCustomerCodes: 'Buscar por código o cliente...',
    searchLabel: 'Buscar códigos promocionales',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar los códigos promocionales.',
    none: 'Ningún código coincide con esta búsqueda.',
    allStores: 'Todas las tiendas',

    code: 'Código',
    codeHint: 'Lo que escribe el cliente. En mayúsculas, y no se puede cambiar después de guardar.',
    theCode: 'El código',
    customer: 'Cliente',
    store: 'Tienda',
    global: 'Todas las tiendas',
    value: 'Valor',
    minPurchase: 'Compra mínima',
    minPurchaseHint: 'Déjalo en 0 para no exigir mínimo.',
    noMinimum: 'Ninguna',
    expires: 'Vence',
    expiryHint: 'Válido hasta el final de ese día. Déjalo vacío y no vence nunca.',
    neverExpires: 'No vence',
    expired: 'Vencido',

    scope: 'Dónde aplica',
    storeHint: 'Déjalo en todas las tiendas a menos que sea para una sola.',
    appliesTo: 'Aplica a',
    appliesTo0: 'Recogido',
    appliesTo1: 'Entrega',
    appliesTo2: 'Recogido y entrega',
    usageLimit: 'Límite de uso',
    usageLimitHint: 'Cuántas veces se puede redimir este código.',
    usage0: 'Una vez en total',
    usage1: 'Ilimitado',
    usage2: 'Una vez por cliente',

    notification: 'Notificación',
    notifyCustomer: 'Avisarle al cliente',
    notifyHint: 'Le envía el código con el mensaje de abajo. Apagado, se emite en silencio.',
    message: 'Mensaje',
    searchCustomer: 'Buscar por nombre, correo o teléfono...',
    noCustomers: 'No se encontraron clientes.',
    clearCustomer: 'Elegir a otra persona',

    newStoreCode: 'Nuevo código de tienda',
    newCustomerCode: 'Nuevo código de cliente',
    editCode: 'Editar código promocional',
    editExpiry: 'Editar vencimiento',
    create: 'Crear',
    created: 'Código promocional creado.',
    expiryUpdated: 'Vencimiento actualizado.',
    saveFailed: 'No se pudo guardar el código promocional.',
    expiryOnlyNotice:
      'Solo se puede cambiar la fecha de vencimiento. Todo lo demás queda fijo al crear el código: archiva este y crea uno nuevo para cambiarlo.',

    archive: 'Archivar',
    archiveConfirm:
      '"{{code}}" deja de funcionar de inmediato y sale de esta lista. Las redenciones ya hechas se conservan.',
    archived: 'Código promocional archivado.',

    redemptions: 'Redenciones',
    redemptionsFor: 'Todas las órdenes que han usado {{code}}.',
    redemptionsFailed: 'No se pudieron cargar las redenciones.',
    neverRedeemed: 'Este código todavía no se ha usado.',
    close: 'Cerrar',
  },

  sponsorships: {
    title: 'Patrocinios',
    subtitle:
      'Un periodo, un conjunto de productos y un conjunto de tiendas. Nada llega al catálogo hasta que lo apliques.',
    searchPlaceholder: 'Buscar por nombre...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar los patrocinios.',
    detailFailed: 'No se pudo cargar este patrocinio.',
    none: 'Todavía no hay patrocinios.',
    noneMatch: 'Ningún patrocinio coincide con estos filtros.',
    backToList: 'Todos los patrocinios',
    open: 'Abrir',
    edit: 'Editar',

    status: 'Estado',
    statusAny: 'Cualquier estado',
    statusRunning: 'En periodo',
    statusScheduled: 'Programado',
    statusFinished: 'Terminado',

    name: 'Nombre',
    nameHint: 'Solo interno: así reconocerás este patrocinio.',
    window: 'Periodo',
    starts: 'Comienza',
    ends: 'Termina',
    endBeforeStart: 'El final tiene que ser después del comienzo.',
    windowHint:
      'Este periodo es lo que se escribe en cada producto al aplicar el patrocinio. Ambos días se incluyen.',

    newSponsorship: 'Nuevo patrocinio',
    editSponsorship: 'Editar patrocinio',
    create: 'Crear',
    created: 'Patrocinio creado.',
    updated: 'Patrocinio guardado.',
    saveFailed: 'No se pudo guardar el patrocinio.',
    delete: 'Eliminar',
    deleteConfirm:
      '"{{name}}" se elimina junto con sus productos y tiendas asignados. Los productos ya marcados por una aplicación anterior conservan sus fechas.',
    deleted: 'Patrocinio eliminado.',

    pushToCatalogue: 'Enviar al catálogo',
    pushExplainer_zero:
      'Aplicar escribe este periodo en cada producto del patrocinio, en cada tienda incluida. Agrega primero algunos de cada uno.',
    pushExplainer_one:
      'Aplicar escribe este periodo en {{count}} combinación de producto y tienda. Cancelar la retrocede para que deje de aparecer como patrocinada.',
    pushExplainer_other:
      'Aplicar escribe este periodo en {{count}} combinaciones de producto y tienda. Cancelar las retrocede para que dejen de aparecer como patrocinadas.',
    apply: 'Aplicar',
    applyConfirm_one:
      'Se actualiza hasta {{count}} combinación: solo se tocan las tiendas que realmente venden el producto. Vuelve a aplicarlo si cambias productos, tiendas o fechas.',
    applyConfirm_other:
      'Se actualizan hasta {{count}} combinaciones: solo se tocan las tiendas que realmente venden el producto. Vuelve a aplicarlo si cambias productos, tiendas o fechas.',
    applied: 'Patrocinio aplicado al catálogo.',
    cancelRun: 'Cancelar',
    cancelConfirm_one:
      'Se retrocede hasta {{count}} combinación y deja de aparecer como patrocinada. El patrocinio se conserva y se puede aplicar de nuevo.',
    cancelConfirm_other:
      'Se retroceden hasta {{count}} combinaciones y dejan de aparecer como patrocinadas. El patrocinio se conserva y se puede aplicar de nuevo.',
    cancelled: 'Patrocinio cancelado en el catálogo.',
    needBoth: 'Agrega al menos una tienda y un producto antes de aplicar.',
    actionFailed: 'No se pudo completar esa acción.',

    stores_one: '{{count}} tienda',
    stores_other: '{{count}} tiendas',
    chooseStore: 'Elige una tienda...',
    noStores: 'Todavía no hay tiendas en este patrocinio.',
    items_one: '{{count}} producto',
    items_other: '{{count}} productos',
    searchItems: 'Buscar en el catálogo...',
    noItemsFound: 'Ningún producto coincidió.',
    noItems: 'Todavía no hay productos en este patrocinio.',
    add: 'Agregar',
    added: 'Agregado',
    remove: 'Quitar',
  },

  movement: {
    title: 'Movimiento',
    subtitle:
      'Lo que se vendió, agrupado por dónde está el producto. Selecciona una fila para ver más.',
    level: 'Agrupar por',
    level_division: 'División',
    level_group: 'Grupo de artículos',
    level_department: 'Departamento',
    level_category: 'Categoría',
    store: 'Tienda',
    allStores: 'Todas las tiendas',
    department: 'Departamento',
    pickDepartment: 'Selecciona un departamento',
    pickDepartmentFirst:
      'Las categorías viven dentro de un departamento. Selecciona uno para ver sus categorías.',
    from: 'Desde',
    to: 'Hasta',
    breadcrumb: 'Nivel del reporte',
    viewAllItems: 'Ver todos los artículos',
    allItems: 'Todos los artículos',
    orders: 'Órdenes',
    ordersHint:
      'Órdenes con al menos un producto del catálogo. Una compra de tarjeta de regalo no tiene ninguno, así que nunca se cuenta aquí; Órdenes en el panel sí la incluye.',
    lines: 'Líneas de orden',
    linesHint:
      'Líneas en una orden, no productos distintos: el mismo producto comprado dos veces cuenta dos veces.',
    unitsSold: 'Unidades vendidas',
    unitsHint:
      'Cantidad vendida en las líneas del catálogo. Los productos pesados cuentan su peso, así que no siempre es un número entero.',
    units: 'Unidades',
    merchandise: 'Mercancía',
    merchandiseHint:
      'Precio por cantidad de los productos del catálogo vendidos, antes de impuestos y descuentos. Incluye productos pagados con tarjeta de regalo: el producto se movió aunque no entró dinero. Nunca incluye una compra de tarjeta de regalo, que es dinero cobrado y no un producto. Coincide exactamente con Mercancía y “Dónde se vendió” en el panel.',
    total: 'Total',
    totalHint:
      'Mercancía más sus impuestos más los modificadores pagados. No es comparable con Coleccionado en el panel, que incluye compras de tarjetas de regalo, deja fuera lo pagado con tarjeta de regalo y es después de los descuentos del POS.',
    totalBreakdown: 'incl. {{tax}} impuestos, {{modifiers}} modificadores',
    share: 'Participación',
    modifiers: 'Modificadores',
    profit: 'Ganancia',
    margin: 'Margen',
    item: 'Artículo',
    upc: 'UPC',
    priceTotal: 'Total precio',
    stateTax: 'Impuesto estatal',
    municipalTax: 'Impuesto municipal',
    totalSales: 'Ventas totales',
    avgPrice: 'Precio promedio',
    avgCost: 'Costo promedio',
    modifier: 'Modificador',
    modifiersOn: 'Modificadores de {{item}}',
    showModifiers: 'Ver los modificadores de {{item}}',
    unnamed: 'Sin nombre',
    substituted: 'Artículos sustituidos',
    noRows: 'No hubo ventas en este rango.',
    noItems: 'No hay artículos en esta selección.',
    export: 'Exportar',
    exported: 'Se exportaron {{count}} filas.',
    nothingToExport: 'No hay nada que exportar.',
    failed: 'No se pudo cargar el reporte.',
  },
  settlements: {
    title: 'Liquidaciones',
    subtitle:
      'Cada lote de terminal según se cerró. Elige un lote para ver su desglose y su recibo.',
    store: 'Tienda',
    allStores: 'Todas las tiendas',
    lane: 'Caja',
    allLanes: 'Todas las cajas',
    from: 'Desde',
    to: 'Hasta',
    sales: 'Ventas liquidadas',
    salesSub: '{{transactions}} transacciones en {{batches}} lotes',
    salesHint:
      'Ventas con tarjeta, ATH Móvil y cash card según cada terminal cerró su lote, con impuestos. Fechadas por el cierre del lote, no por la venta: un lote cerrado en la mañana trae las ventas de la noche anterior. No se compara con Coleccionado del dashboard, que también cuenta el efectivo de la gaveta y las compras de tarjetas de regalo, se fecha por la venta y se cuenta antes de devoluciones.',
    credit: 'Crédito',
    creditHint: 'Visa, Mastercard, American Express y Discover.',
    debit: 'Débito',
    debitHint: 'Tarjetas de débito ATH y ATH Móvil.',
    cash: 'Cash card',
    cashHint:
      'Lo que la terminal archiva como “Cash”; el POS lo registra como CASH CARD. No es el efectivo de la gaveta, que nunca pasa por la terminal.',
    transactions: '{{transactions}} transacciones',
    refunds: 'Devoluciones',
    refundsHint:
      'Devoluciones que la terminal acreditó a una tarjeta en estos lotes, con impuestos. Las anulaciones no están aquí: una venta anulada se saca de su lote.',
    difference: 'Diferencia con el POS',
    differenceHint:
      'Ventas liquidadas menos lo que el POS registró para los mismos lotes, emparejados por el número de lote que la terminal estampa en cada transacción. Por encima de cero, la terminal aprobó dinero que el POS nunca guardó. Un lote sin una liquidación anterior en su caja desde donde empezar no se compara.',
    differenceNone: 'Todos los lotes cuadran con el POS',
    differenceSome: '{{differing}} de {{compared}} lotes no cuadran',
    notCompared: '{{batches}} sin comparar',
    closedAt: 'Cierre',
    transactionsColumn: 'Transacciones',
    salesColumn: 'Ventas',
    net: 'Neto',
    posRecorded: 'Registrado en POS',
    differenceColumn: 'Diferencia',
    matches: 'Cuadra',
    notComparedShort: 'Sin comparar',
    noRows: 'No se cerraron lotes en este rango.',
    failed: 'No se pudieron cargar las liquidaciones.',
    export: 'Exportar',
    exported: 'Se exportaron {{rows}} lotes.',
    nothingToExport: 'No hay nada que exportar.',
    batchTitle: '{{lane}} · {{store}}',
    laneFallback: 'Caja',
    closedOn: 'Cerrado {{at}}',
    openedOn: 'Abierto {{at}}',
    openedUnknown: 'sin liquidación anterior en esta caja',
    comparison: 'Comparado con el POS',
    terminal: 'Terminal',
    pos: 'POS',
    saleCount: 'Ventas',
    saleAmount: 'Monto de ventas',
    refundCount: 'Devoluciones',
    refundAmount: 'Monto de devoluciones',
    explainMatch: 'El POS registró cada transacción de este lote, al centavo.',
    explainTerminalHigher:
      'La terminal cerró {{amount}} más de lo que el POS registró para este lote: un cobro que la terminal aprobó y el POS nunca guardó.',
    explainPosHigher:
      'El POS registró {{amount}} más de lo que la terminal cerró para este lote.',
    explainRefunds: 'Las devoluciones difieren por {{amount}}.',
    explainNotCompared:
      'Sin comparar: esta caja no tiene una liquidación anterior que marque dónde empezó el lote, o el lote estuvo abierto más de 14 días.',
    categories: 'Por categoría de la terminal',
    category: 'Categoría',
    total: 'Total',
    terminalId: 'ID de terminal',
    batchNumber: 'Lote',
    status: 'Estado de la terminal',
    viewReceipt: 'Ver recibo',
    noReceipt: 'Sin recibo',
    receiptTitle: 'Recibo de liquidación',
    receiptFailed: 'No se pudo cargar el recibo.',
    close: 'Cerrar',
  },
  salesSummary: {
    title: 'Resumen de ventas',
    subtitle:
      'Las cifras de ingresos del dashboard para cualquier rango, con las líneas que las suman: pagos, impuestos y depósitos de efectivo.',
    store: 'Tienda',
    allStores: 'Todas las tiendas',
    channel: 'Órdenes',
    channel_all: 'Todas las órdenes',
    channel_pos: 'POS',
    channel_selfCheckout: 'Self-checkout',
    channel_online: 'En línea',
    from: 'Desde',
    to: 'Hasta',
    sameTile:
      'Con Todas las órdenes, esta es la cifra {{tile}} del dashboard para las mismas tiendas y fechas.',
    channelTile:
      'Limitado a órdenes {{channel}}, así que solo coincide con {{tile}} del dashboard cuando todas las órdenes del rango lo fueron.',
    collectedStatement: 'Cómo se suma Coleccionado',
    netStatement: 'Cómo se suman las Ventas netas',
    discounts: 'Descuentos',
    tax: 'Impuestos',
    taxDetail: '{{terminal}} en registros de terminal, {{unlogged}} en órdenes sin registro',
    refundsBeforeTax: 'Devoluciones, antes de impuestos',
    refundsDetail: '{{refunds}} devueltos, menos {{tax}} que eran impuestos',
    giftCards: 'Tarjetas de regalo',
    giftCardsSold: 'Tarjetas de regalo vendidas',
    giftCardsSoldDetail: 'Ya incluidas en Coleccionado: venderla entra dinero.',
    giftCardRedemptionsDetail:
      'No incluidos en Coleccionado: pagar con ella no entra dinero.',
    payments: 'Pagos',
    paymentsHelp:
      'Coleccionado por forma de pago, agrupado como lo agrupa Participación de pagos en el dashboard.',
    paymentsTotal: 'Las filas suman Coleccionado: {{collected}} en {{orders}} órdenes.',
    ordersColumn: 'Órdenes',
    refundsColumn: 'Devoluciones',
    afterRefundsColumn: 'Después de devoluciones',
    noOrders: 'No hay órdenes en este rango.',
    taxes: 'Impuestos',
    taxesHelp:
      'Todos los impuestos dentro de Coleccionado. Los registros de terminal los separan por tasa; una orden cobrada sin la terminal solo trae su total.',
    taxColumn: 'Impuesto',
    applicableColumn: 'Ventas aplicables',
    taxCollectedColumn: 'Cobrado',
    taxRefundedColumn: 'Devuelto',
    taxNetColumn: 'Neto',
    unloggedRow: 'Sin registro de terminal',
    unloggedRowDetail: 'Ventas en efectivo o ATH cobradas sin la terminal',
    total: 'Total',
    drops: 'Depósitos de efectivo',
    dropsHelp:
      'Efectivo sacado de una gaveta a la caja fuerte: los mismos depósitos que cuenta el reporte de gaveta.',
    dropsSummary: '{{drops}} depósitos · {{amount}}',
    time: 'Hora',
    lane: 'Caja',
    employee: 'Empleado',
    amount: 'Monto',
    noDrops: 'No hubo depósitos de efectivo en este rango.',
    failed: 'No se pudo cargar el resumen.',
  },
  salesReport: {
    title: 'Ventas',
    subtitle:
      'Las medidas de ingresos del dashboard, separadas por día, caja, empleado, tienda o forma de pago. Elige una fila para ver sus órdenes.',
    store: 'Tienda',
    allStores: 'Todas las tiendas',
    groupBy: 'Agrupar por',
    groupBy_day: 'Día',
    groupBy_lane: 'Caja',
    groupBy_employee: 'Empleado',
    groupBy_store: 'Tienda',
    groupBy_tenderType: 'Forma de pago',
    groupBy_cardType: 'Tipo de tarjeta',
    from: 'Desde',
    to: 'Hasta',
    discounts: 'Descuentos',
    tax: 'Impuestos',
    refunds: 'Devoluciones',
    refundsHint:
      'Devoluciones a crédito en estas órdenes, con impuestos. Las Ventas netas las restan sin sus impuestos.',
    noLane: 'Sin caja',
    noEmployee: 'Sin empleado',
    unnamed: 'Sin nombre',
    rowsTotal:
      'Las filas suman lo de los recuadros: {{collected}} coleccionado en {{orders}} órdenes.',
    noRows: 'No hay órdenes en este rango.',
    failed: 'No se pudieron cargar las ventas.',
    export: 'Exportar',
    exported: 'Se exportaron {{rows}} filas.',
    nothingToExport: 'No hay nada que exportar.',
    ordersSubtitle: '{{orders}} órdenes · {{collected}} coleccionado',
    time: 'Hora',
    order: 'Orden',
    lane: 'Caja',
    employee: 'Empleado',
    tender: 'Forma de pago',
    noOrders: 'No hay órdenes en este grupo.',
    ordersFailed: 'No se pudieron cargar las órdenes.',
    refundedAmount: '{{amount}} devuelto',
    giftCardAmount: '{{amount}} con tarjeta de regalo',
  },
  posDiscounts: {
    title: 'Descuentos POS',
    subtitle: 'Lo que un cajero puede aplicar en la caja.',
    usageTitle: 'Reporte de descuentos POS',
    usageSubtitle:
      'A dónde fueron los descuentos de caja y cuánto costaron. Solo lectura: los descuentos se administran en Marketing.',

    searchPlaceholder: 'Buscar por nombre...',
    loadFailed: 'No se pudieron cargar los descuentos POS.',
    none: 'Todavía no hay descuentos POS.',
    noneMatch: 'Ningún descuento coincide con esta búsqueda.',

    name: 'Nombre',
    nameEn: 'Nombre (inglés)',
    nameEs: 'Nombre (español)',
    discountType: 'Tipo',
    discountTypeHint: 'Los últimos tres exoneran impuestos en vez de rebajar dinero.',
    type0: 'Monto fijo',
    type1: 'Porcentaje',
    type2: 'Sin impuesto estatal',
    type3: 'Sin impuesto municipal',
    type4: 'Sin impuestos',
    value: 'Valor',
    amount: 'Monto de descuento',
    percentage: 'Por ciento de descuento',
    percentageHint: 'Se escribe y se muestra como porcentaje: 10 significa 10% de descuento.',
    scope: 'Aplica en',
    allStores: 'Todas las tiendas',
    oneStore: 'Una tienda',
    exclusions: 'Exclusiones',
    exclusionCount_one: '{{count}} producto excluido',
    exclusionCount_other: '{{count}} productos excluidos',
    status: 'Estado',
    active: 'Activo',
    inactive: 'Inactivo',
    edit: 'Editar',

    details: 'Detalles',
    isActive: 'Activo',
    isActiveHint: 'Apagado conserva el descuento pero lo oculta en todas las cajas.',
    multiple: 'Se puede aplicar más de una vez',
    multipleHint: 'Permite al cajero acumular este descuento en la misma venta.',
    multipleShort: 'Acumulable',

    newDiscount: 'Nuevo descuento',
    editDiscount: 'Editar descuento',
    create: 'Crear',
    created: 'Descuento creado.',
    updated: 'Descuento guardado.',
    saveFailed: 'No se pudo guardar el descuento.',
    delete: 'Eliminar',
    deleteConfirm:
      '"{{name}}" se elimina y deja de ofrecerse en la caja. Los descuentos ya aplicados a órdenes pasadas se conservan.',
    deleted: 'Descuento eliminado.',

    excludedItems: 'Productos excluidos',
    excludedHint:
      'Este descuento aplica a todo excepto a los productos listados aquí.',
    excludedAfterSave: 'Guarda el descuento primero y luego elige qué excluir.',
    searchItems: 'Buscar en el catálogo...',
    noItemsFound: 'Ningún producto coincidió.',
    noExclusions: 'No hay nada excluido: este descuento aplica a todo.',
    exclude: 'Excluir',
    excluded: 'Excluido',
    removeExclusion: 'Dejar de excluir',

    discounts: 'Descuentos',
    allDiscounts: 'Todos los descuentos',
    oneDiscount: 'Un descuento',
    nDiscounts: '{{count}} descuentos',
    store: 'Tienda',
    period: 'Periodo',
    dateRange: 'Rango de fechas',
    lifetime: 'Todo el tiempo',
    from: 'Desde',
    to: 'Hasta',

    totalGiven: 'Total descontado',
    ordersWithDiscount: 'Órdenes',
    customers: 'Clientes',
    orders: 'Órdenes',
    given: 'Descontado',
    orderTotal: 'Total de la orden',
    noUsage: 'No se aplicaron descuentos en este periodo.',
    noOrders: 'No hay órdenes registradas para este descuento.',
    reportFailed: 'No se pudo cargar el reporte de uso.',
    reportNote: 'Cubre {{scope}}. Los montos son lo que se descontó, no lo que se vendió.',
  },

  loyalty: {
    title: 'Lealtad',
    subtitle: 'En qué pueden gastar sus puntos los clientes, y cuánto vale cada premio.',
    searchPlaceholder: 'Buscar por nombre...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar los premios de lealtad.',
    none: 'Todavía no hay premios de lealtad.',
    noneMatch: 'Ningún premio coincide con estos filtros.',

    status: 'Estado',
    statusAny: 'Cualquier estado',
    statusLive: 'Activo',
    statusScheduled: 'Programado',
    statusEnded: 'Terminado',
    statusInactive: 'Inactivo',

    reward: 'Premio',
    points: 'Puntos',
    pointsHint: 'Lo que gasta un cliente para redimirlo una vez.',
    pointsFor: '{{points}} puntos por {{value}}',
    worth: 'Valor',
    window: 'Periodo',
    edit: 'Editar',
    noProduct: 'Sin producto asignado',

    newReward: 'Nuevo premio',
    editReward: 'Editar premio',
    create: 'Crear',
    created: 'Premio creado.',
    updated: 'Premio guardado.',
    saveFailed: 'No se pudo guardar el premio.',
    remove: 'Quitar',
    removeConfirm:
      '"{{name}}" deja de ofrecerse. Los puntos ya gastados en él no se devuelven.',
    removed: 'Premio quitado.',

    details: 'Detalles',
    internalName: 'Nombre interno',
    internalNameHint: 'Así reconocerás este premio en la lista.',
    name: 'Nombre para el cliente',
    nameHint: 'Lo que ve el cliente al gastar sus puntos.',
    ordering: 'Orden',
    orderingHint: 'Los números más bajos van primero.',
    isActive: 'Activo',
    isActiveHint: 'Apagado oculta el premio a los clientes sin importar sus fechas.',

    whatItGives: 'Qué otorga',
    type: 'Tipo',
    typeHint: 'Los dos premios de producto necesitan un producto asignado abajo.',
    type0: 'Monto de descuento en la orden',
    type1: 'Por ciento de descuento en la orden',
    type2: 'Monto de descuento en un producto',
    type3: 'Por ciento de descuento en un producto',
    amountOff: 'Monto de descuento',
    percentOff: 'Por ciento de descuento',
    percentHint: 'Se escribe y se muestra como porcentaje: 10 significa 10% de descuento.',
    maxValue: 'Máximo que puede dar',
    maxValueHint:
      'Un tope fijo en dinero. Aplica a todos los tipos, así que debe ser al menos lo que vale el premio.',
    maxValueZero:
      'Un tope de cero significa que este premio no descuenta nada, sin importar su valor.',
    givesNothing: 'No da nada',
    givesNothingHint:
      'El tope es cero y toda redención se limita a él, así que este premio cobra puntos y descuenta $0. Ponle un tope para arreglarlo.',
    minOrder: 'Orden mínima',
    minOrderHint: 'Déjalo en 0 para no exigir mínimo.',
    product: 'Producto',
    productHint: 'El premio aplica solo a este producto.',
    changeProduct: 'Elegir otro producto',
    searchItems: 'Buscar en el catálogo...',
    noItemsFound: 'Ningún producto coincidió.',

    schedule: 'Programación',
    starts: 'Comienza',
    ends: 'Termina',
    endBeforeStart: 'El final tiene que ser después del comienzo.',
  },

  configuration: {
    title: 'Configuración',
    subtitle:
      'Los archivos JSON de configuración en blob storage. Al guardar se sobrescribe el archivo completo.',
    file: 'Archivo',
    file_server: 'Servidor',
    file_app: 'App',
    file_manager: 'Manager',
    missingShort: 'no encontrado',
    missing: 'Todavía no existe {{file}} en blob storage. Agrega un ajuste y guarda para crearlo.',
    search: 'Buscar',
    searchPlaceholder: 'Filtrar por clave o valor...',
    loadFailed: 'No se pudo cargar este archivo de configuración.',
    saved: 'Configuración guardada.',
    saveFailed: 'No se pudo guardar la configuración.',

    settingCount: 'Mostrando {{shown}} de {{total}} ajustes',
    empty: 'Este archivo no tiene ajustes.',
    addSetting: 'Agregar ajuste',
    addBody: 'Se agrega localmente. No se escribe nada hasta que guardes el archivo.',
    key: 'Clave',
    value: 'Valor',
    keyExists: 'Ya existe un ajuste con esa clave.',
    willBeStoredAs: 'Se guardará como: {{kind}}.',
    removeSetting: 'Quitar ajuste',
    removeAll: 'Quitar todos',
    removeAllConfirm_one:
      'Se borra {{count}} ajuste localmente. El archivo solo se vacía cuando guardes.',
    removeAllConfirm_other:
      'Se borran {{count}} ajustes localmente. El archivo solo se vacía cuando guardes.',
    download: 'Descargar',
    unsaved: 'Tienes cambios sin guardar. No se escribe nada en el archivo hasta que guardes.',
    invalid_one: '{{count}} ajuste tiene un valor que no corresponde a su tipo. Corrígelo antes de guardar.',
    invalid_other: '{{count}} ajustes tienen valores que no corresponden a su tipo. Corrígelos antes de guardar.',

    kind_string: 'Texto',
    kind_number: 'Número',
    kind_boolean: 'Verdadero/falso',
    kind_json: 'JSON',
    invalid_number: 'Esto tiene que ser un número.',
    invalid_boolean: 'Esto tiene que ser true o false.',
    invalid_json: 'Esto no es JSON válido.',
    invalid_string: '',
  },

  adminPos: {
    title: 'Cajas POS',
    subtitle: 'Todas las cajas de la plataforma y qué versión corre cada una.',
    searchPlaceholder: 'Buscar por caja, tienda, equipo o terminal...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar las cajas POS.',
    none: 'No hay cajas POS registradas.',
    noneMatch: 'Ninguna caja coincide con estos filtros.',

    latestBuild: 'Última versión',
    latestBuildSub: 'La más nueva que ha reportado alguna caja.',
    behindSub: 'de {{total}} cajas',
    neverReportedSub_one: '{{count}} nunca ha reportado',
    neverReportedSub_other: '{{count}} nunca han reportado',
    totalLanes: 'Cajas',
    build: 'Versión',
    anyBuild: 'Cualquier versión',
    current: 'Al día',
    behind: 'Atrasadas',
    unknown: 'Nunca reportó',
    neverReported: 'Nunca reportó',

    store: 'Tienda',
    allStores: 'Todas las tiendas',
    lane: 'Caja',
    version: 'Versión',
    device: 'Equipo',
    lastTransaction: 'Última venta',
    access: 'Acceso',
    anyAccess: 'Cualquier acceso',
    allowed: 'Permitida',
    blocked: 'Bloqueada',
  },

  table: {
    noData: 'No hay nada que mostrar.',
    sortBy: 'Ordenar por',
    sortAsc: 'Orden ascendente',
    sortDesc: 'Orden descendente',
  },

  businessAccounts: {
    title: 'Cuentas de negocio',
    subtitle:
      'La cuenta de pago de cada tienda: a dónde va su dinero y cómo se reparte.',
    searchPlaceholder: 'Buscar por tienda, titular o correo...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudieron cargar las cuentas de negocio.',
    none: 'Todavía no hay cuentas de negocio.',
    noneMatch: 'Ninguna cuenta coincide con estos filtros.',
    edit: 'Editar',

    status: 'Estado',
    anyStatus: 'Cualquier estado',
    active: 'Activa',
    inactive: 'Inactiva',
    isActive: 'Activa',
    isActiveHint: 'Apagada suspende la cuenta sin eliminarla.',
    payouts: 'Pagos',
    anyPayouts: 'Cualquier ajuste de pago',
    payoutsOn: 'Pagos activos',
    payoutsOff: 'Pagos detenidos',
    allowPayouts: 'Permitir pagos',
    allowPayoutsHint: 'Apagado retiene el dinero en vez de enviarlo.',

    store: 'Tienda',
    chooseStore: 'Elige una tienda...',
    accountHolder: 'Titular',
    bankAccount: 'Cuenta bancaria',

    newAccount: 'Nueva cuenta',
    editAccount: 'Editar cuenta de negocio',
    create: 'Crear',
    created: 'Cuenta de negocio creada.',
    updated: 'Cuenta de negocio guardada.',
    saveFailed: 'No se pudo guardar la cuenta de negocio.',
    delete: 'Eliminar',
    deleteConfirm:
      'Se elimina la cuenta de pago de "{{name}}" y esa tienda deja de recibir pagos. Los pagos anteriores se conservan.',
    deleted: 'Cuenta de negocio eliminada.',

    bank: 'Datos bancarios',
    accountHolderName: 'Nombre del titular',
    accountNumber: 'Número de cuenta',
    routingNumber: 'Número de ruta',
    reveal: 'Mostrar',
    hide: 'Ocultar',
    hidden: 'oculto, se ven los últimos cuatro dígitos',
    minimumPayout: 'Pago mínimo',
    minimumPayoutHint: 'No se envía nada hasta que el balance llegue a esto.',
    processorAccountId: 'ID de cuenta del procesador',
    processorAccountIdHint: 'La cuenta a la que corresponde esta tienda en el procesador de pagos.',
    payPalClientId: 'Client ID de PayPal',

    entity: 'A quién se le paga',
    holderType: 'Tipo de titular',
    company: 'Empresa',
    individual: 'Persona',
    businessType: 'Categoría de comercio',
    legalName: 'Nombre legal del negocio',
    ein: 'EIN',
    firstName: 'Nombre',
    lastName: 'Apellido',
    birthDate: 'Fecha de nacimiento',
    last4Ssn: 'SSN (últimos 4)',
    last4SsnHint: 'Solo se guardan los últimos cuatro dígitos.',
    accountName: 'Nombre en el estado de cuenta',
    accountNameHint: 'Lo que aparece en el estado de cuenta del cliente.',
    contactEmail: 'Correo de contacto',
    contactPhone: 'Teléfono de contacto',

    address: 'Dirección',
    address1: 'Dirección línea 1',
    address2: 'Dirección línea 2',
    city: 'Ciudad',
    state: 'Estado',
    postalCode: 'Código postal',
    country: 'País',

    split: 'Reparto de cargos',
    splitHint:
      'La parte de cada orden que se retiene antes del pago. Se guarda tal cual: verifica contra el acuerdo con el procesador antes de cambiarlos.',
    subtotalPct: 'Subtotal %',
    taxesPct: 'Impuestos %',
    serviceFeePct: 'Cargo por servicio %',
    processorPct: 'Procesador %',
  },

  newBusiness: {
    title: 'Nueva tienda',
    subtitle:
      'Crea la tienda y se la asigna a un dueño. Su catálogo, personal y horarios se configuran después.',
    theStore: 'La tienda',
    ownerEmail: 'Correo del dueño',
    ownerEmailHint: 'La cuenta que será dueña de esta tienda. Tiene que existir ya.',
    emailInvalid: 'Eso no parece una dirección de correo.',
    name: 'Nombre de la tienda',
    description: 'Descripción',
    isBeta: 'Beta',
    isBetaHint:
      'Mantiene la tienda fuera de la app pública mientras se configura. Solo la ven las cuentas con acceso beta.',

    where: 'Dónde está',
    address1: 'Dirección línea 1',
    address2: 'Dirección línea 2',
    city: 'Ciudad',
    lat: 'Latitud',
    lon: 'Longitud',
    coordinatesHint:
      'Se usan para calcular la distancia de entrega. Dejar ambas en cero coloca la tienda frente a la costa de África.',

    fulfilment: 'Cómo vende',
    allowPickup: 'Permitir recogido',
    minimumPickup: 'Orden mínima de recogido',
    allowDelivery: 'Permitir entrega',
    minimumDelivery: 'Orden mínima de entrega',
    hasDeli: 'Tiene deli',
    acceptsEbt: 'Acepta EBT',
    pricesIncludeTax: 'Los precios incluyen impuesto',
    pricesIncludeTaxHint: 'Los precios de góndola ya traen el impuesto, así que no se añade al pagar.',

    create: 'Crear tienda',
    created: '"{{name}}" creada.',
    failed: 'No se pudo crear la tienda.',
  },

  impersonation: {
    title: 'Suplantar un usuario',
    subtitle: 'Inicia sesión como un cliente para ver exactamente lo que él ve.',
    warningTitle: 'Esto reemplaza tu sesión.',
    warningBody:
      'Todas las peticiones a partir de ese momento se hacen como ese cliente, incluido cualquier cambio. Tu sesión se conserva y la barra en la parte superior la devuelve.',

    searchPlaceholder: 'Buscar por nombre, correo o teléfono...',
    startBody: 'Busca al cliente como quien necesitas entrar. No se muestra nada hasta que busques.',
    noResults: 'Nada coincidió con "{{term}}".',
    deleted: 'Eliminado',
    signInAs: 'Entrar como',

    confirmTitle: '¿Entrar como este cliente?',
    confirmBody:
      'Quedarás dentro como {{name}} hasta que lo detengas. Todo lo que hagas quedará registrado como esa persona.',
    started: 'Entraste como {{name}}.',
    failed: 'No se pudo entrar como ese cliente.',

    bannerViewingAs: 'Viendo como {{name}}.',
    bannerSignedInAs: 'Tu sesión como {{name}} está esperando.',
    stop: 'Detener',
  },

  inventory: {
    title: 'Inventario',
    subtitle: 'Existencias, producto por producto.',
    subtitleFor: 'Existencias en {{store}}, producto por producto.',
    chooseStore: 'El inventario pertenece a una tienda. Elige una arriba para verlo.',
    searchPlaceholder: 'Buscar por nombre, marca o UPC...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudo cargar el inventario.',
    noResults: 'Ningún producto coincide con esta búsqueda.',

    division: 'División',
    anyDivision: 'Cualquier división',
    department: 'Departamento',
    anyDepartment: 'Cualquier departamento',
    category: 'Categoría',
    anyCategory: 'Cualquier categoría',
    sortBy: 'Ordenar por',
    sortDefault: 'Nombre',
    sortLowest: 'Menos existencias primero',
    sortHighest: 'Más existencias primero',

    product: 'Producto',
    where: 'Dónde',
    onHand: 'disponibles',
    cost: 'Costo',
    flags: 'Marcas',
    manage: 'Gestionar',
    item: 'Producto',
    itemFailed: 'No se pudo cargar este producto.',
    hidden: 'Oculto',
    unavailable: 'No disponible',
    untracked: 'Sin seguimiento',

    tabMove: 'Mover existencias',
    tabSettings: 'Ajustes',
    tabHistory: 'Historial',

    movementType: 'Qué pasó',
    type0: 'Venta',
    type1: 'Ajuste hacia arriba',
    type2: 'Ajuste hacia abajo',
    type3: 'Recibo de mercancía',
    amount: 'Cantidad',
    amountAddHint: 'Cuántas unidades agregar.',
    amountRemoveHint: 'Cuántas unidades quitar. Escríbelo como número positivo.',
    reason: 'Razón',
    reasonHint: 'Se guarda con el movimiento y es como agrupan los reportes de pérdida.',
    reason0: 'Sin especificar',
    reason1: 'Daño',
    reason2: 'Perdido',
    reason3: 'Encontrado',
    reason4: 'Reabastecimiento',
    reason5: 'Muestra',
    reason6: 'Regalo',
    reason7: 'Dañado',
    reason8: 'Traslado a cocina',
    reason9: 'Traslado a otra tienda',
    reason10: 'Traslado al deli',
    reason11: 'Layaway',
    changedAt: 'Cuándo pasó',
    changedAtHint: 'Déjalo vacío para registrarlo como ahora.',
    comments: 'Nota',
    echo: 'Lleva este producto de {{from}} a {{to}}.',
    record: 'Registrar movimiento',
    movementRecorded: 'Movimiento registrado.',
    actionFailed: 'No se pudo completar esa acción.',

    affectsInventory: 'Llevar inventario',
    affectsInventoryHint: 'Apagado, las ventas nunca cambian la cuenta y el número de abajo deja de significar algo.',
    isVisible: 'Visible',
    isVisibleHint: 'Apagado oculta el producto por completo a los clientes.',
    isAvailable: 'Disponible para comprar',
    isAvailableHint: 'Apagado lo deja visible pero no se puede ordenar. Revertir un movimiento vuelve a fijar esto según la nueva cuenta.',
    costs: 'Costos',
    costOfBox: 'Costo de la caja',
    pack: 'Unidades por caja',
    packHint: 'Se usa para calcular el costo unitario a partir del costo de la caja.',
    saveCosts: 'Guardar costos',
    costsSaved: 'Costos guardados.',

    noHistory: 'Todavía no se ha movido nada en este producto.',
    revert: 'Revertir',
    revertConfirm:
      'Quita {{amount}} de la cuenta y elimina este movimiento del historial, así que no queda registro de que ocurrió. También vuelve a fijar si el producto se puede comprar.',
    reverted: 'Movimiento revertido.',

    packsAndUnits: '{{packs}} cajas y {{units}} sueltas',
    recalculate: 'Recalcular',
    recalculated: 'Cuenta reconstruida a partir del historial de movimientos.',
    recalculateUntracked: 'Este producto no lleva inventario, así que no hay nada que reconstruir.',

    helpers: 'Calcular la cantidad',
    boxes: 'Cajas',
    unitsPerBox: 'Unidades c/u',
    useIt: 'Usar',
    countedTo: 'Contado',
    countedToHint: 'Escribe lo que contaste y esto fija la dirección y la diferencia.',

    breakdown: 'Desglosar',
    breakdownBody:
      'Abrir una caja suma una a esa fila y descuenta sus unidades de {{name}}. Encajar hace lo contrario.',
    noRelated: 'Este producto no tiene nada en qué desglosarse.',
    worthUnits_one: 'vale {{count}} unidad',
    worthUnits_other: 'vale {{count}} unidades',
    hasNow: 'tiene {{quantity}}',
    open: 'Abrir',
    boxUp: 'Encajar',
    breakdownEcho: 'Lleva {{name}} de {{from}} a {{to}}.',
    breakdownNegative:
      'Esto llevaría {{name}} a {{to}}, por debajo de cero. Reduce las cajas que estás abriendo.',
    applyBreakdown: 'Aplicar',
    breakdownDone: 'Desglose aplicado.',
    export: 'Exportar',
    exported: 'Catálogo exportado.',
    exportedIgnoringSearch:
      'Catálogo exportado. Se aplicaron los filtros, pero no la búsqueda: el endpoint de exportación no acepta término de búsqueda.',
    exportFailed: 'No se pudo exportar el catálogo.',
    sharedStores: 'Tiendas compartidas',
    sharedStoresBody: 'Tiendas cuyo inventario se mantiene en sincronía con {{store}}.',
    linkStore: 'Enlazar una tienda',
    chooseStoreToLink: 'Elige una tienda...',
    link: 'Enlazar',
    unlink: 'Desenlazar',
    noSharedStores: 'Esta tienda no comparte su inventario con nadie.',
    done: 'Listo',
  },

  inventoryReport: {
    title: 'Reporte de inventario',
    subtitle:
      'Qué se movió en un periodo, cuánto valía y qué queda en la góndola.',
    searchPlaceholder: 'Buscar por producto o GTIN...',
    showingOf: 'Mostrando {{shown}} de {{total}}',
    loadFailed: 'No se pudo cargar el reporte de inventario.',
    noResults: 'No se movió nada en este periodo con estos filtros.',

    store: 'Tienda',
    allStores: 'Todas las tiendas',
    movementType: 'Movimiento',
    anyType: 'Cualquier movimiento',
    reasons: 'Razones',
    anyReason: 'Cualquier razón',
    nReasons: '{{count}} razones',
    division: 'División',
    anyDivision: 'Cualquier división',
    from: 'Desde',
    to: 'Hasta',

    sortBy: 'Ordenar por',
    sortDefault: 'Producto',
    sortMovedDesc: 'Más movido',
    sortMovedAsc: 'Menos movido',
    sortValueDesc: 'Mayor valor movido',
    sortValueAsc: 'Menor valor movido',
    sortQohDesc: 'Más existencias',
    sortQohAsc: 'Menos existencias',

    product: 'Producto',
    gtin: 'GTIN',
    moved: 'Movido',
    movedValue: 'Valor movido',
    onHand: 'Disponible',
    onHandValue: 'Valor disponible',
    unitsMoved: '{{amount}} unidades',
    onHandRow: '{{quantity}} disponibles, valen {{value}}',
    onHandNow:
      'En todo lo que coincide con estos filtros: {{quantity}} unidades disponibles, por {{value}}.',

    export: 'Exportar',
    exported_one: '{{count}} fila exportada.',
    exported_other: '{{count}} filas exportadas.',
    nothingToExport: 'No hay nada que exportar con estos filtros.',
    exportFailed: 'No se pudo exportar el reporte.',
  },

  filters: {
    title: 'Filtros',
    apply: 'Aplicar',
    reset: 'Restablecer',
  },

  nav: {
    sections: {
      platform: 'Plataforma',
      booking: 'Reservas',
      finance: 'Finanzas',
      catalog: 'Catálogo',
      marketing: 'Mercadeo',
      store: 'Tienda',
      support: 'Soporte',
      general: 'General',
    },

    home: 'Inicio',
    globalDashboard: 'Tablero global',
    storeDashboard: 'Tablero de tienda',
    globalPayouts: 'Pagos globales',
    reports: 'Reportes',
    logsGlobal: 'Registros globales',
    documents: 'Documentos',
    downloads: 'Descargas',
    checkHealth: 'Estado del sistema',

    adminServices: 'Servicios de administración',
    businessAccounts: 'Cuentas de negocio',
    newBusiness: 'Nuevo negocio',
    documentAcceptance: 'Aceptación de documentos',
    impersonation: 'Suplantar usuario',
    coupons: 'Cupones',
    giftCards: 'Tarjetas de regalo',
    cardRestrictions: 'Restricciones de tarjeta',
    deliveryProviders: 'Proveedores de entrega',
    adminPosLanes: 'Cajas POS',
    imagesService: 'Servicio de imágenes',
    changeApiUrl: 'Cambiar URL del API',
    configuration: 'Configuración',
    configServer: 'Servidor',
    configApp: 'App',
    configManager: 'Manager',

    customerService: 'Servicio al cliente',
    ordersGlobal: 'Órdenes',
    users: 'Usuarios',
    accountRequests: 'Solicitudes de cuenta',
    referralEmail: 'Correo de referidos',
    externalContacts: 'Contactos externos',
    layawaysGlobal: 'Todos los apartados',
    paypalInvoicesGlobal: 'Facturas PayPal',

    analytics: 'Analítica',
    usageAnalytics: 'Uso de aplicaciones',

    booking: {
      maintenance: 'Mantenimiento',
      management: 'Gestión',
      reports: 'Reportes',

      membershipPlans: 'Planes de membresía',
      events: 'Eventos',
      membershipGifts: 'Regalos de membresía',
      giftVouchers: 'Certificados de regalo',
      giftVouchersManage: 'Certificados de regalo',
      giftVouchersReport: 'Certificados de regalo',

      manageBookings: 'Reservas',
      manageSubscriptions: 'Suscripciones',
      paymentReview: 'Revisión de pagos',

      businessOverview: 'Resumen del negocio',
      reportsBookings: 'Reservas',
      reportsSubscriptions: 'Suscripciones',
      sales: 'Ventas',
      paymentHistory: 'Historial de pagos',
      timeslotHosts: 'Anfitriones por horario',
      heatmaps: 'Mapas de calor',
      heatmapSales: 'Ventas',
      heatmapBookings: 'Reservas',

      hosts: 'Anfitriones',
      locations: 'Localidades',
      serviceTypes: 'Tipos de servicio',
    },

    finance: {
      sales: 'Ventas',
      salesReport: 'Reporte de ventas',
      general: 'General',
      inStore: 'En tienda',
      inStoreEmployees: 'Empleados en tienda',
      settlements: 'Liquidaciones',
      byStation: 'Por estación',
      summary: 'Resumen',

      movement: 'Movimiento',
      division: 'División',
      group: 'Grupo',
      department: 'Departamento',
      category: 'Categoría',

      loss: 'Mermas',
      targetVsActual: 'Meta vs real',
      participation: 'Participación',
      monthly: 'Mensual',

      posDiscountReport: 'Reporte de descuentos POS',
      inventoryReport: 'Reporte de inventario',
      invoices: 'Facturas',
      topProducts: 'Productos destacados',
    },

    catalog: {
      title: 'Catálogo',
      itemMaster: 'Maestro de artículos',
      itemRegistration: 'Registro de artículos',
      departments: 'Departamentos',
      categories: 'Categorías',
      searchOptimization: 'Optimización de búsqueda',
      consumerSearch: 'Búsqueda del consumidor',
      itemPrices: 'Precios de artículos',
      requirements: 'Requisitos',
      sides: 'Acompañantes',
      addons: 'Complementos',
    },

    marketing: {
      title: 'Mercadeo',
      promotions: 'Promociones',
      promoCodes: 'Códigos promocionales',
      bannerAds: 'Banners',
      promoEmails: 'Correos promocionales',
      pushNotifications: 'Notificaciones push',
      productCampaign: 'Campañas de producto',
      infoCards: 'Tarjetas informativas',
      sponsorships: 'Patrocinios',
      posDiscounts: 'Descuentos POS',
      loyalty: 'Lealtad',
    },

    store: {
      title: 'Tienda',
      orders: 'Órdenes de tienda',
      customers: 'Clientes',
      storage: 'Almacenamiento',
      onBehalf: 'En nombre de',
      products: 'Productos',
      storeItemPrices: 'Precios de la tienda',
      shopper: 'Comprador',
      storeCoupons: 'Cupones de tienda',
      selfCheckout: 'Autopago',
      inventory: 'Inventario',
      performance: 'Rendimiento',
      sorting: 'Ordenamiento',
      chats: 'Chats',
      logs: 'Registros de tienda',
      paypalInvoices: 'Facturas PayPal',

      staff: 'Personal',
      staffList: 'Equipo',
      timesheets: 'Hojas de tiempo',
      timesheetTypes: 'Tipos de hoja de tiempo',

      pos: 'POS',
      posLanes: 'Cajas',
      cashDrawerReport: 'Reporte de caja',

      layaway: 'Apartados',
      bins: 'Casilleros',
      layaways: 'Apartados',

      deliveries: 'Entregas',

      payments: 'Pagos',
      payouts: 'Desembolsos',
      paymentOptions: 'Opciones',

      settings: 'Ajustes',
      general: 'General',
      options: 'Opciones',
      hours: 'Horarios',
      delivery: 'Entrega',
      customDepartments: 'Departamentos personalizados',
      storeDocuments: 'Documentos de tienda',
    },
  },

  pages: {
    home: {
      welcome: 'Bienvenido de vuelta, {{name}}',
      scopedTo: 'Viendo {{store}}.',
      global: 'Viendo todas las tiendas.',
      shortcuts: 'Ir a',
      frequent: 'Tus más visitadas',
      frequentHelp: 'A donde más has ido en los últimos 30 días.',
      more: 'Más',
      goodToKnow: 'Bueno saber',
      tipStoreTitle: 'Cambiar de tienda',
      tipStoreBodyGlobal:
        'Elige una tienda en el cambiador de la barra lateral para filtrar las páginas por ella. Las páginas exclusivas de tienda aparecen al seleccionar una.',
      tipStoreBodyScoped:
        'Las páginas están filtradas por {{store}}. Vuelve a Global desde la barra lateral para ver todas las tiendas a la vez.',
      tipSearchTitle: 'Encuentra cualquier página',
      tipSearchBody: 'Presiona ⌘K (Ctrl+K en Windows) para buscar páginas y saltar directo a ellas.',
      tipPrefsTitle: 'Idioma y tema',
      tipPrefsBody: 'Ambos están en el menú de tu avatar, arriba a la derecha.',
    },
    placeholder: {
      description: 'Esta página aún no se ha construido en el rediseño.',
      title: 'Aún no implementada',
      body: 'La entrada de navegación ya está conectada — la página está por venir.',
      fallbackName: 'Página',
    },
    notFound: {
      title: 'Página no encontrada',
      body: 'Esa URL no corresponde a nada en el portal.',
    },
    unauthorized: {
      title: 'No tienes acceso',
      body: 'A tu cuenta le falta el rol requerido para esta página. Consulta a un administrador si crees que es un error.',
    },
  },
};
